/**
 * seoLandingPagesTierF.js — Iter 192 Tier-F JA-primary expansion (10 net-new).
 *
 * Net-new topics from the priority-20 list (duplicates with existing
 * Tier-C / Tier-D / Tier-E / Phase-A guides excluded):
 *
 *   1. etc-meisai-pdf-download-howto             #1  PDFダウンロード手順
 *   2. etc-meisai-csv-export-howto               #2  CSV出力手順
 *   3. etc-meisai-missing-trips-reasons          #5  表示されない走行がある理由
 *   4. etc-kyujitsu-warikibi-exclusion-days      #8  休日割引が適用されない日
 *   5. etc-senyou-mis-entry-recovery             #11 ETC専用入口での誤進入時対応
 *   6. etc-card-vs-obu-number-guide              #13 ETCカード番号と車載器番号の違い
 *   7. etc-meisai-unknown-entry-ic               #14 入口ICが不明と表示される理由
 *   8. etc-meisai-update-timing                  #15 明細が反映されるまでの時間
 *   9. etc-meisai-vs-jtr-comparison              #18 ETC利用照会サービスとJTRの違い
 *  10. etc-meisai-daily-email-howto              #20 毎日メールで受け取る方法
 *
 * Editorial guardrails (never bend):
 *  - JTRはETC利用照会サービス・NEXCO・首都高・MEISAIの公式ではない
 *  - 税務・経理・インボイス・監査判断は税理士または所轄税務署で確認
 *  - 「PDF + CSV」を統一表現とし「Excel」「.xlsx」は使わない
 *  - 料金・距離・割引率・適用条件は「公式情報を要確認」「車種・区間で変動」と添える
 *  - 公式サービスを尊重、競合の悪口を書かない
 *  - JTR is positioned as「確認を支援」「整理に役立つ」「候補を確認」「利用傾向を確認」
 */

const REF_ETC_MEISAI = { label: 'ETC利用照会サービス（公式）', url: 'https://www.etc-meisai.jp/', note: '通行明細・利用証明書PDF・利用明細CSVの公式出力先' };
const REF_ETC_MEISAI_TEBIKI = { label: 'ETC利用照会サービス 操作手引き', url: 'https://www.etc-meisai.jp/tebiki/tebiki_03.html', note: '利用明細・利用証明書の発行手順' };
const REF_GOETC = { label: 'go-etc.jp — ETCカード総合情報', url: 'https://www.go-etc.jp/', note: 'ETCカード・ETC2.0・割引制度の公式案内' };
const REF_GOETC_SENYOU = { label: 'go-etc.jp — ETC専用料金所', url: 'https://www.go-etc.jp/useful/etc-only', note: 'ETC専用料金所の運用ガイド・救済方法' };
const REF_NEXCO_E = { label: 'NEXCO東日本（公式）', url: 'https://www.e-nexco.co.jp/', note: '東日本管内の高速道路情報' };
const REF_NEXCO_C = { label: 'NEXCO中日本（公式）', url: 'https://www.c-nexco.co.jp/', note: '中日本管内の高速道路情報' };
const REF_NEXCO_W = { label: 'NEXCO西日本（公式）', url: 'https://www.w-nexco.co.jp/', note: '西日本管内の高速道路情報' };
const REF_NEXCO_C_FAQ = { label: 'NEXCO中日本 ETCレーンFAQ', url: 'https://highwaypost.c-nexco.co.jp/faq/etc/lane/83.html', note: 'ETCレーン関連の公式FAQ' };
const REF_ETC_MILEAGE = { label: 'ETCマイレージサービス（公式）', url: 'https://www.smile-etc.jp/', note: '休日割引・平日朝夕割引等の登録窓口' };
const REF_SHUTOKO = { label: '首都高速道路（公式）', url: 'https://www.shutoko.jp/', note: '首都高速の通行料金・距離別料金体系' };

// Shared related-feature link block (Japanese first, JTR product education).
const RELATED_FEATURES_CORE = [
  { title: '無料週次レポート', desc: '週1回のメール配信。無期限・無料・カード登録不要。', path: '/free-weekly' },
  { title: 'プレミアム毎日レポート', desc: '前日のETC通行明細をPDF + CSVで毎朝自動配信。', path: '/personal/features' },
  { title: 'PDF + CSV エクスポート', desc: '会計ソフト・スプレッドシートとの互換性。Excel不要。', path: '/guides/etc-pdf-csv-download-guide' },
  { title: '経費精算レポート', desc: '通行ごとの証跡を経費精算向けに整形。', path: '/features/reimbursement-reports' },
];

export const TIER_F_PAGES = [
  // 1) ETC利用明細をPDFでダウンロードする方法
  {
    slug: 'etc-meisai-pdf-download-howto',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: 'ETC PDF ダウンロード',
      en: 'ETC PDF download',
    },
    ja: {
      title: 'ETC利用証明書のPDFダウンロード方法と自動化 | Japan Toll Receipts',
      metaDesc: 'ETC利用照会サービスから利用証明書PDF（適格簡易請求書）をダウンロードする手順を、英語版も含めて解説。取得タイミング（利用金額確定後）、過去15か月（ETCコーポレートカードは62日）の保管期間、ファイル命名規則、長期保存の電帳法対応、独立サービスJTRの日次自動配信を併用するメリットまで紹介します。',
      h1: 'ETC利用証明書のPDFダウンロード方法',
      primaryKeyword: 'ETC PDF ダウンロード',
      answer: 'ETC利用照会サービスにログインし、利用明細を表示後、該当期間または走行の利用証明書または利用明細をPDF形式でダウンロードできます。通常のETCクレジットカードおよびETCパーソナルカードは15か月間、ETCコーポレートカードは62日間の記録が照会可能です。JTRを利用すると、手動ログインの繰り返しを減らし、整理されたPDFとCSV記録をメール受信できます。',
      whyMatters: 'ETCレーンでは通常その場で紙の領収書が発行されないため、後日公式照会サービスから利用証明書を取得する必要があります。PDFは閲覧・添付・印刷・保存が容易で、経費精算の承認者が表計算列を調整せずに確認でき、個人事業主は月別保管が簡単です。インボイス制度対応では確定した利用証明書が推奨されており、タイミングと最終化の確認が重要です。CSV記録と併用することで、書類レビューと構造的な経理処理の両方に対応できます。',
      officialBackground: 'ETC利用照会サービスは、対象カード保有者が利用証明書の発行、利用明細の確認、PDFまたはCSVでのダウンロードを行える公式サービスです。対象はETCクレジットカード、ETCパーソナルカード、ETCコーポレートカードで、通常のETCクレジットカードおよびETCパーソナルカードは15か月間、ETCコーポレートカードは62日間の記録が照会可能です。登録にはカード情報、ETC無線通行実績、車載器管理番号、ナンバープレート末尾4桁、利用年月日などが必要で、初回登録完了後の明細表示には約4時間かかる場合があります。NEXCO東日本の案内では、インボイス制度対応の書類が必要な場合はETC利用照会サービスから確定した利用証明書を取得することが推奨されています。',
      whoFor: [
        '経費精算のためPDF形式の通行記録が必要なドライバー',
        '通行記録を月別保存したい個人事業主',
        '領収書スタイルのETC記録が必要な法人ユーザー',
        '日本語サイトの操作を英語で理解したい外国人居住者',
      ],
      jtrSolution: {
        intro: 'JTRはPDF配信とアーカイブのワークフローを提供します。公式ETC利用照会サービスが利用証明書の正式な発行元であり、JTRは手動作業を減らして整理された記録の受信と保管を支援します。',
        bullets: [
          '毎日または毎月の手動ログインを繰り返さず、整理された通行記録をメール受信',
          '複数カードを保有する事業者や英語対応が必要なドライバーに有用',
          '記録取得の失念を防ぎ、締切前の焦りを軽減',
          'PDFとCSVの両形式で配信し、書類レビューと構造的経理の両方に対応',
          '公式記録の作成ではなく、記録の整理と定期的な配信フローを担当',
        ],
      },
      pdfCsv: 'PDF記録は書類レビューに適し、CSV記録は構造的な経理処理に適しています。PDFのみでは部門別や車両別の配分が困難になり、CSVのみでは承認用の便利な書類形式ファイルが欠けます。JTRは両形式を提供し、異なる用途に対応します。',
      email: 'JTRは、利用者の承認設定に基づいて整理されたETC記録をメールで配信できます。これにより日本語専用ポータルへの繰り返しログインを避け、英語話者や経理チームが反復可能な記録フローを維持できます。',
      steps: [
        {
          t: 'ETC利用照会サービスへの事前登録',
          d: '急な経費精算の締切前に、ETC利用照会サービスのアカウント登録を完了します。登録には車載器管理番号、ナンバープレート下4桁、最近のETC無線通行日などが必要です。',
        },
        {
          t: '利用明細の表示期間を確認',
          d: '通常のETCクレジットカードおよびETCパーソナルカードは過去15か月分、ETC法人カードは過去62日分の明細が照会可能です。対象期間内の明細を表示します。',
        },
        {
          t: '利用証明書または明細をPDF形式でダウンロード',
          d: '必要な通行記録を選択し、利用証明書または利用明細をPDF形式で出力・ダウンロードします。画面レイアウトは変更される場合がありますが、ダウンロード機能は提供されています。',
        },
        {
          t: 'インボイス制度対応が必要な場合は確定状況を確認',
          d: 'NEXCO公式ガイダンスによると、インボイス制度に対応する場合は確定した利用証明書の使用が推奨されます。早期発行や未確定の証明書は要件を満たさない場合があります。',
        },
        {
          t: 'PDFファイルに明確なファイル名を付けて保存',
          d: '年月、カード名、運転者名または車両名を含む分かりやすいファイル名で保存します。経理フォルダへの整理や月次保管を行い、後の検索を容易にします。',
        },
        {
          t: 'JTRで日次PDF配信を自動化',
          d: '手作業での繰り返しログインを減らすため、JTRを利用してETC記録をメール配信します。PDF形式とCSV形式の両方を受け取ることで、確認と会計処理の両方に対応できます。',
        },
      ],
      commonProblems: [
        {
          q: '登録直後に明細が表示されない',
          a: '公式Q&Aによると、本登録完了後に利用明細データが表示されるまで約4時間かかる場合があります。急ぎの場合は早めに登録を済ませておくことが重要です。',
        },
        {
          q: 'PDFだけでは経理処理が難しい',
          a: 'PDFは目視確認に適していますが、部門別配分や車両別集計にはCSV形式が必要です。JTRはPDFとCSVの両方を提供し、異なる用途に対応します。',
        },
        {
          q: '現金レシートとETC利用証明書の違いが分からない',
          a: '現金レシートは支払時に発行されますが、ETC利用証明書は通行後に照会サービスから取得する記録です。形式と発行タイミングが異なります。',
        },
        {
          q: '会社の経費精算で受理されるか不明',
          a: 'PDFの受理基準は会社や経理方針によって異なります。JTRは記録の整理を支援しますが、最終的な受理可否は社内規定や経理担当者の判断に従ってください。',
        },
      ],
      examples: [
        {
          who: '従業員の経費精算',
          use: '顧客訪問に利用した高速道路の利用証明書PDFをダウンロードし、社内の経費精算フォームに添付して提出します。',
        },
        {
          who: '個人事業主の記録保管',
          use: '月次でETC利用証明書PDFを年度フォルダに保存し、事業用と私用を仕分けるためにCSVファイルも併せて保管します。',
        },
        {
          who: '経理担当者の確認作業',
          use: '目視確認用にPDF証明書をチェックし、合計額の照合や会計システムへの取込みにはCSVを使用して二重の確認を行います。',
        },
        {
          who: '英語圏の居住者',
          use: '日本語のみのポータル操作を繰り返し行う負担を避けるため、JTRからPDFとCSVをメールで受け取り、記録管理を簡素化します。',
        },
      ],
      faqs: [
        {
          q: 'ETC明細はPDF形式でダウンロードできますか?',
          a: 'はい。公式ガイダンスによると、ETC利用照会サービスを通じて利用明細をPDFファイルとしてダウンロードすることが可能です。',
        },
        {
          q: 'PDFの保管期間はどのくらいですか?',
          a: '通常のETCクレジットカードおよびETCパーソナルカードは過去15か月分、ETC法人カードは過去62日分が公式サービスで照会可能です。ダウンロード後は任意の期間保管できます。',
        },
        {
          q: 'CSV形式もダウンロードすべきですか?',
          a: '会計ソフトへの取込み、部門別配分、月次照合、車両別集計が必要な場合はCSVも併せてダウンロードすることを推奨します。PDFとCSVは異なる用途に適しています。',
        },
        {
          q: 'JTRはPDFをメール配信できますか?',
          a: 'はい。JTRはユーザーの認証設定に基づき、整理されたETC記録をPDFおよびCSV形式でメール配信することができます。手作業でのダウンロードを減らすことが可能です。',
        },
        {
          q: 'PDFがあれば経費精算は通常は承認されますか?',
          a: 'いいえ。承認基準は会社や経理方針によって異なります。JTRは記録の整理を支援しますが、最終的な承認可否は社内規定や経理担当者の判断に従ってください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'PDF + CSV出力機能',
          desc: 'JTRは日次または月次でETC利用記録をPDFとCSV両方の形式で配信し、確認と会計処理の両方に対応します。',
          path: '/features',
        },
        {
          title: '個人事業主向けプラン',
          desc: '月次の記録保管、事業用・私用の仕分け、確定申告準備に役立つ自動配信とアーカイブ機能を提供します。',
          path: '/personal',
        },
        {
          title: '法人向けプラン',
          desc: '複数カード対応、部門別配分、経理チーム向けの反復可能な記録フロー構築を支援します。',
          path: '/business',
        },
      ],
      references: [
        {
          label: 'ETC総合情報ポータル:ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別、利用証明書、PDFおよびCSVダウンロード、通常ETCクレジットカードおよびETCパーソナルカードの15か月履歴、ETC法人カードの62日履歴、登録要件、登録後約4時間の初回表示タイミングに関する公式説明。',
        },
        {
          label: 'ETC利用照会サービス公式ポータル',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用明細の照会、利用証明書の発行、PDFまたはCSVファイルのダウンロードを行う公式サービス。',
        },
        {
          label: 'ETC利用照会サービスQ&A:登録後の表示タイミング',
          url: 'https://www.etc-meisai.jp/faq/11.html',
          note: '本登録完了後に利用明細データが表示されるまで約4時間かかる場合があることを説明する公式Q&A。',
        },
        {
          label: 'NEXCO東日本:高速道路利用料金のインボイス制度対応',
          url: 'https://www.driveplaza.com/cms/box_side_news/2023/0915/etc_Invoice-system_start.html',
          note: 'ETCクレジットカード利用時のETC利用照会サービスを通じた確定利用証明書の取扱いと、インボイス制度に関する注意事項を説明する公式通知。',
        },
        {
          label: 'NEXCO東日本:ETC利用履歴プリンター',
          url: 'https://www.driveplaza.com/sapa/shisetsu_service/etc_history_printer/',
          note: '選定されたサービスエリアおよびパーキングエリアに設置されたETC利用履歴プリンターに関する公式ガイダンス。当日通行分印刷の制限およびインボイス制度対応の取扱いについて記載。',
        },
      ],
      longBody: 'ETC利用証明書をPDF形式でダウンロードする作業は、日本で高速道路を利用するドライバーにとって最も重要な記録業務の一つです。ETCレーンを通過した際、料金所で紙の領収書が発行されることはほとんどなく、走行後の証明書類は後日、公式のETC利用照会サービスから利用証明書または利用明細として取得するのが一般的な流れとなっています。公式ポータルによれば、このサービスはETCクレジットカード、ETCパーソナルカード、ETCコーポレートカードに対応しており、通常のETCクレジットカードおよびETCパーソナルカードは過去15か月分、ETCコーポレートカードは過去62日分の利用明細が照会可能です。利用証明書の発行、利用明細の確認、PDFやCSV形式でのダウンロードが可能とされています。PDF形式が広く利用される理由は、閲覧、添付、印刷、保管がしやすく、経理承認者がスプレッドシートの列操作をせずに内容を確認でき、個人事業主が月別にファイルを整理でき、企業が共有会計フォルダに保存しやすいためです。PDF以外にもCSV形式は構造化された会計処理に適していますが、書面としての証憑性という観点ではPDFが最も扱いやすい形式です。公式サービスの画面構成は変更される可能性があるため、具体的なボタン名を固定的に記載することは避け、一般的なワークフローを説明する必要があります。利用者はまずアカウント登録を完了し、対象のETCカードを紐付け、ログイン後に利用明細を検索または表示し、必要な期間や走行を選択してPDF形式の利用証明書または明細をダウンロードします。登録時には車載器管理番号、ナンバープレートの一部、最近のETC無線通行実績などの情報が必要とされ、公式Q&Aでは初回登録後に利用明細データの表示まで約4時間かかる場合があると案内されています。急ぎの経費精算期限が迫ってから登録を試みると、即座に明細が表示されず困る可能性があるため、早めの登録が推奨されます。インボイス制度対応の観点では、NEXCO東日本の公式案内により、ETCクレジットカード利用時にインボイス対応書類が必要な場合は、ETC利用照会サービスから確定した利用証明書を取得するよう案内されており、早期発行や未確定の証明書、あるいはETC利用履歴プリンタの出力がインボイス要件を満たさない可能性があると注意されています。本記事では、すべてのPDFが自動的にあらゆる税務目的に適合すると断言せず、公式案内および会計専門家の助言に従うよう伝える必要があります。PDF記録はCSV記録と組み合わせることで最大の効果を発揮します。PDFは書類レビューに適し、CSVは構造化された会計処理や部署別・車両別の配分作業に適しています。PDF単体では後日の配分作業に苦労し、CSV単体では承認用の書面として扱いにくいため、Japan Toll Receiptsが提唱する価値提案はPDFとCSVの両形式を併用することです。JTRは公式サービスへの毎日または毎月の手動ログインを不要にし、利用者の認可設定に基づいて整理された通行記録をメールで受け取れるようにします。これは英語圏のドライバー、複数カードを管理する企業、繰り返し可能な記録フローを必要とする経理チームにとって特に有用です。JTRは公式記録を作成するのではなく、それらを配信・整理する補助を提供します。適切なPDFダウンロード習慣とは、早めにダウンロードし、明確な命名規則でファイルを保存し、会計処理が構造を必要とする場合にはCSVと併用することです。公式のETC利用照会サービスが記録の発行元であり、JTRは利用者が記録取得を忘れて緊急対応に追われる事態を防ぐワークフローとして機能します。',
    },
    en: {
      title: 'How to Download ETC Usage Certificate PDFs | Japan Toll Receipts',
      metaDesc: "Step-by-step instructions for downloading ETC usage certificate PDFs from the ETC Inquiry Service. Covers availability windows, retention periods, and JTR's automated delivery workflow.",
      h1: 'How to Download ETC Usage Certificate PDFs',
      primaryKeyword: 'ETC PDF download',
      answer: 'Log in to the ETC Meisai Inquiry Service, display your usage history, then download usage certificates or itemized statements as PDF files for your selected period or journey. Regular ETC credit cards and ETC Personal Cards retain records for 15 months; ETC Corporate Cards retain records for 62 days. JTR reduces repeated manual logins by delivering organized PDF and CSV records via email.',
      whyMatters: 'ETC lanes typically issue no paper receipt on the spot, so drivers must retrieve usage certificates from the official inquiry service afterward. PDFs are easy to view, attach, print, and archive. Expense approvers can review them without adjusting spreadsheet columns, and sole proprietors can store them by month with minimal effort. For invoice-system compliance, NEXCO recommends finalized usage certificates, making timing and confirmation important. Using PDFs alongside CSV records supports both document review and structured accounting workflows.',
      officialBackground: 'The ETC Meisai Inquiry Service is the official platform where eligible cardholders can issue usage certificates, review itemized statements, and download records in PDF or CSV format. It covers ETC credit cards, ETC Personal Cards, and ETC Corporate Cards. Regular ETC credit cards and ETC Personal Cards retain records for 15 months; ETC Corporate Cards retain records for 62 days. Registration requires card details, wireless-toll-collection transaction history, OBU management number, last four digits of the license plate, and usage dates. After initial registration, statement display may take approximately four hours. NEXCO East guidance recommends retrieving finalized usage certificates from the ETC Meisai Inquiry Service when invoice-system documentation is required.',
      whoFor: [
        'Drivers who need PDF-format toll records for expense reimbursement',
        'Sole proprietors who want to archive toll records by month',
        'Corporate users who require receipt-style ETC documentation',
        'Foreign residents who want English explanations of Japanese-language portal procedures',
      ],
      jtrSolution: {
        intro: 'JTR provides a PDF delivery and archiving workflow. The official ETC Meisai Inquiry Service is the authoritative issuer of usage certificates; JTR reduces manual effort by helping you receive and store organized records.',
        bullets: [
          'Receive organized toll records via email without repeated daily or monthly manual logins',
          'Useful for businesses managing multiple cards or drivers who need English-language support',
          'Prevents forgotten retrieval deadlines and last-minute stress',
          'Delivers both PDF and CSV formats to support document review and structured accounting',
          'Handles record organization and periodic delivery flow, not creation of official records',
        ],
      },
      pdfCsv: 'PDF records suit document review; CSV records suit structured accounting. PDF alone makes departmental or vehicle allocation difficult; CSV alone lacks a convenient document-format file for approvals. JTR provides both formats to serve different use cases.',
      email: 'JTR can deliver organized ETC records via email based on user-approved settings. This avoids repeated logins to the Japanese-only portal and helps English speakers and accounting teams maintain a repeatable record workflow.',
      steps: [
        {
          t: 'Pre-register with ETC Toll Inquiry Service',
          d: 'Before urgent expense deadlines, in-depth account registration with the ETC Toll Inquiry Service. You will need your ETC device management number, last 4 digits of license plate, and recent ETC wireless toll gate passage dates.',
        },
        {
          t: 'Check available statement period',
          d: 'Standard ETC credit cards and ETC Personal Cards allow access to the past 15 months; ETC Corporate Cards cover the past 62 days. Display statements within the available period.',
        },
        {
          t: 'Download usage certificate or statement as PDF',
          d: 'Select the required toll records and download the usage certificate or statement in PDF format. While screen layouts may change, the download function remains available.',
        },
        {
          t: 'Verify finalized status if Invoice System compliance is needed',
          d: 'According to NEXCO official guidance, finalized usage certificates are recommended for Invoice System compliance. Early-issued or unfinalized certificates may not meet requirements.',
        },
        {
          t: 'Save PDF files with clear file names',
          d: 'Use descriptive file names including year-month, card name, and driver or vehicle name. Organize into accounting folders and archive monthly for easier retrieval later.',
        },
        {
          t: 'Automate daily PDF delivery with JTR',
          d: 'To reduce repetitive manual logins, use JTR to deliver ETC records by email. Receiving both PDF and CSV formats supports both review and accounting workflows.',
        },
      ],
      commonProblems: [
        {
          q: 'Statements not visible immediately after registration',
          a: 'According to the official FAQ, usage statement data may take approximately 4 hours to appear after registration is finalized. For urgent needs, in-depth registration well in advance.',
        },
        {
          q: 'PDF alone is difficult for accounting processing',
          a: 'PDF is suitable for visual confirmation, but departmental allocation and vehicle-specific aggregation require CSV format. JTR provides both PDF and CSV to serve different purposes.',
        },
        {
          q: 'Difference between cash receipts and ETC usage certificates unclear',
          a: 'Cash receipts are issued at payment time, while ETC usage certificates are records retrieved from the inquiry service after passage. Format and issuance timing differ.',
        },
        {
          q: 'Uncertain if accepted for company expense reimbursement',
          a: 'PDF acceptance criteria vary by company and accounting policy. JTR assists with record organization, but final acceptance depends on internal rules and accounting staff judgment.',
        },
      ],
      examples: [
        {
          who: 'Employee expense reimbursement',
          use: 'Download usage certificate PDFs for expressway travel during client visits, attach them to internal expense reimbursement forms, and submit.',
        },
        {
          who: 'Sole proprietor record retention',
          use: 'Save monthly ETC usage certificate PDFs into annual folders and retain CSV files alongside to separate business and personal use.',
        },
        {
          who: 'Accounting staff verification',
          use: 'Review PDF certificates for visual confirmation, then use CSV for total reconciliation and accounting system import, enabling dual verification.',
        },
        {
          who: 'English-speaking residents',
          use: 'Avoid the burden of repeated navigation through Japanese-only portals by receiving PDFs and CSVs via email from JTR, simplifying record management.',
        },
      ],
      faqs: [
        {
          q: 'Can ETC statements be downloaded as PDF?',
          a: 'Yes. According to official guidance, usage statements can be downloaded as PDF files through the ETC Toll Inquiry Service.',
        },
        {
          q: 'How long are PDFs retained?',
          a: 'Standard ETC credit cards and ETC Personal Cards allow inquiry for the past 15 months; ETC Corporate Cards cover the past 62 days on the official service. After download, you can retain them for any period.',
        },
        {
          q: 'Should I also download CSV format?',
          a: 'If you need accounting software import, departmental allocation, monthly reconciliation, or vehicle-specific aggregation, downloading CSV alongside PDF is recommended. PDF and CSV serve different purposes.',
        },
        {
          q: 'Can JTR deliver PDFs by email?',
          a: 'Yes. JTR can deliver organized ETC records by email in both PDF and CSV formats based on your authentication settings, reducing manual download effort.',
        },
        {
          q: 'Will expense claims typically be approved if I have a PDF?',
          a: 'No. Approval criteria vary by company and accounting policy. JTR assists with record organization, but final approval depends on internal rules and accounting staff judgment.',
        },
      ],
      relatedFeatures: [
        {
          title: 'PDF + CSV export function',
          desc: 'JTR delivers ETC usage records daily or monthly in both PDF and CSV formats, supporting both review and accounting workflows.',
          path: '/features',
        },
        {
          title: 'Sole proprietor plan',
          desc: 'Provides automatic delivery and archiving features for monthly record retention, business-personal separation, and tax filing preparation.',
          path: '/personal',
        },
        {
          title: 'Corporate plan',
          desc: 'Supports multi-card handling, departmental allocation, and building repeatable record workflows for accounting teams.',
          path: '/business',
        },
      ],
      references: [
        {
          label: 'ETC General Information Portal: ETC Toll Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Official explanation covering eligible card types, usage certificates, PDF and CSV download, 15-month history for standard ETC credit cards and ETC Personal Cards, 62-day history for ETC Corporate Cards, registration requirements, and approximately 4-hour initial display timing after registration.',
        },
        {
          label: 'ETC Toll Inquiry Service Official Portal',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service for querying ETC card usage statements, issuing usage certificates, and downloading PDF or CSV files.',
        },
        {
          label: 'ETC Toll Inquiry Service FAQ: Display timing after registration',
          url: 'https://www.etc-meisai.jp/faq/11.html',
          note: 'Official FAQ explaining that usage statement data may take approximately 4 hours to appear after registration is finalized.',
        },
        {
          label: 'NEXCO East: Invoice System Compliance for Expressway Toll Charges',
          url: 'https://www.driveplaza.com/cms/box_side_news/2023/0915/etc_Invoice-system_start.html',
          note: 'Official notice explaining handling of finalized usage certificates through the ETC Toll Inquiry Service for ETC credit card usage and notes regarding Invoice System compliance.',
        },
        {
          label: 'NEXCO East: ETC Usage History Printers',
          url: 'https://www.driveplaza.com/sapa/shisetsu_service/etc_history_printer/',
          note: 'Official guidance on ETC usage history printers installed at selected service and parking areas, including limitations on same-day passage printing and Invoice System compliance handling.',
        },
      ],
      longBody: "Downloading an ETC utilization certificate in PDF format is one of the most important record-keeping tasks for drivers using expressways in Japan. When passing through an ETC lane, toll booths rarely issue paper receipts, and the standard procedure after travel is to obtain a utilization certificate or detailed statement later from the official ETC utilization inquiry service. According to the official portal, this service supports ETC credit cards, ETC personal cards, and ETC corporate cards, with standard ETC credit and personal cards offering access to the past fifteen months of records, while ETC corporate cards cover the past sixty-two days. Users can issue utilization certificates, review detailed statements, and download them in PDF or CSV format. PDF format is widely preferred because it is easy to view, attach, print, and store—accounting approvers can review contents without manipulating spreadsheet columns, sole proprietors can organize files by month, and companies can save them to shared accounting folders with minimal friction. While CSV format suits structured accounting workflows, PDF remains the most practical format from a documentary evidence perspective. Because the official service interface may change over time, this article avoids citing specific button labels and instead explains the general workflow. Users first in-depth account registration, link their ETC card, log in, search or display their utilization records, select the desired period or trip, and download the PDF-format utilization certificate or statement. Registration requires information such as the on-board unit management number, part of the license plate, and recent ETC wireless toll transactions; the official FAQ notes that it may take approximately four hours after initial registration for utilization data to appear. Attempting to register just before an urgent expense-reporting deadline can result in delays, so early registration is recommended. Regarding invoice-system compliance, NEXCO East Japan's official guidance states that when invoice-compliant documentation is required for ETC credit card use, users should obtain a finalized utilization certificate from the ETC utilization inquiry service, and warns that early issuance, unconfirmed certificates, or ETC usage history printer output may not meet invoice requirements. This article does not claim that all PDFs automatically satisfy every tax purpose, and users should follow official guidance and consult accounting professionals. PDF records deliver the greatest benefit when combined with CSV records—PDFs are suited to document review, while CSVs enable structured accounting processing and allocation by department or vehicle. PDF alone makes subsequent allocation tasks difficult, and CSV alone is awkward to handle as approval documentation, so Japan Toll Receipts' value proposition centers on using both formats together. JTR eliminates the need for daily or monthly manual logins to the official service, delivering organized toll records by email based on user authorization settings. This is especially useful for English-speaking drivers, companies managing multiple cards, and accounting teams requiring repeatable record workflows. JTR does not create official records—it helps deliver and organize them. Sound PDF download habits mean downloading early, saving files with clear naming conventions, and pairing PDFs with CSVs when accounting processes require structure. The official ETC utilization inquiry service is the issuing source of all records; JTR functions as a workflow that prevents users from forgetting to retrieve records and then scrambling to respond under time pressure.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: 'ETC利用証明書PDF取得の流れ',
            intro: '公式照会サービスからPDFをダウンロードする標準手順',
            nodes: [
              {
                title: 'ログイン',
                desc: 'ETC利用照会サービスにカード情報で認証',
              },
              {
                title: '期間選択',
                desc: '照会したい利用月または日付範囲を指定',
              },
              {
                title: '明細表示',
                desc: '該当期間の走行記録を画面で確認',
              },
              {
                title: 'PDF出力',
                desc: '利用証明書をPDF形式でダウンロード',
              },
              {
                title: '保存・提出',
                desc: '経費精算や月別アーカイブに使用',
              },
            ],
          },
          en: {
            title: 'ETC PDF Receipt Download Flow',
            intro: 'Standard steps to download PDFs from the official query service',
            nodes: [
              {
                title: 'Log in',
                desc: 'Authenticate with card details on ETC Query Service',
              },
              {
                title: 'Select period',
                desc: 'Choose the month or date range to review',
              },
              {
                title: 'View records',
                desc: 'Check on-screen list of toll transactions',
              },
              {
                title: 'Export PDF',
                desc: 'Download usage certificate in PDF format',
              },
              {
                title: 'Save & submit',
                desc: 'Use for expense claims or monthly archives',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: 'PDF vs CSV：用途別の適性',
            intro: '両形式の特性と推奨利用シーン',
            columns: [
              'PDF形式',
              'CSV形式',
            ],
            rows: [
              {
                feature: '書類添付',
                a: '承認者が直接閲覧可能',
                b: '開くには変換が必要',
                kindA: 'yes',
                kindB: 'no',
              },
              {
                feature: '印刷保管',
                a: 'レイアウト維持',
                b: '表計算ソフトで整形',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '部門別集計',
                a: '手作業で転記',
                b: 'ピボット・フィルタ可',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '会計ソフト連携',
                a: '手入力',
                b: 'インポート対応',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'インボイス対応',
                a: '公式書類として推奨',
                b: '補助データとして併用',
                kindA: 'yes',
                kindB: 'partial',
              },
            ],
          },
          en: {
            title: 'PDF vs CSV: Format Suitability',
            intro: 'Strengths of each format for different workflows',
            columns: [
              'PDF format',
              'CSV format',
            ],
            rows: [
              {
                feature: 'Attach to email',
                a: 'Readable by approvers',
                b: 'Needs conversion',
                kindA: 'yes',
                kindB: 'no',
              },
              {
                feature: 'Print & file',
                a: 'Layout preserved',
                b: 'Requires formatting',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Split by dept.',
                a: 'Manual copy-paste',
                b: 'Pivot & filter ready',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Accounting import',
                a: 'Manual entry',
                b: 'Direct import',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Invoice compliance',
                a: 'Recommended official doc',
                b: 'Supplementary data',
                kindA: 'yes',
                kindB: 'partial',
              },
            ],
          },
        },
      },
      {
        component: 'MonthlyReviewBoard',
        labels: {
          ja: {
            title: '月次ETC記録取得カレンダー',
            intro: '確定タイミングと取得推奨日（15か月保存期限に注意）',
            milestones: [
              {
                day: 5,
                label: '前月走行確定',
                kind: 'default',
              },
              {
                day: 10,
                label: 'PDF/CSV取得推奨',
                kind: 'action',
              },
              {
                day: 20,
                label: '経費締切（例）',
                kind: 'deadline',
              },
              {
                day: 31,
                label: '月末・翌月確認',
                kind: 'default',
              },
            ],
          },
          en: {
            title: 'Monthly ETC Record Retrieval Calendar',
            intro: 'Finalization timing & suggested download dates (note 15-month retention)',
            milestones: [
              {
                day: 5,
                label: 'Prior month finalized',
                kind: 'default',
              },
              {
                day: 10,
                label: 'Download PDF/CSV',
                kind: 'action',
              },
              {
                day: 20,
                label: 'Expense deadline (ex.)',
                kind: 'deadline',
              },
              {
                day: 31,
                label: 'Month-end review',
                kind: 'default',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: 'ETC記録の公式情報源',
            intro: '利用証明書の発行元と関連公式ガイド',
            sources: [
              {
                name: 'ETC利用照会サービス',
                role: '利用証明書・明細の正式発行元',
                url: 'etc-meisai.jp',
                scope: 'カード登録・PDF/CSVダウンロード',
              },
              {
                name: 'NEXCO東日本',
                role: 'インボイス制度対応案内',
                url: 'e-nexco.co.jp',
                scope: '確定した利用証明書の推奨',
              },
              {
                name: '高速道路6会社',
                role: '料金・割引制度の運営主体',
                url: '各社サイト',
                scope: '料金計算と割引ルール',
              },
              {
                name: 'Japan Toll Receipts',
                role: '記録配信とアーカイブ支援',
                url: 'japantollreceipts.jp',
                scope: '自動取得・整理・メール配信',
              },
            ],
          },
          en: {
            title: 'Official ETC Record Sources',
            intro: 'Where usage certificates originate and supporting guides',
            sources: [
              {
                name: 'ETC Query Service',
                role: 'Official issuer of certificates & statements',
                url: 'etc-meisai.jp',
                scope: 'Card registration, PDF/CSV download',
              },
              {
                name: 'NEXCO East',
                role: 'Invoice compliance guidance',
                url: 'e-nexco.co.jp',
                scope: 'Recommendation for finalized certificates',
              },
              {
                name: 'Six expressway operators',
                role: 'Toll & discount scheme authorities',
                url: 'Each co. site',
                scope: 'Fare calculation & discount rules',
              },
              {
                name: 'Japan Toll Receipts',
                role: 'Record delivery & archive helper',
                url: 'japantollreceipts.jp',
                scope: 'Auto-fetch, organize, email delivery',
              },
            ],
          },
        },
      },
    ],
  },

  // 2) ETC利用明細をCSVで出力する方法
  {
    slug: 'etc-meisai-csv-export-howto',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: 'ETC利用明細 CSV出力',
      en: 'ETC transaction details CSV export',
    },
    ja: {
      title: 'ETC利用明細をCSV出力する方法 | 会計・仕訳・日次配信 – JTR',
      metaDesc: 'ETC利用明細のCSV出力方法を解説。ETC利用照会サービスからのCSVダウンロード、freee・マネーフォワード クラウド・弥生会計・勘定奉行クラウドへの取り込み、カード別・車両別の集計、経費仕訳、JTRによる日次PDF+CSV自動配信まで業務効率化の手順を紹介します。',
      h1: 'ETC利用明細をCSV出力する方法',
      primaryKeyword: 'ETC利用明細 CSV出力',
      answer: 'ETC利用照会サービスでは、ETCカード・パーソナルカード・コーポレートカードの利用明細をCSV形式でダウンロードできます。CSVは並べ替え・集計・会計ソフトへの取込が可能で、PDF証明書と併用することで記録管理と仕訳作業の両立が図れます。JTRは日次でPDF+CSVを配信し、月末の手作業を削減します。',
      whyMatters: 'PDFは閲覧・印刷・添付に便利ですが、多数の記録を仕訳するにはCSVが有効です。複数カード・複数車両・部署別配賦・個人事業主の事業/私用区分など、集計と分類が必要な場面でCSVが役立ちます。会計チームはfreee・マネーフォワード・弥生などへ取り込み、個人事業主は取引先・用途列を追加して年度末処理を効率化できます。公式記録にはビジネス目的や部署情報は含まれないため、内部で列を補完する運用が重要です。',
      officialBackground: 'ETC利用照会サービスは、ETCクレジットカード・ETCパーソナルカード・ETCコーポレートカードの利用証明書発行・利用明細確認・PDF/CSVダウンロードに対応しています。通常のETCカード・パーソナルカードは約15か月分、コーポレートカードは約62日分の履歴が参照可能です。登録完了後の初回表示には約4時間、走行後の反映には交通量などにより時間を要する場合があります。インボイス制度下では利用証明書の取扱いにも留意が必要であり、国税庁Q&A・NEXCO案内を参照のうえ、専門家の助言を得ることが推奨されます。CSVは仕訳補助に有用ですが、証明書類としてはPDFとの併用が一般的です。',
      whoFor: [
        '会計チームが複数カードの明細を取り込み・集計する場合',
        '個人事業主が事業利用と私用を区分して記帳する場合',
        '部署・車両・プロジェクト別に通行料を配賦する企業',
        '月次締め前に使途確認や重複チェックを行う経理担当者',
      ],
      jtrSolution: {
        intro: 'JTRは日次でPDF利用証明書とCSV明細を配信し、毎月のログイン・ダウンロード・リネーム作業を軽減します。定期配信により月末は突合確認のみとなり、記録抜けや遅延の心配が減ります。',
        bullets: [
          'PDF+CSVを毎日メール配信 — 月末まとめ作業が不要',
          'カード・期間を指定した自動取得 — ログイン手順を省略',
          'ファイル名・フォルダ構成を統一 — 会計フローへ組込み可能',
          '利用直後から記録蓄積 — ドライバーが用途を忘れる前に確認',
          'PDFは証跡保管、CSVは仕訳取込 — 両立を一度に実現',
        ],
      },
      pdfCsv: 'JTRはPDF利用証明書とCSV明細を同時配信します。PDFは閲覧・印刷・添付用、CSVは並べ替え・集計・会計ソフト取込用として使い分けることで、記録の正確性と作業効率を両立できます。どちらか一方だけでは不十分な業務要件に対応します。',
      email: 'JTRは日次または週次で、指定メールアドレスへPDF+CSVを配信します。経理担当・車両管理者・部署責任者それぞれに必要な形式を届けることで、月末の手作業ダウンロードと社内転送の手間を削減し、記録漏れリスクを低減します。',
      steps: [
        {
          t: 'ETC利用照会サービスからCSVをダウンロード',
          d: '対象カードと期間を選択し、公式ポータルから利用明細をCSV形式でダウンロードします。通常のETCクレジットカードとETCパーソナルカードは過去15か月分、ETCコーポレートカードは過去62日分が取得可能です。',
        },
        {
          t: '対応するPDF利用証明書も保存',
          d: 'CSV と同じ期間の PDF 利用証明書または利用明細も取得し、書類確認や証憑保管に備えます。インボイス制度対応や税務相談時に PDF が必要になる場合があります。',
        },
        {
          t: '社内管理用の列を追加',
          d: '運転者、車両、部署、プロジェクト、業務目的、承認状況など、会社独自の分類項目を CSV に追加します。公式記録には業務目的や配賦先が含まれないため、社内で補完する必要があります。',
        },
        {
          t: 'カード明細と合計額を照合',
          d: 'CSV の合計金額をクレジットカード明細や経費精算申請と突き合わせ、不一致や重複申請がないか確認します。月末処理の前に定期的に照合すると差異の早期発見につながります。',
        },
        {
          t: '例外的な利用を早期に確認',
          d: '深夜や週末の走行、通常と異なる路線、高額な利用など、説明が必要な記録は運転者の記憶が鮮明なうちに確認します。不正の断定ではなく、業務目的の記録整備として進めます。',
        },
        {
          t: 'JTRで定期配信に切り替え',
          d: '毎月の手動ダウンロード作業を減らすため、JTR を活用して PDF と CSV を定期的にメール受信できる体制を検討します。月末が発見作業ではなく確認作業になります。',
        },
      ],
      commonProblems: [
        {
          q: 'CSVに業務目的が記載されていない',
          a: '公式記録には走行事実のみが記録されます。業務目的、訪問先、プロジェクト名などは社内で列を追加し、運転者や管理者が補完する運用が必要です。',
        },
        {
          q: '会計ソフトにそのまま取り込めない',
          a: 'freee、マネーフォワード、弥生などへの取り込みには列の並び替え、日付形式の変換、科目マッピングなどの加工が必要な場合があります。初回は試験的に取り込み、フォーマットを確認してください。',
        },
        {
          q: '直近の走行がCSVに反映されない',
          a: '利用照会サービスでは走行後の反映に時間がかかる場合があります。公式 Q&A によると初回登録後は約4時間、通常も交通量などで遅れることがあるため、余裕を持った取得スケジュールを組んでください。',
        },
        {
          q: 'CSVだけでインボイス対応できるか不安',
          a: '国税庁や NEXCO の案内では ETC 利用証明書の取扱いが示されています。CSV は集計作業に便利ですが、証憑としては PDF も保管し、税理士や会計担当者と確認することをお勧めします。',
        },
      ],
      examples: [
        {
          who: '個人事業主',
          use: 'CSV に業務目的・顧客名・事業/私用区分の列を追加し、年末の確定申告に向けて経費区分を整理しています。PDF は証憑として保管します。',
        },
        {
          who: '経理担当者',
          use: '月末に全カードの CSV をダウンロードし、スプレッドシートでカード別に集計してクレジットカード明細と突き合わせ、差異を確認しています。',
        },
        {
          who: '部門経理',
          use: 'ETC カードを部署ごとに分けて管理し、CSV の走行記録を部門別に配賦して月次コストレポートを作成しています。',
        },
        {
          who: '車両管理者',
          use: '深夜や週末に繰り返し発生している経路を CSV で抽出し、運転者にヒアリングして業務実態を確認しています。不正判定ではなく記録整備として実施しています。',
        },
      ],
      faqs: [
        {
          q: 'ETC 利用明細は CSV でダウンロードできますか?',
          a: 'はい。公式の ETC 利用照会サービスで利用明細を CSV 形式でダウンロードできます。対象カードと期間を選択して取得してください。',
        },
        {
          q: 'CSV と PDF、どちらを使えばよいですか?',
          a: 'CSV はデータ集計や会計ソフト取り込みに適し、PDF は書類確認や証憑保管に適しています。業務では両方を保管することをお勧めします。',
        },
        {
          q: 'JTR は毎日 CSV を送信できますか?',
          a: 'JTR は利用者の設定に基づき、整理された ETC 記録(PDF と CSV)を定期的にメール配信できる仕組みを提供しています。手動ダウンロードの負担軽減に役立ちます。',
        },
        {
          q: 'CSV に運転者や車両番号は記録されますか?',
          a: '公式記録には車載器管理番号などが含まれますが、運転者名や社内車両番号、部署名は含まれません。社内で列を追加して管理してください。',
        },
        {
          q: 'CSV だけでインボイス制度に対応できますか?',
          a: '前提にしないでください。国税庁や NEXCO の案内では利用証明書の取扱いが説明されています。税理士や会計担当者に確認し、PDF も併せて保管することをお勧めします。',
        },
      ],
      relatedFeatures: [
        {
          title: 'PDF + CSV 定期配信',
          desc: 'JTR は利用明細を PDF と CSV の両方で配信し、書類確認とデータ集計の両立を支援します',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数カードや部門別管理が必要な企業向けに、整理された記録配信で月末業務を効率化します',
          path: '/business',
        },
        {
          title: '個人事業主向けプラン',
          desc: '事業用と私用を分類しやすい形式で記録を受け取り、確定申告準備の負担を軽減します',
          path: '/personal',
        },
      ],
      references: [
        {
          label: 'ETC総合情報ポータル:ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '利用照会サービスの公式説明、対象カード種別、PDF・CSV ダウンロード、15か月/62日の履歴期間、登録後約4時間の初回表示タイミングについて記載',
        },
        {
          label: 'ETC利用照会サービス 公式ポータル',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETC カードの利用明細確認、利用証明書発行、PDF・CSV ファイルダウンロードを行う公式ポータル',
        },
        {
          label: 'ETC利用照会サービス Q&A:登録後の表示タイミング',
          url: 'https://www.etc-meisai.jp/faq/11.html',
          note: '利用照会サービス登録完了後、利用明細データの初回表示に約4時間かかる旨を説明する公式 Q&A',
        },
        {
          label: 'NEXCO東日本:インボイス制度における高速道路料金の取扱い',
          url: 'https://www.driveplaza.com/cms/box_side_news/2023/0915/etc_Invoice-system_start.html',
          note: 'ETC クレジットカード利用時の利用証明書取扱いとインボイス制度対応に関する NEXCO 公式案内',
        },
        {
          label: '国税庁:高速道路利用に係る適格請求書 Q&A 問103',
          url: 'https://www.nta.go.jp/taxes/shiraberu/zeimokubetsu/shohi/keigenzeiritsu/pdf/qa/103.pdf',
          note: 'ETC クレジットカード利用時の高速道路料金記録の取扱いを説明する国税庁公式 Q&A。税務判断は専門家に相談してください',
        },
      ],
      longBody: 'ETC利用明細のCSVエクスポート機能は、通行記録を書類から使いやすい会計データへ変換する仕組みです。PDFは閲覧や印刷、添付に適していますが、CSVは並べ替え、フィルタ、グループ化、インポート、照合といったデータ処理に適しています。複数のETCカードを持つ場合や出張回数が多い場合、事業用と私用の区分が必要な場合、月末の経理処理が発生する場合には、PDF単体よりもCSV形式が重要な役割を果たします。ETC利用照会サービスの公式案内によれば、登録済み利用者は利用証明書の発行、利用明細の確認、PDFまたはCSV形式での利用明細ダウンロードが可能とされており、対象にはETCクレジットカード、ETCパーソナルカード、ETCコーポレートカードが含まれます。照会可能期間は一般的にETCクレジットカードおよびETCパーソナルカードで15か月、ETCコーポレートカードで62日間とされています。\n\nCSV形式はコンマ区切りのテキストファイルであり、表計算ソフトや会計システムが読み込みやすい構造を持ちます。ETC業務においてCSVは、PDF単体では答えにくい疑問への対応を助けます。たとえば、どのカードが最も支出が多いか、どの車両が繰り返し同じ路線を使用しているか、どの部署にコストを割り振るべきか、重複請求がないか、請求内容がカード明細と一致しているか、深夜や週末の利用に説明が必要かといった確認作業です。個人事業主にとっては、事業用と私用の区分作業にCSVが役立ちます。同じETCカードで顧客訪問、家族の用事、空港送迎、プライベート旅行を行った場合、年末にはPDFが証拠資料として重要ですが、多数の行を素早く分類するにはCSVが便利です。利用者は事業目的、取引先、区分、控除可否、メモといった列を追加できます。税務アドバイスを提供するものではありませんが、CSVが分類作業を効率化することは説明できます。\n\n企業にとっては、個々のドライバーが想像する以上にCSVが重要です。経理部門はfreee、マネーフォワード、弥生会計、社内スプレッドシートなどへ記録をインポートする必要があることがあります。最終的なインポート形式にはクリーニングや列マッピングが必要な場合もありますが、PDF群よりもCSVエクスポートの方が作業の起点として適しています。企業は運転者、車両、部署、プロジェクト、承認状態といった内部列を追加することもできます。ただしCSVには限界があります。公式記録には社内の業務目的は含まれません。どの顧客訪問、作業指示、配送ルート、私用が通行の原因となったかは記録されません。企業がその文脈を追加する必要があります。CSV行は通行を示しますが、業務理由を判断することはできません。そのため最適な運用は、公式CSVデータと社内方針および管理者レビューを組み合わせることです。\n\nタイミングの考慮もPDFと同様にCSVで重要です。最近の通行記録がすぐに表示されない場合があり、新規登録アカウントでは明細が見えるまで時間を要する場合があります。公式Q&Aでは、初回の利用明細データが本登録完了後約4時間で表示される可能性があると案内されています。公式の走行反映案内でも、表示タイミングには変動があると記載されています。経理部門は余裕を持ったスケジュールを組み、即時詳細性を前提とすべきではありません。インボイス制度や税務処理に関わる業務では、CSV単体では不十分な場合があります。NEXCO資料や国税庁資料では、ETCクレジットカード通行料金に対する利用証明書の取り扱いが強調されています。CSVは会計作業を助けますが、PDF形式の利用証明書が必要となる場合もあります。最も安全な一般推奨は、PDFとCSVの両方を保管し、公式ガイダンスと専門家助言に従うことです。JTRの価値は、PDFとCSVを定期的に配信できる点にあります。担当者が毎月ポータルにログインし、ファイルをダウンロードし、名前を変更し、経理部門へ送信する代わりに、JTRは定期的な記録配信の構築を支援できます。企業は手動追跡ではなく、配信ファイルを中心に照合業務を組み立てることができます。',
    },
    en: {
      title: 'How to Export ETC Transaction Details to CSV | JTR',
      metaDesc: "Learn how to export ETC transaction details to CSV. From downloading via ETC Inquiry Service to importing into accounting software, plus JTR's daily PDF+CSV delivery.",
      h1: 'How to Export ETC Transaction Details to CSV',
      primaryKeyword: 'ETC transaction details CSV export',
      answer: 'The ETC Inquiry Service allows you to download transaction details for ETC cards, Personal cards, and Corporate cards in CSV format. CSV files enable sorting, aggregation, and import into accounting software. When used alongside PDF certificates, you can manage both record-keeping and bookkeeping tasks. JTR delivers PDF+CSV daily to eliminate month-end manual work.',
      whyMatters: 'While PDFs are convenient for viewing, printing, and attaching, CSV is essential for processing multiple records. CSV proves valuable when handling multiple cards, vehicles, departmental allocation, or separating business/personal use for sole proprietors. Accounting teams can import into freee, Money Forward, Yayoi, and similar platforms; sole proprietors can add client or purpose columns to streamline year-end processing. Since official records lack business purpose or department data, supplementing columns internally is key to effective workflow.',
      officialBackground: 'The ETC Inquiry Service supports certificate issuance, transaction detail viewing, and PDF/CSV downloads for ETC credit cards, ETC Personal cards, and ETC Corporate cards. Standard ETC and Personal cards retain approximately 15 months of history; Corporate cards retain roughly 62 days. Initial display after registration takes about 4 hours; post-trip reflection times vary with traffic volume. Under the invoice system, certificate handling requires attention—consult National Tax Agency Q&A, NEXCO guidance, and professional advisors. CSV assists bookkeeping, but PDF co-use remains standard practice for supporting documentation.',
      whoFor: [
        'Accounting teams importing and aggregating details from multiple cards',
        'Sole proprietors separating business and personal use for bookkeeping',
        'Companies allocating tolls by department, vehicle, or project',
        'Finance staff conducting usage review or duplicate checks before monthly close',
      ],
      jtrSolution: {
        intro: 'JTR delivers PDF certificates and CSV details daily, reducing monthly login, download, and file-rename tasks. Scheduled delivery limits month-end work to reconciliation only, reducing concerns about missing records or delays.',
        bullets: [
          'PDF+CSV emailed daily — no month-end batch work required',
          'Automated retrieval by card and period — skip login steps',
          'Unified file names and folder structure — integrate into accounting flow',
          'Records accumulate from the day of use — confirm purpose before drivers forget',
          'PDF for audit trail, CSV for journal entry — achieve both at once',
        ],
      },
      pdfCsv: 'JTR delivers PDF certificates and CSV details simultaneously. PDFs serve viewing, printing, and attachment needs; CSVs enable sorting, aggregation, and accounting software import. By dividing roles between formats, you maintain record accuracy while improving workflow efficiency—addressing requirements that neither format alone can satisfy.',
      email: 'JTR sends PDF+CSV to designated email addresses daily or weekly. Delivering the necessary format to accounting staff, vehicle managers, and department heads reduces month-end manual downloads and internal forwarding, lowering the risk of missing records.',
      steps: [
        {
          t: 'Download CSV from ETC Inquiry Service',
          d: 'Select the target card and period, then download the usage statement in CSV format from the official portal. Regular ETC credit cards and ETC personal cards allow retrieval of the past 15 months; ETC corporate cards allow the past 62 days.',
        },
        {
          t: 'Save corresponding PDF usage certificates',
          d: 'Obtain PDF usage certificates or statements for the same period as the CSV to support document review and record retention. PDFs may be needed for invoice system compliance or when consulting with tax advisors.',
        },
        {
          t: 'Add columns for internal management',
          d: 'Add company-specific classification fields to the CSV such as driver, vehicle, department, project, business purpose, and approval status. Official records do not include business purpose or allocation information, so internal supplementation is necessary.',
        },
        {
          t: 'Reconcile totals with card statements',
          d: 'Cross-check the CSV total amounts against credit card statements or expense reports to identify discrepancies or duplicate submissions. Regular reconciliation before month-end processing leads to early detection of variances.',
        },
        {
          t: 'Review exceptional usage early',
          d: "Review records requiring explanation—such as late-night or weekend trips, unusual routes, or high-value usage—while drivers' memories are fresh. Proceed as record-keeping rather than fraud determination.",
        },
        {
          t: 'Switch to scheduled delivery with JTR',
          d: 'To reduce monthly manual download work, consider using JTR to establish a system for regularly receiving PDFs and CSVs via email. Month-end becomes a review task rather than a discovery task.',
        },
      ],
      commonProblems: [
        {
          q: 'Business purpose is not recorded in the CSV',
          a: 'Official records contain only trip facts. Business purpose, destination, and project name must be supplemented internally by adding columns and having drivers or managers in-depth them in your workflow.',
        },
        {
          q: 'Cannot import directly into accounting software',
          a: 'Importing into freee, Money Forward, Yayoi, or similar requires processing such as column reordering, date format conversion, and account mapping. For the first import, test and verify the format requirements.',
        },
        {
          q: 'Recent trips are not reflected in the CSV',
          a: 'The inquiry service may take time to reflect trips. According to the official Q&A, initial registration takes about 4 hours, and normal updates can also be delayed by traffic volume and other factors. Plan your retrieval schedule with buffer time.',
        },
        {
          q: 'Unsure if CSV alone satisfies invoice requirements',
          a: 'The National Tax Agency and NEXCO provide guidance on handling ETC usage certificates. While CSV is convenient for aggregation, also retain PDFs as supporting documents and consult with your accountant or tax advisor.',
        },
      ],
      examples: [
        {
          who: 'Sole proprietor',
          use: 'Added columns for business purpose, client name, and business/personal classification to the CSV, organizing expense categories for year-end tax filing. PDFs are retained as supporting documents.',
        },
        {
          who: 'Accounting staff',
          use: 'At month-end, download CSVs for all cards, aggregate by card in a spreadsheet, reconcile with credit card statements, and verify discrepancies.',
        },
        {
          who: 'Department accountant',
          use: 'Manage ETC cards separately by department, allocate CSV trip records by division, and create monthly cost reports.',
        },
        {
          who: 'Vehicle manager',
          use: 'Extract repeated late-night or weekend routes from the CSV and interview drivers to confirm operational context. Conducted as record-keeping rather than fraud investigation.',
        },
      ],
      faqs: [
        {
          q: 'Can I download ETC usage statements in CSV format?',
          a: 'Yes. The official ETC Inquiry Service allows you to download usage statements in CSV format. Select the target card and period to retrieve the data.',
        },
        {
          q: 'Should I use CSV or PDF?',
          a: 'CSV is suited for data aggregation and importing into accounting software; PDF is suited for document review and record retention. We recommend retaining both for business purposes.',
        },
        {
          q: 'Can JTR send CSVs daily?',
          a: 'JTR provides a mechanism to regularly email organized ETC records (PDF and CSV) based on user settings. This helps reduce the burden of manual downloads.',
        },
        {
          q: 'Are driver names or vehicle numbers recorded in the CSV?',
          a: 'Official records include onboard unit management numbers and similar data, but do not include driver names, internal vehicle numbers, or department names. Add columns internally for management.',
        },
        {
          q: 'Can CSV alone satisfy invoice system requirements?',
          a: 'Do not assume so. The National Tax Agency and NEXCO provide guidance on handling usage certificates. Consult with your accountant or tax advisor and retain PDFs as well.',
        },
      ],
      relatedFeatures: [
        {
          title: 'PDF + CSV scheduled delivery',
          desc: 'JTR delivers statements in both PDF and CSV formats, supporting both document review and data aggregation',
          path: '/how-it-works',
        },
        {
          title: 'Business plan',
          desc: 'For companies requiring multi-card or departmental management, organized record delivery streamlines month-end operations',
          path: '/business',
        },
        {
          title: 'Sole proprietor plan',
          desc: 'Receive records in a format that simplifies business/personal classification, reducing the burden of tax filing preparation',
          path: '/personal',
        },
      ],
      references: [
        {
          label: 'ETC General Information Portal: ETC Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Official explanation of the inquiry service, eligible card types, PDF/CSV download, 15-month/62-day history periods, and initial display timing of about 4 hours after registration',
        },
        {
          label: 'ETC Inquiry Service Official Portal',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official portal for checking ETC card usage statements, issuing usage certificates, and downloading PDF and CSV files',
        },
        {
          label: 'ETC Inquiry Service Q&A: Display timing after registration',
          url: 'https://www.etc-meisai.jp/faq/11.html',
          note: 'Official Q&A explaining that usage statement data takes approximately 4 hours to display initially after inquiry service registration is completed',
        },
        {
          label: 'NEXCO East: Handling of expressway tolls under the invoice system',
          url: 'https://www.driveplaza.com/cms/box_side_news/2023/0915/etc_Invoice-system_start.html',
          note: 'Official NEXCO guidance on handling usage certificates when using ETC credit cards and compliance with the invoice system',
        },
        {
          label: 'National Tax Agency: Qualified invoices for expressway usage Q&A Question 103',
          url: 'https://www.nta.go.jp/taxes/shiraberu/zeimokubetsu/shohi/keigenzeiritsu/pdf/qa/103.pdf',
          note: 'Official National Tax Agency Q&A explaining handling of expressway toll records when using ETC credit cards. Consult professionals for tax judgments',
        },
      ],
      longBody: "The CSV export function for ETC usage records transforms toll transaction data from static receipts into flexible accounting information. While PDFs suit viewing, printing, and attachment, CSV files enable sorting, filtering, grouping, importing, and reconciliation. When managing multiple ETC cards, frequent business trips, separation of business and personal use, or monthly accounting closings, CSV format plays a more practical role than PDF alone. According to the official ETC usage inquiry service guidance, registered users may download usage statements in either PDF or CSV format, covering ETC credit cards, ETC personal cards, and ETC corporate cards. The inquiry period typically spans fifteen months for credit and personal cards and sixty-two days for corporate cards. CSV is a comma-separated text format easily read by spreadsheet and accounting software. In ETC administration, CSV helps answer questions that PDF alone makes difficult: Which card incurs the highest expense? Which vehicle repeatedly uses the same route? Which department should costs be allocated to? Are there duplicate charges? Does the statement match the card bill? Does late-night or weekend usage require explanation? For sole proprietors, CSV aids in separating business and personal use. When the same ETC card covers client visits, family errands, airport pickups, and private trips, PDF serves as evidence at year-end, but CSV allows faster classification of many rows. Users can add columns for business purpose, client, category, deductibility, and notes. While this is not tax advice, CSV clearly streamlines classification work. For companies, CSV matters more than individual drivers imagine. Accounting departments often need to import records into freee, Money Forward, Yayoi, or internal spreadsheets. Although final import may require cleaning and column mapping, a CSV export provides a better starting point than a stack of PDFs. Companies may add internal columns for driver, vehicle, department, project, and approval status. CSV has limits, however: official records contain no internal business purpose. They do not capture which client visit, work order, delivery route, or personal errand caused the toll. Companies must add that context themselves. CSV rows show the transaction but cannot determine the business reason, so the best practice combines official CSV data with internal policies and manager review. Timing considerations apply to CSV as they do to PDF. Recent transactions may not appear immediately, and newly registered accounts may wait before statements become visible. Official guidance indicates that initial usage data may appear roughly four hours after registration completes, and display timing varies. Accounting staff should allow margin and not assume instant completeness. For invoice or tax workflows, CSV alone may be insufficient. NEXCO and National Tax Agency materials emphasize the role of usage certificates in PDF form for ETC credit-card tolls. CSV supports accounting work, but PDF certificates may still be required. The safest general recommendation is to retain both PDF and CSV and follow official guidance and professional advice. JTR's value lies in delivering both PDF and CSV on a regular schedule. Instead of staff logging into the portal each month, downloading files, renaming them, and forwarding them to accounting, JTR can help build a routine delivery of records, allowing companies to organize reconciliation around delivered files rather than manual retrieval.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: 'ETC明細のCSV出力フロー',
            intro: 'ETC利用照会サービスでCSVをダウンロードし、会計ソフトへ取り込むまでの4ステップ。',
            nodes: [
              {
                title: '①ログイン',
                desc: 'ETC利用照会サービスにカード情報で登録・ログイン（初回約4時間で反映）',
              },
              {
                title: '②期間選択',
                desc: '対象カード・期間を指定（通常カード15か月/コーポレート62日分）',
              },
              {
                title: '③CSV出力',
                desc: '利用明細をCSV形式でダウンロード・ファイル保存',
              },
              {
                title: '④会計取込',
                desc: 'freee・マネフォ・弥生等へインポート、仕訳・集計・部署配賦を実施',
              },
            ],
          },
          en: {
            title: 'ETC CSV Export Flow',
            intro: 'Four steps from ETC usage inquiry service login to accounting software import.',
            nodes: [
              {
                title: '①Login',
                desc: 'Register/login to ETC service with card info (initial ~4h reflection)',
              },
              {
                title: '②Select Period',
                desc: 'Choose target card & period (15mo standard / 62d corporate)',
              },
              {
                title: '③Export CSV',
                desc: 'Download usage details in CSV format and save file',
              },
              {
                title: '④Import',
                desc: 'Load into freee/MF/Yayoi, then journalize/aggregate/allocate by dept',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: 'PDF vs CSV — 用途別の使い分け',
            intro: '利用証明書PDFと明細CSVはそれぞれ異なる強みを持ちます。JTRは両形式を同時配信。',
            columns: [
              'PDF利用証明書',
              'CSV明細',
            ],
            rows: [
              {
                feature: '閲覧・印刷',
                a: '○ 高画質・レイアウト保持',
                b: '△ 表計算ソフトで開く',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '会計ソフト取込',
                a: '× 手入力が必要',
                b: '○ 自動インポート対応',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '並べ替え・集計',
                a: '× 固定レイアウト',
                b: '○ 列ソート・SUM可能',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '証跡保管',
                a: '○ 公式フォーマット',
                b: '△ 補助資料',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '用途列追加',
                a: '× 編集困難',
                b: '○ 部署・PJ列を追加可',
                kindA: 'no',
                kindB: 'yes',
              },
            ],
          },
          en: {
            title: 'PDF vs CSV — Use-case Fit',
            intro: 'Official PDF and CSV detail each serve distinct roles. JTR delivers both daily.',
            columns: [
              'PDF Certificate',
              'CSV Detail',
            ],
            rows: [
              {
                feature: 'View/Print',
                a: '○ Hi-res layout',
                b: '△ Open in spreadsheet',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Acct import',
                a: '× Manual entry',
                b: '○ Auto import',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Sort/Sum',
                a: '× Fixed format',
                b: '○ Column sort/SUM',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Record keeping',
                a: '○ Official format',
                b: '△ Supplementary',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Custom cols',
                a: '× Hard to edit',
                b: '○ Add dept/project',
                kindA: 'no',
                kindB: 'yes',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: 'CSV出力に関する公式情報源',
            intro: 'ETC明細CSV取得・会計処理・インボイス制度の参考リンク一覧。',
            sources: [
              {
                name: 'ETC利用照会サービス',
                role: '明細CSV・PDF証明書ダウンロード',
                url: 'https://www.etc-meisai.jp/',
                scope: '登録・期間指定・出力手順',
              },
              {
                name: 'NEXCO東日本インボイス案内',
                role: '適格請求書発行者・証明書取扱い',
                url: 'https://www.e-nexco.co.jp/invoice/',
                scope: '登録番号・証明書の位置付け',
              },
              {
                name: '国税庁インボイスQ&A',
                role: '帳簿・証憑保存要件',
                url: 'https://www.nta.go.jp/taxes/shiraberu/zeimokubetsu/shohi/keigenzeiritsu/invoice_faq.htm',
                scope: '電子帳簿保存法との併用',
              },
              {
                name: 'freee・MF・弥生サポート',
                role: 'CSVインポート形式・列マッピング',
                url: '各社ヘルプページ',
                scope: '取込設定・仕訳テンプレート',
              },
            ],
          },
          en: {
            title: 'Official CSV Export Resources',
            intro: 'Key links for ETC CSV retrieval, accounting, and invoice compliance.',
            sources: [
              {
                name: 'ETC Inquiry Service',
                role: 'CSV/PDF download portal',
                url: 'https://www.etc-meisai.jp/',
                scope: 'Registration, period selection, export steps',
              },
              {
                name: 'NEXCO Invoice Guide',
                role: 'Qualified issuer & certificate handling',
                url: 'https://www.e-nexco.co.jp/invoice/',
                scope: 'Registration number, document status',
              },
              {
                name: 'NTA Invoice FAQ',
                role: 'Ledger & evidence retention rules',
                url: 'https://www.nta.go.jp/taxes/shiraberu/zeimokubetsu/shohi/keigenzeiritsu/invoice_faq.htm',
                scope: 'E-ledger law overlap',
              },
              {
                name: 'freee/MF/Yayoi Docs',
                role: 'CSV import format & column mapping',
                url: 'Vendor help sites',
                scope: 'Import settings, journal templates',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: 'CSV出力後の7つの確認ポイント',
            intro: 'ダウンロードしたCSVを会計ソフトへ取り込む前にチェックすべき項目。',
            items: [
              {
                text: '期間・カードが全て含まれているか',
                hint: '対象月の全走行が反映されているか件数確認',
              },
              {
                text: '重複レコードがないか',
                hint: '同一日時・区間が複数行になっていないか',
              },
              {
                text: '文字コードと区切り文字',
                hint: 'Shift_JIS/UTF-8、カンマ/タブ等のソフト互換性',
              },
              {
                text: '列の順序と名称',
                hint: '会計ソフトの取込設定と一致するかマッピング確認',
              },
              {
                text: '用途・部署列の追加',
                hint: '事業/私用、PJ・車両番号など内部管理列を補完',
              },
              {
                text: '消費税率と金額',
                hint: '税込/税抜の扱いと8%/10%区分（道路料金は10%）',
              },
              {
                text: 'PDFとの突合',
                hint: 'CSV合計額とPDF証明書記載額の一致確認',
              },
            ],
          },
          en: {
            title: '7 CSV Pre-Import Checks',
            intro: 'Essential validation steps before loading downloaded CSV into accounting software.',
            items: [
              {
                text: 'Period & cards complete?',
                hint: 'Verify all target month trips reflected in record count',
              },
              {
                text: 'No duplicate records?',
                hint: 'Check same date/time/section not appearing twice',
              },
              {
                text: 'Encoding & delimiter',
                hint: 'Shift_JIS/UTF-8, comma/tab compatibility with your software',
              },
              {
                text: 'Column order & names',
                hint: 'Match accounting import mapping settings',
              },
              {
                text: 'Add usage/dept columns',
                hint: 'Append business/personal, project, vehicle# for internal tracking',
              },
              {
                text: 'Tax rate & amounts',
                hint: 'Confirm incl/excl tax handling; toll is 10% in Japan',
              },
              {
                text: 'Reconcile with PDF',
                hint: 'CSV total must equal PDF certificate amount',
              },
            ],
          },
        },
      },
    ],
  },

  // 3) ETC利用照会サービスに表示されない走行がある理由
  {
    slug: 'etc-meisai-missing-trips-reasons',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: 'ETCメイサイ 記録欠落 原因',
      en: 'ETC Meisai missing records causes',
    },
    ja: {
      title: 'ETCメイサイ記録の欠落：原因と確認方法 | Japan Toll Receipts',
      metaDesc: 'ETCメイサイに記録が見つからない・反映が遅れる主な原因を切り分け解説。利用照会サービスの反映タイミング（通常1〜3日）、カード読み取り不良・車載器エラー・カード裏返し・期限切れ・登録情報のずれ、ETC利用照会サービスでの再確認手順、独立サービスJTRが日次配信で抜けに早期に気づく仕組みまで。',
      h1: 'ETCメイサイ記録の欠落：原因と確認方法',
      primaryKeyword: 'ETCメイサイ 記録欠落 原因',
      answer: 'ETCメイサイに利用記録が見つからない場合、反映タイミングの遅れ、カード挿入・読取エラー、無線不通による一般処理、運営者の記録処理、登録間違い、または別のETCカードでの利用が考えられます。記録がないと判断する前に、公式のETC利用照会サービスやカード発行会社の明細、該当する道路事業者へ問い合わせて確認することが重要です。',
      whyMatters: '経理担当者や運転者が通行料金の記録を探す際、記録の欠落は精算や報告の遅延につながります。反映待ち、カード問題、無線通信の不具合、登録内容の相違など複数の原因があるため、公式記録と照合せずに推測だけで処理すると誤った判断や二重請求のリスクが生じます。公式の照会窓口で確認し、PDF・CSV記録と社内メモを一緒に保管することで、後日の監査や問合せ対応が円滑になります。',
      officialBackground: '日本の有料道路は複数の運営者が管理しており、料金・割引・車種区分・IC情報は各事業者サイトで公開されています。ETC利用照会サービスはETCカードごとの利用明細と利用証明書を提供し、通常のETCカードは過去15か月分、ETCコーポレートカードは62か月分の記録を照会できます。カード発行会社も月次明細を提供する場合があります。記録の欠落が疑われるときは、まず該当カードの登録状態、車載器のエラー表示、通行日時と料金所名を確認し、ETC利用照会サービスまたは道路事業者の窓口へ問い合わせます。JTRは独立したサービスであり、公式の料金記録を作成する立場ではなく、ユーザーが取得したETC利用明細の受信・整理・保管・レビュー支援を行います。',
      whoFor: [
        'ETCメイサイで記録が見つからず原因を調べたいドライバー',
        '社用車や複数カードの利用記録を照合する経理担当者',
        '利用明細の欠落や不一致を問い合わせる顧客サポート担当者',
        '公式照会と社内記録を統合管理したい法人・官公庁の管理者',
      ],
      jtrSolution: {
        intro: 'JTRはETC利用明細をPDF・CSV形式で受信・整理し、記録の欠落や不一致を早期に発見できるよう支援します。公式データ作成者ではなく、ユーザーの記録管理を補助する独立サービスです。',
        bullets: [
          'PDF・CSV形式でETC利用明細を受信し、カードや車両ごとに整理',
          '複数カードの記録を一覧化し、欠落や重複の早期発見を支援',
          '通行日・IC名・車種・カード番号を構造化し検索・照合を効率化',
          '不明な記録や想定外の利用にフラグを立て、管理者レビューを促進',
          '公式照会結果と社内メモを一元保管し、後日の監査や精算に対応',
        ],
      },
      pdfCsv: 'JTRはETC利用明細をPDF形式とCSV形式で提供します。PDF形式は閲覧・共有に適し、CSV形式は表計算ソフトへのインポートや絞込み、部門別集計に便利です。Excel・XLSXファイルではなく、汎用性の高いPDF・CSVで出力されます。',
      email: 'JTRはETC利用明細を指定メールアドレスへ自動配信できます。経理担当者や管理者は毎月の記録を受信し、欠落の有無を早期に確認できます。配信設定は管理画面で調整可能で、複数の宛先やカード別の振分けにも対応しています。',
      steps: [
        {
          t: '対象となる利用明細の欠落パターンを確認する',
          d: '計上タイミング、カード・車載器の問題、無線/非無線処理、事業者の処理遅延、アカウント登録ミス、または別のETCカードで確認している可能性を整理します。',
        },
        {
          t: '公式の道路事業者情報を最初に確認する',
          d: 'ルート、料金額、車種区分、割引適用条件、ETC専用料金所、道路固有のルールに関する疑問点は、該当する道路事業者の公式サイトで確認します。',
        },
        {
          t: 'ETC利用照会サービスまたはカード明細を照合する',
          d: '利用後のETC利用明細行、利用証明書、PDFレコード、CSVレコードをETC利用照会サービスまたはカード発行会社の適切な情報源で確認します。',
        },
        {
          t: '利用日・IC・カード・車両・理由を統一形式で記録する',
          d: '入口IC、出口IC、ETCカード、車両、車種区分、ドライバーまたは部署、利用目的を一貫した社内フォーマットで記録し、後日の照会をスムーズにします。',
        },
        {
          t: '公式記録と社内説明を分離して保管する',
          d: '公式記録は通行実績を示し、社内メモは精算目的や承認状況を説明します。両者を明確に区別して保存することで、後の監査や質問対応が容易になります。',
        },
        {
          t: '不明点は推測せず公式窓口に問い合わせる',
          d: '記録が不詳細、遅延、予想外、または不一致の疑いがある場合は、推測で処理せず関連する道路事業者またはカード発行会社に問い合わせ、回答を記録と一緒に保管します。',
        },
      ],
      commonProblems: [
        {
          q: 'ETC利用照会サービスに特定の利用記録が表示されない',
          a: '計上タイミングの遅延、カード挿入エラー、非無線通行、または別のETCカードでの利用が原因の可能性があります。該当カード・期間・車両を再確認し、数日後に再度照会するか、道路事業者の窓口に問い合わせてください。',
        },
        {
          q: 'PDFとCSVのどちらを保存すべきか判断できない',
          a: 'PDFは閲覧・共有が容易で、CSVは並び替え・フィルタ・会計システムへの取り込みに適しています。両方を保存しておくことで、用途に応じて使い分けられます。',
        },
        {
          q: '社内の車両台帳と利用記録が一致しない',
          a: 'ETCカードと車両の組み合わせ、利用日時、入口IC・出口ICを照合し、ドライバーまたは部署への確認を行います。不明な利用がある場合は公式記録を確認してから対応してください。',
        },
        {
          q: '税務処理や精算にこの記録を使えるか不安',
          a: 'ETC利用記録は精算や税務レビューの補助資料として有用ですが、最終的な取扱いは勤務先の方針、税理士の助言、公式ガイダンスで確認してください。本ガイドは税務アドバイスではありません。',
        },
      ],
      examples: [
        {
          who: 'ドライバー',
          use: '通行料精算申請を提出する前に、どの公式情報源を確認すべきかを本ガイドで確認し、正しい記録を添付します。',
        },
        {
          who: '経理担当チーム',
          use: '月次のETC利用後にPDF + CSVレコードをエクスポートし、社内の車両・部署割当と照合してレビューを効率化します。',
        },
        {
          who: '複数車両を保有する家族',
          use: 'ETCカード記録を使って個人利用と業務利用を分離し、税理士に送付する資料を整理します。',
        },
        {
          who: '車両管理者',
          use: '見慣れない利用を発見した際、公式ETC利用記録を確認してからドライバーに経緯を問い合わせ、誤解やミスを防ぎます。',
        },
      ],
      faqs: [
        {
          q: 'JTRはこのトピックに関する公式の道路事業者ですか?',
          a: 'いいえ。JTRは独立したサービスです。公式のルート、料金、割引、設定、安全ルールは該当する道路事業者または公式ETCサービスで確認してください。JTRはETC利用記録の受信・整理・保管・レビュー支援を行います。',
        },
        {
          q: 'ETC利用照会サービスは道路事業者サイトの代わりになりますか?',
          a: 'いいえ。ETC利用照会サービスはETCカード利用明細と利用証明書の確認に有用ですが、現行のルート、料金、割引、レーン、車種区分ルールは各道路事業者サイトで確認する必要があります。',
        },
        {
          q: 'PDFとCSVのどちらを保存すべきですか?',
          a: '両方が有用です。PDFは閲覧・共有が容易で、CSVは並び替え・フィルタ・会計または社内レビューワークフローへの取り込みに適しています。',
        },
        {
          q: '料金やルートが間違っているように見える場合はどうすればよいですか?',
          a: '公式記録を確認し、カードと車両を再確認し、利用日とIC情報を照合してください。問題が解決しない場合は、該当する道路事業者またはカード発行会社に問い合わせてください。',
        },
        {
          q: 'ETC利用明細欠落時の主なリスクは何ですか?',
          a: '公式ETC利用記録と社内の業務コンテキストを一緒に保管せず、記憶・スクリーンショット・不詳細な明細に頼ることが主なリスクです。後日の説明や監査で困難が生じる可能性があります。',
        },
      ],
      relatedFeatures: [
        {
          title: 'ETC明細 入口IC不明の対応',
          desc: '「入口IC不明」と表示された走行の確認方法を解説します。',
          path: '/guides/etc-meisai-unknown-entry-ic',
        },
        {
          title: 'ETC明細の反映時間と月末対応',
          desc: '走行から明細反映までの時間と、月末締めの段取りを解説します。',
          path: '/guides/etc-meisai-update-timing',
        },
        {
          title: 'JTRの仕組み',
          desc: 'ETC利用記録の受信、整理、保管、レビュー支援の流れを確認し、PDF + CSV出力とカード・車両別の記録管理方法を理解できます。',
          path: '/how-it-works',
        },
        {
          title: 'ビジネス向けプラン',
          desc: '複数車両・複数部署の利用記録を一元管理し、精算・監査・ドライバー質問対応を効率化するビジネスプランの詳細を確認できます。',
          path: '/business',
        },
        {
          title: 'セキュリティとプライバシー',
          desc: 'JTRがどのように記録を保護し、公式データと社内説明を分離し、独立したサービスとして運用されているかを確認できます。',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用照会、利用証明書発行、PDF・CSV形式での利用明細ダウンロードが可能な公式サービス。通常のETCカード記録は過去15か月分が照会可能です。',
        },
        {
          label: 'ETC総合情報ポータル: ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別、利用証明書発行、通常ETCカード記録期間、ETCコーポレートカード記録期間、無線・非無線利用、PDF・CSVダウンロードの説明があります。',
        },
        {
          label: 'ETC総合情報ポータル: ETCを始める四輪・二輪',
          url: 'https://www.go-etc.jp/support',
          note: 'カード挿入時の注意、レーン利用ルール、エラーコード、二輪車ETC情報を含む公式サポートページです。',
        },
        {
          label: 'ETC総合情報ポータル: エラーコード一覧',
          url: 'https://www.go-etc.jp/support/code',
          note: 'ETC車載器のエラーコードに関する公式ガイダンス。カード挿入エラー、カード読取エラー、開閉バーが開かない場合の係員指示に従う手順が記載されています。',
        },
        {
          label: 'ETC総合情報ポータル: お問い合わせ',
          url: 'https://www.go-etc.jp/contact',
          note: '通行料金、割引、ETC利用に関する質問のための公式道路事業者カスタマーセンター一覧が掲載されています。',
        },
      ],
      longBody: 'ETC明細に通行記録が見当たらない場合、複数の原因が考えられます。最も多いのは利用照会サービスへのデータ反映タイミングのずれで、通行直後は未掲載でも数時間から翌営業日には反映されることがあります。また、ETCカードの挿入不良や車載器の読み取りエラーが発生した場合、無線通行として記録されず一般レーンでの現金扱いとなるため、ETC利用照会サービスには履歴が残りません。料金所でエラーコードが表示された際は係員の指示に従い、後日カード明細と照合する必要があります。複数のETCカードを使い分けている利用者は、別のカードで通行した記録を誤って探しているケースもよくあります。法人カードと個人カードを併用する場合、それぞれ照会先が異なるため確認漏れが生じやすくなります。さらに、ETC利用照会サービスへの初回登録時にカード番号や有効期限を誤入力すると、該当カードの履歴がほぼ表示されないため、登録情報の再確認が必要です。道路事業者側のシステム処理遅延や、通行直後の一時的な未反映も原因として挙げられます。特に深夜や早朝の通行は、バッチ処理のタイミングによって翌日以降の反映となる場合があります。記録が見つからない場合は、まず通行日時・入口IC・出口IC・使用カード・車両を正確に整理し、公式の利用照会サービスで該当期間を再検索します。それでも記録が確認できない場合は、カード発行会社の利用明細で請求の有無を確認し、必要に応じて道路事業者の問い合わせ窓口へ連絡します。JTRは独立したサービスとして、利用者がPDFおよびCSV形式で受け取った記録を整理・保管・確認する支援を行いますが、公式の通行データを生成する機能はありません。経理担当者や管理者が複数車両・複数カードの記録を照合する際、JTRは記録のグループ化や不明瞭な利用の早期発見を支援しますが、最終的な税務処理や不正判定は会計士や社内規程に従って行う必要があります。記録の欠落や不一致が見つかった場合は、推測や記憶に頼らず公式記録と社内メモを併せて保管し、後日の確認や精算に備えることが重要です。',
    },
    en: {
      title: 'Missing ETC Meisai Records: Causes & How to Check | JTR',
      metaDesc: 'Why ETC Meisai trips may be missing: timing delays, card errors, wireless failures, registration mistakes. Official inquiry channels and verification steps.',
      h1: 'Missing ETC Meisai Records: Causes & Verification',
      primaryKeyword: 'ETC Meisai missing records causes',
      answer: "When trip records are missing from ETC Meisai, common causes include reflection timing delays, card insertion or read errors, wireless communication failures resulting in manual-lane processing, operator record handling, registration mistakes, or use of a different ETC card. Before concluding a record is missing, verify via the official ETC Usage Inquiry Service, your card issuer's statement, and contact the relevant road operator.",
      whyMatters: 'Accounting staff and drivers searching for toll records face reporting and reconciliation delays when trips are missing. Multiple causes exist—reflection lag, card problems, wireless issues, registration mismatches—so processing on assumptions alone without cross-checking official records risks incorrect judgment or duplicate billing. Verifying through official inquiry windows and storing PDF and CSV records alongside internal notes streamlines future audits and customer inquiries.',
      officialBackground: "Japan's toll roads are managed by multiple operators, each publishing toll rates, discounts, vehicle classes, and IC information on their websites. The ETC Usage Inquiry Service provides usage details and proof-of-use certificates by ETC card: standard cards allow retrieval for the past 15 months, ETC Corporate Cards for 62 months. Card issuers may also provide monthly statements. When records appear missing, first confirm the card's registration status, check the onboard unit for error indicators, verify trip date/time and toll plaza name, then contact the ETC Usage Inquiry Service or the road operator's help desk. JTR is an independent service and does not create official toll records; it supports users by receiving, organizing, storing, and reviewing ETC usage data obtained from official sources.",
      whoFor: [
        'Drivers unable to find trip records in ETC Meisai and investigating the cause',
        'Accounting staff reconciling usage records across company vehicles and multiple cards',
        'Customer-support teams handling inquiries about missing or inconsistent usage data',
        'Corporate and government vehicle managers integrating official inquiries with internal records',
      ],
      jtrSolution: {
        intro: 'JTR receives and organizes ETC usage records in PDF and CSV formats, helping detect missing or inconsistent records early. It is an independent service that assists users with record management, not an official data creator.',
        bullets: [
          'Receive ETC usage records in PDF and CSV formats, organized by card and vehicle',
          'Consolidate records from multiple cards into a single view to spot gaps and duplicates early',
          'Structure trip date, IC names, vehicle class, and card numbers for efficient search and cross-check',
          'Flag unexpected or unidentified usage to prompt manager review',
          'Store official inquiry results and internal notes in one place for audits and reconciliation',
        ],
      },
      pdfCsv: 'JTR delivers ETC usage records in PDF and CSV formats. PDF is suited for viewing and sharing; CSV enables import into spreadsheet software, filtering, and departmental aggregation. Exports are PDF and CSV—not Excel or XLSX files—ensuring broad compatibility.',
      email: 'JTR can automatically deliver ETC usage records to designated email addresses. Accounting staff and managers receive monthly records and can verify the presence or absence of trips early. Delivery settings are adjustable in the dashboard, supporting multiple recipients and card-specific routing.',
      steps: [
        {
          t: 'Identify the pattern of missing trip details',
          d: 'Organize possibilities: posting timing delays, card or OBU issues, wireless vs. non-wireless processing, operator processing delays, account registration errors, or checking under a different ETC card.',
        },
        {
          t: 'Check official road operator information first',
          d: 'For questions about routes, toll amounts, vehicle classifications, discount eligibility, ETC-only toll plazas, or road-specific rules, consult the official website of the relevant road operator.',
        },
        {
          t: 'Cross-reference ETC inquiry service or card statements',
          d: 'Verify post-trip ETC detail lines, usage certificates, PDF records, and CSV records through the ETC inquiry service or the appropriate source from your card issuer.',
        },
        {
          t: 'Record date, IC, card, vehicle, and reason in a unified format',
          d: 'Log entry IC, exit IC, ETC card, vehicle, vehicle class, driver or department, and trip purpose in a consistent internal format to streamline future inquiries.',
        },
        {
          t: 'Store official records and internal explanations separately',
          d: 'Official records demonstrate actual usage; internal notes explain reimbursement purpose and approval status. Clearly separating the two makes later audits and questions easier to handle.',
        },
        {
          t: 'Do not guess on unclear points; contact official channels',
          d: 'When records are incomplete, delayed, unexpected, or suspected of mismatch, do not process by guesswork—contact the relevant road operator or card issuer and file the reply with your records.',
        },
      ],
      commonProblems: [
        {
          q: 'A specific trip does not appear in the ETC inquiry service',
          a: "Possible causes include posting timing delays, card insertion errors, non-wireless passage, or use of a different ETC card. Re-check the card, period, and vehicle in question, re-query after a few days, or contact the road operator's help desk.",
        },
        {
          q: 'Cannot decide whether to save PDF or CSV',
          a: 'PDFs are easy to view and share; CSVs are suited for sorting, filtering, and importing into accounting systems. Saving both lets you choose the appropriate format for each use case.',
        },
        {
          q: 'Internal vehicle register does not match usage records',
          a: 'Cross-check ETC card and vehicle combinations, date-times, entry and exit ICs, and confirm with the driver or department. If any usage is unclear, verify the official record before taking action.',
        },
        {
          q: 'Unsure if I can use this record for tax processing or reimbursement',
          a: "ETC usage records are useful supporting materials for reimbursement or tax review, but final handling should be confirmed by your employer's policy, accountant's advice, or official guidance. This guide is not tax advice.",
        },
      ],
      examples: [
        {
          who: 'Driver',
          use: 'Before submitting a toll reimbursement claim, consults this guide to identify which official sources to check and attaches the correct records.',
        },
        {
          who: 'Accounting team',
          use: 'After monthly ETC usage, exports PDF + CSV records and cross-references them with internal vehicle and department assignments to streamline review.',
        },
        {
          who: 'Family with multiple vehicles',
          use: 'Uses ETC card records to separate personal and business trips, organizing materials to send to their accountant.',
        },
        {
          who: 'Vehicle manager',
          use: 'Upon discovering unfamiliar usage, checks official ETC usage records before asking the driver for details, preventing misunderstandings or mistakes.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR the official road operator for this topic?',
          a: 'No. JTR is an independent service. For official routes, tolls, discounts, settings, and safety rules, consult the relevant road operator or official ETC service. JTR helps you receive, organize, store, and review ETC usage records.',
        },
        {
          q: 'Does the ETC inquiry service replace road operator websites?',
          a: "No. The ETC inquiry service is useful for checking ETC card detail lines and usage certificates, but current routes, tolls, discounts, lanes, and vehicle class rules must be confirmed on each road operator's website.",
        },
        {
          q: 'Should I save PDF or CSV?',
          a: 'Both are useful. PDFs are easy to view and share; CSVs are suited for sorting, filtering, and importing into accounting or internal review workflows.',
        },
        {
          q: 'What if a toll or route looks incorrect?',
          a: 'Verify official records, re-check the card and vehicle, and cross-reference the trip date and IC information. If the issue persists, contact the relevant road operator or card issuer.',
        },
        {
          q: 'What are the main risks when ETC detail is missing?',
          a: 'The main risk is relying on memory, screenshots, or incomplete statements instead of storing official ETC usage records together with internal business context. This can create difficulties in later explanations or audits.',
        },
      ],
      relatedFeatures: [
        {
          title: 'ETC entry IC unknown — recovery steps',
          desc: 'What to do when ETC Usage Inquiry Service (MEISAI) shows \'entry IC unknown\' on a trip.',
          path: '/guides/etc-meisai-unknown-entry-ic',
        },
        {
          title: 'ETC statement update timing',
          desc: 'How long trips take to appear and how to plan month-end closing.',
          path: '/guides/etc-meisai-update-timing',
        },
        {
          title: 'How JTR Works',
          desc: 'Understand the flow of receiving, organizing, storing, and reviewing ETC usage records, and learn how PDF + CSV output and card-and-vehicle-based record management work.',
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'Review the details of business plans that centralize multi-vehicle and multi-department usage records, streamlining reimbursement, audits, and driver inquiries.',
          path: '/business',
        },
        {
          title: 'Security and Privacy',
          desc: 'See how JTR protects records, separates official data from internal explanations, and operates as an independent service.',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC Usage Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service for ETC card usage inquiry, usage certificate issuance, and downloading trip details in PDF and CSV formats. Standard ETC card records can be queried for the past 15 months.',
        },
        {
          label: 'ETC General Information Portal: ETC Usage Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Describes eligible card types, usage certificate issuance, standard ETC card record periods, ETC corporate card record periods, wireless and non-wireless usage, and PDF and CSV downloads.',
        },
        {
          label: 'ETC General Information Portal: Getting Started with ETC for Cars and Motorcycles',
          url: 'https://www.go-etc.jp/support',
          note: 'Official support page covering card insertion precautions, lane usage rules, error codes, and motorcycle ETC information.',
        },
        {
          label: 'ETC General Information Portal: Error Code List',
          url: 'https://www.go-etc.jp/support/code',
          note: 'Official guidance on ETC onboard unit error codes, including card insertion errors, card read errors, and procedures to follow staff instructions when barrier gates do not open.',
        },
        {
          label: 'ETC General Information Portal: Contact',
          url: 'https://www.go-etc.jp/contact',
          note: 'Lists official road operator customer centers for questions regarding tolls, discounts, and ETC usage.',
        },
      ],
      longBody: "When a trip record is missing from your ETC meisai, several factors may be at play. The most common cause is a delay in data reflection to the official ETC inquiry service; records from recent passages often appear several hours later or by the next business day. If your ETC card was not seated properly or the onboard unit failed to read it, the passage would not register as wireless ETC usage and would instead be treated as a manual cash transaction at a staffed lane, leaving no trace in the ETC inquiry system. When an error code appears at a toll gate, follow the attendant's instructions and later cross-check your card statement. Users who rotate multiple ETC cards frequently search under the wrong card by mistake. Corporate and personal cards require separate inquiry endpoints, increasing the risk of overlooked records. Additionally, if you mistype the card number or expiration date during your first registration with the ETC inquiry service, no history for that card will display, so re-verifying your registration details is essential. System processing delays on the road operator's side and temporary non-reflection immediately after passage are also known causes. Trips during late-night or early-morning hours may appear only the following day due to batch-processing schedules. If a record cannot be found, first compile accurate details—date and time of passage, entry interchange, exit interchange, card used, and vehicle—then re-search the relevant period in the official inquiry service. If the record still does not appear, check your card issuer's billing statement to confirm whether a charge was posted, and contact the road operator's helpdesk if necessary. JTR operates as an independent service that helps users organize, store, and review records received in PDF and CSV formats; it does not generate official toll data. When accounting staff or vehicle managers reconcile records across multiple vehicles and cards, JTR supports grouping and early identification of unclear entries, but final tax treatment and fraud determination must follow guidance from accountants and internal policies. When gaps or discrepancies are discovered, preserve both official records and internal notes rather than relying on memory or assumptions, ensuring readiness for future review and settlement.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: 'ETC記録の反映フロー',
            intro: '利用から照会可能になるまでの処理経路',
            nodes: [
              {
                title: '料金所通過',
                desc: '車載器がETCカード情報と通行データを記録',
              },
              {
                title: '事業者処理',
                desc: '道路運営者が通行記録を受信・検証',
              },
              {
                title: 'カード会社連携',
                desc: '決済情報がカード発行会社へ送信',
              },
              {
                title: '照会サービス反映',
                desc: 'ETC利用照会サービスで記録が閲覧可能に（通常数日）',
              },
              {
                title: '月次明細発行',
                desc: 'カード会社が請求明細書を作成・送付',
              },
            ],
          },
          en: {
            title: 'ETC Record Reflection Flow',
            intro: 'Processing path from toll passage to query availability',
            nodes: [
              {
                title: 'Toll Gate Passage',
                desc: 'On-board unit records ETC card and trip data',
              },
              {
                title: 'Operator Processing',
                desc: 'Road operator receives and validates trip records',
              },
              {
                title: 'Card Issuer Link',
                desc: 'Payment data transmitted to card issuer',
              },
              {
                title: 'Query Service Update',
                desc: 'Record becomes visible in ETC Query Service (typically days)',
              },
              {
                title: 'Monthly Statement',
                desc: 'Card issuer generates and sends billing statement',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '記録欠落時の公式照会先',
            intro: '記録が見つからない場合の確認窓口',
            sources: [
              {
                name: 'ETC利用照会サービス',
                role: 'カード別の利用明細と証明書を提供',
                url: 'https://www.etc-meisai.jp',
                scope: '過去15か月分（通常カード）または62か月分（コーポレート）',
              },
              {
                name: 'カード発行会社',
                role: '月次請求明細と決済記録を管理',
                url: 'カード裏面の連絡先',
                scope: '請求済み料金の詳細・未請求分の確認',
              },
              {
                name: 'NEXCO各社お客様センター',
                role: '高速道路の通行記録を直接照会',
                url: '各社公式サイト',
                scope: '東日本・中日本・西日本の管轄路線',
              },
              {
                name: '首都高・阪神高速',
                role: '都市高速の料金問い合わせ',
                url: '各社お客様センター',
                scope: '首都高速・阪神高速の利用記録',
              },
              {
                name: '地方道路公社',
                role: '該当する地方有料道路の記録',
                url: '各公社窓口',
                scope: '本州四国連絡高速道路・その他地方路線',
              },
            ],
          },
          en: {
            title: 'Official Inquiry Channels for Missing Records',
            intro: 'Where to verify when records are not found',
            sources: [
              {
                name: 'ETC Meisai Service',
                role: 'Provides card-specific usage statements and certificates',
                url: 'https://www.etc-meisai.jp',
                scope: 'Past 15 months (standard) or 62 months (corporate)',
              },
              {
                name: 'Card Issuer',
                role: 'Manages monthly billing and payment records',
                url: 'Contact on card back',
                scope: 'Billed toll details and pending charges',
              },
              {
                name: 'NEXCO Customer Centers',
                role: 'Direct inquiry for expressway trip records',
                url: "Each company's official site",
                scope: 'East, Central, and West Japan expressway networks',
              },
              {
                name: 'Shuto/Hanshin Expressways',
                role: 'Urban expressway toll inquiries',
                url: "Each company's customer center",
                scope: 'Metropolitan and Osaka-area expressway records',
              },
              {
                name: 'Local Road Corporations',
                role: 'Regional toll road records',
                url: "Each corporation's office",
                scope: 'Honshu-Shikoku Bridge and other local toll roads',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: '記録欠落の主な原因比較',
            intro: '想定される理由と確認方法の違い',
            columns: [
              '反映待ち',
              'カード・車載器エラー',
            ],
            rows: [
              {
                feature: '記録は実際に存在',
                a: '数日後に照会可能',
                b: '無線不通なら一般扱い記録',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '通行直後の確認',
                a: '未反映が正常',
                b: 'エラー音・表示で判別可能',
                kindA: 'neutral',
                kindB: 'yes',
              },
              {
                feature: '公式照会で解決',
                a: '時間経過で自動反映',
                b: '道路事業者への問合せ必要',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '利用者の対処',
                a: '待機のみ',
                b: 'カード再挿入・車載器点検',
                kindA: 'neutral',
                kindB: 'yes',
              },
              {
                feature: '請求への影響',
                a: '予定通り請求',
                b: '別経路で請求される場合あり',
                kindA: 'neutral',
                kindB: 'partial',
              },
            ],
          },
          en: {
            title: 'Common Causes of Missing Records',
            intro: 'Differences in suspected reasons and verification methods',
            columns: [
              'Processing Delay',
              'Card/Unit Error',
            ],
            rows: [
              {
                feature: 'Record actually exists',
                a: 'Available after few days',
                b: 'General-lane record if wireless failed',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Immediate check',
                a: 'Non-reflection is normal',
                b: 'Identifiable by error sound/display',
                kindA: 'neutral',
                kindB: 'yes',
              },
              {
                feature: 'Resolved by official query',
                a: 'Auto-reflects over time',
                b: 'Requires road operator inquiry',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'User action',
                a: 'Wait only',
                b: 'Re-insert card, check unit',
                kindA: 'neutral',
                kindB: 'yes',
              },
              {
                feature: 'Billing impact',
                a: 'Charged as scheduled',
                b: 'May be billed via alternate path',
                kindA: 'neutral',
                kindB: 'partial',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '記録欠落時の確認チェックリスト',
            intro: '問い合わせ前に確認すべき8項目',
            items: [
              {
                text: '通行から3～5営業日経過したか',
                hint: '反映には通常数日かかります',
              },
              {
                text: '正しいETCカード番号で照会したか',
                hint: '複数カード保有時は番号を再確認',
              },
              {
                text: '車載器にエラー表示・音が出たか',
                hint: 'エラー時は一般レーン扱いの可能性',
              },
              {
                text: 'カードは正しく挿入されていたか',
                hint: '挿入不良は記録されない原因',
              },
              {
                text: 'ETC利用照会サービスに登録済みか',
                hint: '未登録カードは照会不可',
              },
              {
                text: '通行した料金所名と日時を特定できるか',
                hint: '問合せ時に必須の情報',
              },
              {
                text: 'カード発行会社の月次明細を確認したか',
                hint: '請求済みなら記録は存在',
              },
              {
                text: '該当する道路事業者の窓口を把握したか',
                hint: 'NEXCO・首都高・地方公社など',
              },
            ],
          },
          en: {
            title: 'Missing Record Verification Checklist',
            intro: '8 items to check before contacting support',
            items: [
              {
                text: 'Has 3–5 business days passed since passage?',
                hint: 'Reflection typically takes several days',
              },
              {
                text: 'Queried with correct ETC card number?',
                hint: 'Re-verify number if multiple cards held',
              },
              {
                text: 'Did unit display error or sound alert?',
                hint: 'Errors may result in general-lane processing',
              },
              {
                text: 'Was card inserted correctly?',
                hint: 'Poor insertion prevents recording',
              },
              {
                text: 'Is card registered with ETC Query Service?',
                hint: 'Unregistered cards cannot be queried',
              },
              {
                text: 'Can you specify toll gate name and datetime?',
                hint: 'Essential information for inquiries',
              },
              {
                text: "Checked card issuer's monthly statement?",
                hint: 'If billed, record exists',
              },
              {
                text: 'Identified the relevant road operator contact?',
                hint: 'NEXCO, Shuto, local corporations, etc.',
              },
            ],
          },
        },
      },
    ],
  },

  // 4) ETC休日割引が適用されない日を確認する方法
  {
    slug: 'etc-kyujitsu-warikibi-exclusion-days',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: 'ETC休日割引 適用除外日',
      en: 'ETC holiday discount exclusion days',
    },
    ja: {
      title: 'ETC休日割引の適用除外日を確認する方法 | Japan Toll Receipts',
      metaDesc: 'ETC休日割引の適用除外日（ゴールデンウィーク・お盆・年末年始の繁忙期）の仕組み、除外日カレンダーの公式公表元、適用される車種区分、ETC利用照会サービスで実際の割引適用の有無を確認する手順、週末でも割引対象外になる理由、独立サービスJTRを併用した月次レビューフローまで網羅した、ドライバー・経理向けの実務ガイド。',
      h1: 'ETC休日割引の適用除外日を確認する',
      primaryKeyword: 'ETC休日割引 適用除外日',
      answer: 'ETC休日割引は土日祝日に適用されますが、ゴールデンウィーク・お盆・年末年始・シルバーウィーク・三連休などの交通混雑期は適用除外となります。NEXCO各社が毎年公式カレンダーを公開しているため、旅行前に該当年度の除外日を確認することで、想定外の通行料金を防げます。',
      whyMatters: '休日割引は「休日だから適用される」と思われがちですが、日本の高速道路では混雑緩和の観点から主要連休期間が除外されています。前年のカレンダーやSNS情報を流用すると、実際の除外日とずれる可能性があります。経理担当者が週末の通行料金に割引が適用されていない理由を確認する際も、公式の年間カレンダーと照合する必要があります。物流事業者が繁忙期のコスト計画を立てる場合も、除外日を事前に把握しておくことで正確な予算を組めます。',
      officialBackground: 'ETC利用の正式な記録階層は、道路事業者・ETCカード・ETC利用照会サービスです。利用照会サービスは、利用者が利用明細を確認し、利用証明書を印刷し、PDFやCSVをダウンロードする場所です。割引の具体的なルールについては、NEXCO各社・ETCマイレージサービス・ETC2.0の公式ページが管理しています。割引や精算に関する記事が絶対的に聞こえると誤解を招く恐れがあるため、利用者から「なぜ割引されなかったのか」と問われた際、推測ではなく日付・時間・車種・カード・ルート・道路事業者・登録状況・公式発表を確認することが安全です。JTRはその確認作業を整理された日次記録で支援しますが、公式な割引判定は料金システムと事業者ルールに委ねられます。',
      whoFor: [
        '連休期間に長距離ドライブを計画する旅行者',
        '季節ごとの通行料金予算を管理する物流事業者',
        '週末の通行料金に割引が適用されなかった理由を確認する経理担当者',
        'ゴールデンウィークやお盆の帰省を計画する在日外国人',
      ],
      jtrSolution: {
        intro: 'Japan Toll Receipts(JTR)は、ETC利用記録を整理して定期配信する独立サービスです。NEXCO・MEISAI・ETC利用照会サービス・マイレージサービス・道路事業者ではありません。JTRは公式料金データを作成せず、割引適用を判定せず、精算承認を保証しません。組織化されたPDF・CSV記録を配信することで、手作業を減らし人間による確認を支援します。',
        bullets: [
          'ETC記録をメールで定期受信し、月末まで待たずに確認できる',
          '受信トレイに実用的な取扱履歴を作成し、見逃しや異常ルートを早期発見',
          '公式サービスを確認する前に、整理された記録で事実関係を把握',
          'PDF形式(文書レビュー用)とCSV形式(並び替え・照合・会計処理用)の両方を保管',
          '割引ルールや事業者規則の代替ではなく、記録整理ツールとして活用',
        ],
      },
      pdfCsv: 'JTRはPDF(証明書スタイルの閲覧用)とCSV(並び替え・照合・会計ソフト連携用)の両形式で記録を提供します。PDFは人間が読みやすく、CSVは大量データの処理に適しています。両方を保管することで、後日の確認作業がスムーズになります。',
      email: 'JTRはETC記録をメールで定期配信するため、月末を待たずに利用状況を把握できます。受信トレイに記録が蓄積されることで、カード違い・未登録ルート・想定外の割引結果などを早期に発見し、公式サービスでの確認や社内承認プロセスを円滑に進められます。',
      steps: [
        {
          t: '車両区分が休日割引の対象かを確認',
          d: '普通車・軽自動車等(二輪車含む)が対象。中型車・大型車など営業用車両は対象外のため、まず車種を確認します。',
        },
        {
          t: '通行する道路が対象路線かを確認',
          d: '東京・大阪近郊区間や沖縄自動車道など一部路線は休日割引の対象外です。NEXCO公式ページで除外路線を確認します。',
        },
        {
          t: '通行日が土日祝日かを確認',
          d: '休日割引は土曜・日曜・国民の祝日が基本対象日です。平日は対象外となります。',
        },
        {
          t: '公式の年間除外日カレンダーを確認',
          d: 'ゴールデンウィーク・お盆・年末年始・シルバーウィーク・三連休などの渋滞期は除外日として指定されています。NEXCO発表の年度別カレンダーで確認します。',
        },
        {
          t: '通行後にETC利用照会サービスで実際の請求額を確認',
          d: '通行後、ETC利用照会サービスで記録された実際の料金を確認します。料金所表示やETC車載器の音声は確定額でない場合があります。',
        },
        {
          t: 'PDF・CSV記録を保管し後日検証できる状態にする',
          d: '後日の確認や経理処理のため、ETC利用照会サービスから取得したPDFおよびCSV記録を月次で整理・保管します。',
        },
      ],
      commonProblems: [
        {
          q: '土日なのに休日割引が適用されなかった',
          a: 'ゴールデンウィーク・お盆・年末年始・シルバーウィーク・三連休などは「渋滞対策期間」として除外日に指定されます。NEXCO公式の年間カレンダーで該当日が除外日かを確認してください。',
        },
        {
          q: '割引が適用されているか料金所で確認できない',
          a: '料金所の表示やETC車載器の音声は速報的なものであり、最終確定額ではない場合があります。ETC利用照会サービスまたはクレジットカード明細で実際の請求額を確認してください。',
        },
        {
          q: '前年と同じ日程なのに今年は割引されない',
          a: '除外日は年度ごとに変わります。前年の記憶や保存した画像をそのまま使わず、通常は最新年度のNEXCO公式カレンダーを確認してください。',
        },
        {
          q: 'JTRの記録で割引有無を判断してよいか',
          a: 'JTRは記録整理・配信サービスであり、割引可否の判定機関ではありません。割引適用条件や最終請求額の確認は、ETC利用照会サービスおよびNEXCO公式ページで行ってください。',
        },
      ],
      examples: [
        {
          who: 'ゴールデンウィーク帰省予定の家族',
          use: 'NEXCO公式の除外日カレンダーを確認し、渋滞期間中は休日割引が適用されないことを前提に高速料金を予算化。',
        },
        {
          who: 'お盆期間に帰省する会社員',
          use: '出発日がお盆除外期間に含まれるかNEXCO年間告知で確認し、割引が適用される日時を選んで出発計画を調整。',
        },
        {
          who: '経理担当者',
          use: '社員が週末通行した高速料金に休日割引が適用されていない理由を、NEXCO公式除外日カレンダーと照合して確認。',
        },
        {
          who: '物流事業者の配送計画担当',
          use: '三連休期間が除外日に該当するかを確認し、休日割引を前提としたコスト試算を行わないよう社内で周知。',
        },
      ],
      faqs: [
        {
          q: 'JTRは休日割引の可否を判定しますか?',
          a: 'いいえ。JTRは独立した記録整理サービスであり、割引判定や公式データの作成は行いません。公式のETC利用照会サービスおよび道路事業者の規定で確認してください。',
        },
        {
          q: '除外日のルールは毎年変わりますか?',
          a: 'はい。除外日の日程や対象期間は年度ごとに変わります。過去年度の資料やSNS画像を使わず、通常は最新年度のNEXCO公式カレンダーを確認してください。',
        },
        {
          q: 'PDF・CSV両方の記録を保管すべきですか?',
          a: '推奨します。PDFは文書形式での確認に、CSVは並べ替えや経理照合作業に便利です。両方を月次で整理・保管することで後日検証がしやすくなります。',
        },
        {
          q: '割引が適用されない場合は通常はエラーですか?',
          a: 'いいえ。車両区分・除外路線・除外日・通行時間・カード種別・登録状態など様々な条件により割引対象外となる場合があります。公式ルールと実際の記録を照合してください。',
        },
        {
          q: 'JTRはこのテーマでどう役立ちますか?',
          a: 'JTRはETC記録を定期的に整理・配信することで、人による確認作業を支援します。割引ルールや最終請求額の判定は公式サービス・事業者規定に従ってください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'JTRの仕組み',
          desc: 'ETC記録を定期配信・整理するJTRの独立サービス構造と、公式データ源との関係を解説します。',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数車両・カードのETC記録を月次で整理し、経理担当者の確認作業を支援する法人向けサービス。',
          path: '/business',
        },
        {
          title: '無料トライアル',
          desc: 'JTRの記録配信・整理機能を実際に試し、休日割引除外日などの確認作業にどう役立つかを体験できます。',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'NEXCO東日本 休日割引',
          url: 'https://www.driveplaza.com/etc/dis/etc_dis_weekend/',
          note: '公式の割引条件、除外区間、対象車種を掲載。',
        },
        {
          label: '2026年度 休日割引適用に関するお知らせ',
          url: 'https://www.e-nexco.co.jp/news/important_info/2026/0317/00016041.html',
          note: '2026年度の休日割引適用期間と除外日の公式発表。',
        },
        {
          label: '2026年度 休日割引カレンダーPDF',
          url: 'https://www.e-nexco.co.jp/news/cms_assets/news/2026/03/17/01.pdf',
          note: '適用日・除外日が記載された公式年間カレンダー。',
        },
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: '通行後の実際の請求額を確認できる公式サービス。',
        },
        {
          label: 'NEXCO東日本 割引概要',
          url: 'https://www.driveplaza.com/etc/dis/',
          note: '主要なETC割引制度の公式まとめページ。',
        },
      ],
      longBody: 'ETC休日割引は土曜・日曜・祝日に地方部の高速道路を走行すると普通車・軽自動車・二輪車が30%割引を受けられる制度ですが、実際には適用除外日が設定されており、週末や祝日でも割引が適用されないケースがあります。この除外日は交通集中が予想される期間として設定され、ゴールデンウィーク・お盆・年末年始・シルバーウィーク・三連休などが対象となります。NEXCO東日本・中日本・西日本やJB本四高速、宮城県道路公社などが毎年度ごとに適用除外日カレンダーを公表しており、同じ曜日配置でも年度によって除外日が変わるため、前年度の資料を使い回すと誤った判断につながります。企業の経理担当者が週末走行分の通行料金を確認した際、割引が適用されていないことに気づいて誤請求を疑うケースがありますが、まず確認すべきは公式カレンダー上の除外日に該当していないかという点です。休日割引は車種・道路区間・走行日時のすべてが条件を満たして初めて適用されるため、日付だけでなく首都圏近郊区間や大阪近郊区間などの対象外路線を走行していないか、車両区分が正しく登録されているかも確認が必要です。JTRは独立したETC利用記録の整理・配信サービスであり、NEXCO・MEISAI・ETC利用照会サービス・マイレージサービスとは別組織です。JTRは割引の適用可否を決定せず、公式な通行料金データを新たに生成することもありません。JTRが提供するのはPDFおよびCSV形式の記録を定期的にメール配信し、社内での確認作業を効率化する仕組みです。ドライバーが「休日割引が適用されるはず」と記憶していても、実際には除外日だった、対象外区間を含んでいた、車両クラスが不適合だったといった理由で割引が適用されないことは珍しくありません。企業が通行料精算規定を作成する際は、割引の有無を公式記録と運営者ルールに基づいて確認し、JTRレポートは整理・保管ツールとして位置づける表現が現実的です。月次でPDF証明書・CSV明細・運転者メモ・承認記録をセットで保管しておけば、後日の問い合わせや確認対応時にも事実確認がスムーズになります。割引適用の最終判断は料金所システムと運営者規定にあり、JTRはその結果を整理して人による確認をサポートする役割を担います。',
    },
    en: {
      title: 'ETC Holiday Discount Exclusion Days | Japan Toll Receipts',
      metaDesc: "How to check ETC holiday discount exclusion days (Golden Week, Obon, New Year). Understand when weekend discounts don't apply and where to verify official calendars.",
      h1: 'Check ETC Holiday Discount Exclusion Days',
      primaryKeyword: 'ETC holiday discount exclusion days',
      answer: "ETC holiday discounts apply on Saturdays, Sundays, and national holidays, but are excluded during peak traffic periods including Golden Week, Obon, New Year holidays, Silver Week, and certain three-day weekends. NEXCO companies publish official annual calendars showing exclusion dates. Checking the current year's calendar before travel prevents unexpected toll charges.",
      whyMatters: "Many drivers assume holiday discounts apply on all weekends, but Japanese expressways exclude major holiday periods to manage congestion. Relying on last year's calendar or social media posts can lead to incorrect exclusion dates. When accounting staff find weekend tolls without discounts, they must cross-check with official annual calendars. Logistics operators planning busy-season budgets need advance knowledge of exclusion dates for accurate cost forecasting.",
      officialBackground: "The formal record hierarchy for ETC usage is: road operator, ETC card, and ETC Inquiry Service. The inquiry service is where users check transaction details, print usage certificates, and download PDF or CSV files. Specific discount rules are managed by NEXCO companies, ETC Mileage Service, and ETC2.0 official pages. Because absolute-sounding statements about discounts or settlement can mislead, when users ask 'why wasn't I discounted,' safe practice is to verify date, time, vehicle class, card, route, road operator, registration status, and official announcements—not assumptions. JTR supports that verification work with organized daily records, but official discount determination rests with the toll system and operator rules.",
      whoFor: [
        'Travelers planning long-distance drives during holiday periods',
        'Logistics operators managing seasonal toll budgets',
        'Accounting staff investigating why weekend tolls were not discounted',
        'Foreign residents in Japan planning Golden Week or Obon travel',
      ],
      jtrSolution: {
        intro: 'Japan Toll Receipts (JTR) is an independent service that organizes and delivers ETC usage records. JTR is NOT NEXCO, ETC Usage Inquiry Service (MEISAI), ETC Inquiry Service, Mileage Service, or a road operator. JTR does not create official toll data, determine discount eligibility, or guarantee settlement approval. JTR delivers organized PDF and CSV records to reduce manual work and support human verification.',
        bullets: [
          'Receive ETC records by email periodically, without waiting until month-end',
          'Build a practical audit trail in your inbox to spot missed trips or unusual routes early',
          'Review organized records to understand facts before checking official services',
          'Store both PDF (for document review) and CSV (for sorting, reconciliation, accounting) formats',
          'Use as a record-organizing tool, not a replacement for discount rules or operator policies',
        ],
      },
      pdfCsv: 'JTR provides records in both PDF (certificate-style viewing) and CSV (sorting, reconciliation, accounting-software integration). PDF is human-readable; CSV suits bulk data processing. Keeping both formats makes later verification tasks smoother.',
      email: 'JTR delivers ETC records by email periodically, so you can review usage without waiting for month-end. Records accumulate in your inbox, enabling early detection of wrong-card usage, unregistered routes, or unexpected discount outcomes, and smoothing official-service checks and internal approval workflows.',
      steps: [
        {
          t: 'Check if your vehicle class qualifies for weekend discount',
          d: 'Only standard cars, light cars, and motorcycles are eligible. Medium and large commercial vehicles are excluded, so first confirm your vehicle type.',
        },
        {
          t: 'Verify the route is included in the discount program',
          d: "Certain sections, such as the Tokyo and Osaka metropolitan areas and the Okinawa Expressway, are excluded from weekend discounts. Check NEXCO's official page for excluded routes.",
        },
        {
          t: 'Confirm your travel date is a Saturday, Sunday, or national holiday',
          d: 'Weekend discounts apply to Saturdays, Sundays, and national holidays. Weekdays are not eligible.',
        },
        {
          t: 'Review the official annual exclusion-day calendar',
          d: "Peak congestion periods including Golden Week, Obon, year-end holidays, Silver Week, and three-day weekends are designated as exclusion days. Check NEXCO's annual calendar for each fiscal year.",
        },
        {
          t: 'After travel, verify actual charges via ETC Inquiry Service',
          d: 'After passing through, check the actual recorded charges on the ETC Inquiry Service. Tollbooth displays and in-vehicle unit voice announcements may not reflect final amounts.',
        },
        {
          t: 'Archive PDF and CSV records for future verification',
          d: 'For later review and accounting needs, organize and store PDF and CSV records obtained from the ETC Inquiry Service on a monthly basis.',
        },
      ],
      commonProblems: [
        {
          q: 'Weekend discount was not applied even though I traveled on Saturday or Sunday',
          a: "Golden Week, Obon, year-end holidays, Silver Week, and three-day weekends are designated as 'congestion countermeasure periods' and excluded. Check NEXCO's official annual calendar to see if your date falls within an exclusion period.",
        },
        {
          q: "I can't confirm if the discount was applied at the tollbooth",
          a: 'Tollbooth displays and ETC unit voice announcements are provisional and may not show final amounts. Verify actual charges through the ETC Inquiry Service or your credit card statement.',
        },
        {
          q: 'Same dates as last year but no discount this year',
          a: "Exclusion days change each fiscal year. Do not rely on last year's memory or saved images; typically check the latest NEXCO official calendar for the current year.",
        },
        {
          q: 'Can I use JTR records to determine discount eligibility?',
          a: "JTR is a record organization and delivery service, not a discount eligibility authority. Verify discount conditions and final charges through the ETC Inquiry Service and NEXCO's official pages.",
        },
      ],
      examples: [
        {
          who: 'Family planning Golden Week trip home',
          use: "Check NEXCO's official exclusion calendar and budget highway tolls assuming weekend discounts will not apply during peak congestion periods.",
        },
        {
          who: 'Office worker returning home during Obon',
          use: "Verify whether departure dates fall within Obon exclusion periods via NEXCO's annual notice, and adjust travel plans to days when discounts apply.",
        },
        {
          who: 'Accounting staff',
          use: "Cross-check NEXCO's official exclusion calendar to understand why weekend discounts were not applied to an employee's weekend highway charges.",
        },
        {
          who: 'Logistics company dispatch planner',
          use: 'Confirm whether three-day weekend periods are exclusion days and notify internally not to base cost estimates on weekend discount assumptions.',
        },
      ],
      faqs: [
        {
          q: 'Does JTR determine weekend discount eligibility?',
          a: 'No. JTR is an independent record organization service and does not perform discount determinations or create official data. Verify via the official ETC Inquiry Service and road operator regulations.',
        },
        {
          q: 'Do exclusion day rules change every year?',
          a: "Yes. Exclusion dates and target periods change each fiscal year. Do not use prior years' materials or social media images; typically check the latest NEXCO official calendar.",
        },
        {
          q: 'Should I keep both PDF and CSV records?',
          a: 'Recommended. PDFs are convenient for document-format review, while CSVs are useful for sorting and accounting reconciliation. Organizing and storing both monthly makes later verification easier.',
        },
        {
          q: "If a discount isn't applied, is it necessarily an error?",
          a: 'No. Various conditions—vehicle class, excluded routes, exclusion days, travel time, card type, registration status—can result in ineligibility. Cross-check official rules with actual records.',
        },
        {
          q: 'How does JTR help with this topic?',
          a: 'JTR supports human review tasks by organizing and periodically delivering ETC records. Determination of discount rules and final charges must follow official services and operator regulations.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How JTR Works',
          desc: "Learn about JTR's independent service structure for periodic ETC record delivery and organization, and its relationship with official data sources.",
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'Corporate service that organizes ETC records for multiple vehicles and cards monthly, supporting accounting staff review tasks.',
          path: '/business',
        },
        {
          title: 'Free Trial',
          desc: "Experience JTR's record delivery and organization features, and see how they assist with tasks like verifying weekend discount exclusion days.",
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'NEXCO East Weekend Discount',
          url: 'https://www.driveplaza.com/etc/dis/etc_dis_weekend/',
          note: 'Official discount conditions, excluded sections, and eligible vehicle types.',
        },
        {
          label: 'FY2026 Weekend Discount Application Notice',
          url: 'https://www.e-nexco.co.jp/news/important_info/2026/0317/00016041.html',
          note: 'Official announcement of FY2026 weekend discount application periods and exclusion days.',
        },
        {
          label: 'FY2026 Weekend Discount Calendar PDF',
          url: 'https://www.e-nexco.co.jp/news/cms_assets/news/2026/03/17/01.pdf',
          note: 'Official annual calendar showing applicable and excluded dates.',
        },
        {
          label: 'ETC Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service to verify actual charges after travel.',
        },
        {
          label: 'NEXCO East Discount Overview',
          url: 'https://www.driveplaza.com/etc/dis/',
          note: 'Official summary page of major ETC discount programs.',
        },
      ],
      longBody: "The ETC holiday discount provides a 30% reduction for ordinary passenger cars, light vehicles, and motorcycles traveling on regional expressways on Saturdays, Sundays, and public holidays. However, exclusion days are designated during which the discount does not apply even on weekends and holidays. These exclusion days are set for periods when heavy traffic is anticipated, including Golden Week, the Obon season, the year-end and New Year period, Silver Week, and three-day weekends. NEXCO East, Central, and West Japan, along with JB Honshu-Shikoku Expressway and the Miyagi Prefectural Road Public Corporation, publish exclusion day calendars each fiscal year. Because exclusion dates vary from year to year even when the day-of-week patterns are similar, relying on the previous year's calendar can lead to incorrect assumptions. Corporate accounting staff sometimes notice that the discount was not applied to weekend travel and suspect billing errors, but the first step is to verify whether the date falls on an official exclusion day according to the published calendar. The holiday discount is applied only when all conditions—vehicle class, road section, and travel date and time—are simultaneously met, so it is necessary to confirm not only the date but also whether the route included non-eligible sections such as the Tokyo or Osaka metropolitan vicinity zones, and whether the vehicle classification is correctly registered. JTR is an independent ETC usage record organization and delivery service, separate from NEXCO, ETC Usage Inquiry Service (MEISAI), the ETC usage inquiry service, and the mileage program. JTR does not determine discount eligibility and does not generate new official toll data. What JTR provides is a mechanism to deliver records in PDF and CSV format via regular email, streamlining in-house review workflows. It is not uncommon for drivers to remember that the holiday discount should have applied, only to find that the date was actually an exclusion day, the route included ineligible segments, or the vehicle class did not qualify. When companies draft toll reimbursement policies, it is practical to verify discount application against official records and operator rules, positioning JTR reports as an organization and archiving tool. By archiving monthly sets of PDF receipts, CSV statements, driver notes, and approval records together, fact-checking becomes smoother during later inquiries or audits. Final discount decisions rest with the tollgate systems and operator regulations; JTR organizes those outcomes and supports human verification.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '休日割引の適用判定フロー',
            intro: '土日祝でも除外日に該当すれば割引は適用されません。',
            nodes: [
              {
                title: '1. 通行日の確認',
                desc: '土曜・日曜・祝日のいずれかに該当するか確認',
              },
              {
                title: '2. 除外日の照合',
                desc: 'GW・お盆・年末年始・三連休など混雑期に該当しないか確認',
              },
              {
                title: '3. 公式カレンダー参照',
                desc: 'NEXCO各社が公開する年間除外日カレンダーと照合',
              },
              {
                title: '4. 割引適用の判定',
                desc: '除外日でなければ通常の休日割引（30%）が適用される',
              },
            ],
          },
          en: {
            title: 'Holiday discount eligibility flow',
            intro: 'Even on weekends/holidays, exclusion days prevent discounts.',
            nodes: [
              {
                title: '1. Check travel date',
                desc: 'Verify if Saturday, Sunday, or national holiday',
              },
              {
                title: '2. Match exclusion list',
                desc: 'Check against GW, Obon, New Year, long weekends',
              },
              {
                title: '3. Consult official calendar',
                desc: 'Cross-reference NEXCO annual exclusion calendar',
              },
              {
                title: '4. Discount determination',
                desc: 'If not excluded, standard 30% holiday discount applies',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '除外日の公式確認先',
            intro: '割引除外日は道路事業者が毎年正式に公表します。',
            sources: [
              {
                name: 'NEXCO東日本',
                role: '東北・関東・信越の除外日カレンダー',
                url: 'e-nexco.co.jp',
                scope: '管内高速道路',
              },
              {
                name: 'NEXCO中日本',
                role: '中部・北陸の除外日カレンダー',
                url: 'c-nexco.co.jp',
                scope: '管内高速道路',
              },
              {
                name: 'NEXCO西日本',
                role: '近畿・中国・四国・九州の除外日',
                url: 'w-nexco.co.jp',
                scope: '管内高速道路',
              },
              {
                name: '首都高速・阪神高速',
                role: '都市高速の休日割引制度',
                url: '各社公式サイト',
                scope: '都市高速道路',
              },
            ],
          },
          en: {
            title: 'Official exclusion-day sources',
            intro: 'Road operators publish annual exclusion calendars.',
            sources: [
              {
                name: 'NEXCO East',
                role: 'Tohoku/Kanto/Shinetsu exclusion calendar',
                url: 'e-nexco.co.jp',
                scope: 'Regional expressways',
              },
              {
                name: 'NEXCO Central',
                role: 'Chubu/Hokuriku exclusion calendar',
                url: 'c-nexco.co.jp',
                scope: 'Regional expressways',
              },
              {
                name: 'NEXCO West',
                role: 'Kansai/Chugoku/Shikoku/Kyushu exclusions',
                url: 'w-nexco.co.jp',
                scope: 'Regional expressways',
              },
              {
                name: 'Shuto/Hanshin',
                role: 'Metro expressway holiday schemes',
                url: 'Operator sites',
                scope: 'Urban expressways',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: '通常の休日 vs 除外日',
            intro: '同じ週末・祝日でも除外日は割引が適用されません。',
            columns: [
              '通常の休日',
              '除外日（混雑期）',
            ],
            rows: [
              {
                feature: '割引率',
                a: '30%',
                b: '0%',
                kindA: 'yes',
                kindB: 'no',
              },
              {
                feature: '対象日',
                a: '土日祝',
                b: 'GW・お盆・年末年始等',
                kindA: 'yes',
                kindB: 'neutral',
              },
              {
                feature: '事前確認',
                a: '曜日のみ',
                b: '年間カレンダー必須',
                kindA: 'neutral',
                kindB: 'yes',
              },
              {
                feature: '適用理由',
                a: '休日利用促進',
                b: '混雑緩和のため除外',
                kindA: 'yes',
                kindB: 'no',
              },
            ],
          },
          en: {
            title: 'Regular holiday vs exclusion day',
            intro: 'Same weekend/holiday, different discount treatment.',
            columns: [
              'Regular holiday',
              'Exclusion day (peak)',
            ],
            rows: [
              {
                feature: 'Discount rate',
                a: '30%',
                b: '0%',
                kindA: 'yes',
                kindB: 'no',
              },
              {
                feature: 'Dates',
                a: 'Sat/Sun/holidays',
                b: 'GW, Obon, New Year, etc.',
                kindA: 'yes',
                kindB: 'neutral',
              },
              {
                feature: 'Pre-check',
                a: 'Day of week',
                b: 'Annual calendar required',
                kindA: 'neutral',
                kindB: 'yes',
              },
              {
                feature: 'Policy reason',
                a: 'Encourage leisure travel',
                b: 'Congestion mitigation',
                kindA: 'yes',
                kindB: 'no',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '除外日を確認する5ステップ',
            intro: '旅行前に必ず最新カレンダーを確認しましょう。',
            items: [
              {
                text: '該当年度のNEXCO公式カレンダーを開く',
                hint: '前年のカレンダーや口コミ情報は年度により異なる場合があります',
              },
              {
                text: '通行予定日が除外日リストに含まれるか確認',
                hint: 'ゴールデンウィーク・お盆・年末年始・シルバーウィーク・三連休が主な除外期間です',
              },
              {
                text: '除外日の場合は割引なしで料金計画を立てる',
                hint: '通常料金で予算を組むことで想定外の出費を防げます',
              },
              {
                text: '通行後にETC利用照会サービスで実際の割引適用を確認',
                hint: 'カード登録状況や時間帯によっても割引結果が変わります',
              },
              {
                text: 'JTRで整理された記録を受信し、月次レポートに活用',
                hint: '定期配信される記録で除外日の影響を経理担当者と共有できます',
              },
            ],
          },
          en: {
            title: '5 steps to verify exclusion days',
            intro: 'Always consult the latest calendar before travel.',
            items: [
              {
                text: 'Open current-year NEXCO official calendar',
                hint: "Prior-year calendars or social posts may not match this year's dates",
              },
              {
                text: 'Check if your travel date is on the exclusion list',
                hint: 'GW, Obon, New Year, Silver Week, and long weekends are typical exclusions',
              },
              {
                text: 'Plan toll budget without discount if excluded',
                hint: 'Budgeting at full fare prevents unexpected expenses',
              },
              {
                text: 'Verify actual discount via ETC Usage Inquiry after travel',
                hint: 'Card registration and time of day also affect discount eligibility',
              },
              {
                text: 'Receive organized records via JTR for monthly reports',
                hint: 'Scheduled delivery helps accounting staff review exclusion-day impacts',
              },
            ],
          },
        },
      },
    ],
  },

  // 5) ETC専用入口で現金車が誤進入した場合
  {
    slug: 'etc-senyou-mis-entry-recovery',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: 'ETC専用料金所 誤進入 記録整理',
      en: 'ETC-only toll gate mis-entry record recovery',
    },
    ja: {
      title: 'ETC専用料金所誤進入時の記録整理ガイド | Japan Toll Receipts',
      metaDesc: 'ETC専用料金所への誤進入時の対応（ETCサポートレーンでの停止、係員指示への従い方）、その後のETC利用照会サービスでの通行記録確認・PDF/CSV取得、料金事後精算の有無、誤進入を防ぐ事前準備（ETC2.0・カード装着確認）、独立サービスJTRの位置づけ、よくある質問と安全注意点を網羅。',
      h1: 'ETC専用料金所誤進入時の記録整理ガイド',
      primaryKeyword: 'ETC専用料金所 誤進入 記録整理',
      answer: 'ETC専用料金所に誤って進入した場合、安全な停止位置で係員や案内に従い、後から利用記録を確認します。記録整理では、どのETCカードが課金され、どの車両・路線・IC区間が使われ、公式証拠がどこにあるかを明確にすることが重要です。公式データと社内説明を分けて保管し、後日の確認や精算を容易にします。',
      whyMatters: 'ETC専用化が進む中、誤進入や機器トラブル後の記録整理は企業・レンタカー利用者・外国人居住者にとって重要です。記録が不十分だと、数週間後の精算や経費確認が困難になります。公式ETC利用照会サービス、カード明細、道路事業者情報を組み合わせ、カード・車両・日時・IC・目的を一貫した形式で記録することで、経理部門・管理者・ドライバー間の混乱を減らし、不正利用の早期発見や適切な精算をサポートします。',
      officialBackground: '日本の高速道路では2026年春以降、NEXCO中日本35箇所、NEXCO西日本30箇所、NEXCO東日本でもETC専用料金所が順次導入されています。ETC利用照会サービス(www.etc-meisai.jp)では、通常ETCカードは過去15ヶ月分の利用履歴照会・利用証明書発行・PDF/CSVダウンロードが可能です。ETC総合情報ポータルでは対象カード種別・無線/非無線利用・法人カード記録期間・エラーコード一覧が案内されています。誤進入時は安全な位置で停止し係員指示に従うこと、バーが開かない場合は後退・車外へ出る・強行突破を避けることが公式に推奨されています。記録確認では道路事業者サイトで料金・割引・車種区分・路線ルールを、ETC利用照会サービスでカード利用明細を、カード発行会社で月次明細をそれぐれ確認します。',
      whoFor: [
        'ETCを持たずに誤進入したドライバー',
        'レンタカーでETC専用料金所を利用した旅行者',
        '複数車両・複数カードを管理する企業経理担当者',
        'ETC関連トラブル対応を行う支援スタッフ',
      ],
      jtrSolution: {
        intro: 'JTRは独立サービスとして、ETC利用記録の受信・整理・保管・確認業務を支援します。NEXCO・MEISAI・ETC利用照会サービス・カード発行会社・政府システムではありません。公式データを作成せず、記録を分かりやすく提示します。',
        bullets: [
          'PDF・CSV記録を保管し、後日の検索・比較を容易化',
          '設定に応じてETCカード・車両ごとに利用履歴をグループ化',
          '想定外の利用や確認が必要な記録を目立たせて早期レビューを支援',
          '社内ポリシーとの照合が必要な管理者向けに記録比較機能を提供',
          '税務・請求書・精算処理は最終的に会計士・雇用主・公式ガイダンスで確認するよう案内',
        ],
      },
      pdfCsv: 'JTRはPDF形式(閲覧・共有に便利)とCSV形式(並べ替え・フィルタ・会計システム取込に適する)で記録を提供します。スプレッドシートへの展開はユーザーがダウンロード後に実施できます。Excel/XLSX形式での直接出力はありません。',
      email: 'JTRは定期的にETC利用記録をメール配信し、確認漏れを防ぎます。配信スケジュール・対象カード・受信者はユーザーが設定します。メール内容は閲覧専用であり、公式な請求書・利用証明書・税務書類ではありません。',
      steps: [
        {
          t: '対象となる通行を特定する',
          d: 'ETC専用料金所に誤進入した場合、安全な位置に停車し、係員または案内に従ってください。バック走行・無理な通過・車外に出る行為は重大な危険を伴います。通行後に記録を確認します。',
        },
        {
          t: '公式の料金所運営者情報を確認する',
          d: '経路・料金額・車種区分・割引適用条件・ETC専用化状況など料金所固有のルールは、該当する道路運営者の公式サイトで最新情報を確認してください。',
        },
        {
          t: 'ETC利用照会サービスまたはカード明細を確認する',
          d: '通行後のETC利用明細・利用証明書・PDFまたはCSV形式の記録は、ETC利用照会サービスまたはカード発行会社の明細サービスで取得してください。',
        },
        {
          t: '通行日・IC・カード・車両・目的を記録する',
          d: '通行日、入口IC、出口IC、使用ETCカード、車両、車種区分、運転者または部署、通行目的を社内フォーマットで記録し、公式記録と社内説明を分けて保管してください。',
        },
        {
          t: '記録が不詳細または疑問がある場合は運営者に問い合わせる',
          d: '記録の遅延・金額の相違・経路の不一致・カード情報の疑問がある場合は、推測せず該当する道路運営者またはカード発行会社に問い合わせ、回答を記録と一緒に保管してください。',
        },
        {
          t: 'PDF・CSV記録と社内説明を統合保管する',
          d: '公式のPDF・CSV記録、カード明細、社内メモを一元管理することで、精算申請・確認対応・部署配分・疑義照会時の確認作業を効率化できます。',
        },
      ],
      commonProblems: [
        {
          q: 'ETC専用料金所に誤進入してしまった場合はどうすればよいですか?',
          a: '安全な位置に停車し、係員または案内表示に従ってください。バック走行・車外に出る・無理に通過する行為は重大な事故につながる危険があります。通行後に公式記録を確認し、必要に応じて運営者へ問い合わせてください。',
        },
        {
          q: '記録が見つからない、または金額が予想と異なる場合はどうすればよいですか?',
          a: '通行日・入口IC・出口IC・使用カード・車両情報を確認し、ETC利用照会サービスまたはカード明細で公式記録を照合してください。それでも解決しない場合は、該当する道路運営者またはカード発行会社へ問い合わせてください。',
        },
        {
          q: 'PDFとCSV、どちらの形式を保存すべきですか?',
          a: '両方の保存を推奨します。PDF形式は閲覧・共有に適し、CSV形式は並べ替え・フィルタ・会計システムへの取り込みに適しています。用途に応じて使い分けることで、記録管理と精算業務の両方に対応できます。',
        },
        {
          q: 'この記録を税務処理や精算申請に使用できますか?',
          a: '記録は精算や税務確認の補助資料として利用できますが、最終的な処理方法は雇用主の方針・税理士の助言・公式ガイダンスに従ってください。本ガイドは税務助言ではありません。',
        },
      ],
      examples: [
        {
          who: '営業車両の運転者',
          use: '誤進入後に公式記録を確認し、通行日・IC・カード・車両情報を社内フォーマットに記録して精算申請に添付することで、経理部門の確認作業を円滑にします。',
        },
        {
          who: '経理担当者',
          use: '月次でPDF・CSV記録をダウンロードし、社内の車両・部署配分リストと照合することで、通行記録と社内管理の整合性を確認し、疑義がある明細を早期に特定します。',
        },
        {
          who: 'レンタカー利用者',
          use: 'レンタル契約書・ETCカード明細・公式利用明細を一式で保管することで、返却後の精算確認や経費申請時の説明資料として利用できます。',
        },
        {
          who: '車両管理担当者',
          use: '入口IC・出口IC・日時・車種区分・カード番号を基に、不明な通行記録を公式照会サービスで確認し、運転者へ確認依頼を行う前に事実関係を整理します。',
        },
      ],
      faqs: [
        {
          q: 'JTRは公式の道路運営者ですか?',
          a: 'いいえ。JTRは独立したサービスです。公式の経路・料金・割引・設定・安全ルールは該当する道路運営者または公式ETCサービスで確認してください。JTRはETC利用記録の受信・整理・保管・確認を支援します。',
        },
        {
          q: 'ETC利用照会サービスは道路運営者のサイトを代替できますか?',
          a: 'いいえ。ETC利用照会サービスはETCカードの利用明細・利用証明書の確認に有用ですが、最新の経路・料金・割引・車線・車種区分ルールは各道路運営者の公式サイトで確認する必要があります。',
        },
        {
          q: 'PDFとCSV形式の記録、どちらを保存すべきですか?',
          a: '両方を推奨します。PDF形式は閲覧・共有に適し、CSV形式は並べ替え・フィルタ・会計システムへの取り込みに適しています。用途に応じて使い分けることで記録管理と精算業務の両方に対応できます。',
        },
        {
          q: 'この記録を税務処理や精算に使用できますか?',
          a: '精算・税務確認の補助資料として利用できますが、最終的な処理は雇用主の方針・税理士の助言・公式ガイダンスに従ってください。本ガイドは税務助言ではありません。',
        },
        {
          q: 'ETC専用料金所誤進入対応で最も注意すべき点は何ですか?',
          a: '記憶・スクリーンショット・不詳細な明細に頼らず、公式のETC利用記録と社内の業務説明を一緒に保管することです。記録が分散すると、後日の確認・精算・監査時に説明が困難になります。',
        },
      ],
      relatedFeatures: [
        {
          title: 'JTRの仕組み',
          desc: 'ETC利用記録の受信・整理・保管の流れと、PDF・CSV形式での記録管理方法を確認できます。',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数車両・複数カード・部署配分・精算承認フローに対応した記録管理機能を提供します。',
          path: '/business',
        },
        {
          title: 'セキュリティとプライバシー',
          desc: '通行記録・カード情報・車両情報の保護方針と、データ管理の透明性について説明します。',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用照会・利用証明書発行・PDF/CSV形式での明細ダウンロードを提供する公式サービス。通常のETCカード記録は過去15か月分が照会可能です。',
        },
        {
          label: 'ETC総合情報ポータル: ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別・利用証明書発行条件・通常ETCカード記録期間・法人ETCカード記録期間・無線/非無線利用・PDF/CSVダウンロードに関する説明。',
        },
        {
          label: 'ETC総合情報ポータル: ETCを始める四輪・二輪',
          url: 'https://www.go-etc.jp/support',
          note: 'カード挿入時の注意・車線利用ルール・エラーコード・二輪車ETC情報を含む公式サポートページ。',
        },
        {
          label: 'ETC総合情報ポータル: エラーコード一覧',
          url: 'https://www.go-etc.jp/support/code',
          note: 'ETC車載器のエラーコード・カード挿入エラー・読み取りエラーの説明と、開閉バーが開かない場合は料金所で停車し係員の指示に従う旨の案内。',
        },
        {
          label: 'NEXCO中日本 2026年ETC専用化',
          url: 'https://www.c-nexco.co.jp/corporate/pressroom/news_release/6532.html',
          note: '2026年春より35か所の料金所がETC専用化されることを発表したNEXCO中日本の公式リリース。',
        },
      ],
      longBody: '2026年春以降、NEXCO各社は全国で合計100か所以上の料金所をETC専用化する方針を発表しました。NEXCO中日本は35か所、NEXCO西日本は30か所、NEXCO東日本も追加の料金所を対象としており、この変更により一般レーンが廃止される料金所が増加します。ETC専用料金所では、ETC車載器が正常に動作している車両のみが通過でき、車載器未搭載車や車載器エラー発生時には通行できません。誤って進入した場合、運転者は指定された安全な場所に停車し、係員の指示に従う必要があります。バックや車外への飛び出し、バーの強行突破は重大な安全リスクを生むため絶対に避けるべきです。事後の記録確認と適切な処理が求められます。\n\nETC専用料金所での誤進入後、通行記録を整理する際には複数の公式情報源を正しく理解することが重要です。道路事業者の公式サイトは路線情報、料金、割引、車種区分、ETC専用化状況、安全指示を提供します。ETC利用照会サービスはETCカードの利用明細、利用証明書、PDF・CSV形式の記録を提供し、通常のETCカードは過去15か月分、ETCコーポレートカードは過去62か月分の記録が照会可能です。カード発行会社は月次利用明細を提供する場合があります。JTRはこれら公式記録の受信、整理、保管、照合を支援する独立したサービスであり、公式な通行記録を作成したり、事業者・カード会社・政府機関に代わるものではありません。\n\n実務上、通行記録には「どのカードが課金されたか」「どの車両が利用したか」「どの区間またはIC組み合わせか」「なぜその移動が発生したか」「公式証跡がどこに保管されているか」の5点を記録しておくと、後日の確認や精算が容易になります。企業、官公庁、レンタカー、家族利用では、公式利用明細・PDF出力・CSV出力・カード明細・社内メモを一体で保管することで、経費精算・運転者確認・部署配分・不適切利用の可能性検討時の混乱を減らせます。記録が不詳細、遅延、予想外、または不一致の疑いがある場合は、推測せず該当する事業者またはカード発行会社に問い合わせ、その回答を記録と共に保管してください。\n\nJTRは利用記録を実務的な照合ワークフローに変える役割を担います。手動での検索、印刷、ファイル名変更、転送作業を削減し、配信・整理・アーカイブ・照合支援に注力します。JTRはPDF・CSV記録の保管、ETCカードまたは車両ごとのグループ化、要確認記録の強調表示、内規との比較を支援しますが、不正を断定したり法的判断を下すものではありません。税務、請求書、精算処理については、記録整理を支援しますが、最終的な取扱いは会計士、雇用主、所属機関の方針、公式ガイダンスで確認してください。',
    },
    en: {
      title: 'ETC-Only Toll Gate Mis-Entry Record Recovery | Japan Toll Receipts',
      metaDesc: 'How to check, organize, and store toll records after mistakenly entering an ETC-only gate. Official inquiry services, PDF/CSV logs, JTR tools, FAQ & safety tips.',
      h1: 'ETC-Only Toll Gate Mis-Entry Record Recovery',
      primaryKeyword: 'ETC-only toll gate mis-entry record recovery',
      answer: 'If you mistakenly enter an ETC-only toll gate, stop safely and follow staff or signage instructions, then verify your usage record afterward. Effective record recovery means clarifying which ETC card was charged, which vehicle, route, and IC span were used, and where official evidence resides. Keep official data separate from internal notes to simplify later review and settlement.',
      whyMatters: 'As Japan expands ETC-only gates, post-mis-entry and equipment-failure record recovery is critical for companies, rental-car users, and foreign residents. Incomplete records make settlement and expense review difficult weeks later. Combining the official ETC Usage Inquiry Service, card statements, and road-operator data into a consistent format—card, vehicle, date-time, IC, purpose—reduces confusion among accounting, managers, and drivers, supports early usage pattern review, and enables accurate settlement.',
      officialBackground: 'From spring 2026 onward, NEXCO Central introduces 35 ETC-only gates, NEXCO West 30, and NEXCO East follows suit. The ETC Usage Inquiry Service (www.etc-meisai.jp) offers 15 months of history, usage certificates, and PDF/CSV downloads for standard ETC cards. The ETC Comprehensive Portal lists eligible card types, wireless/non-wireless usage, corporate-card retention periods, and error codes. Official guidance: stop safely at mis-entry, follow staff instructions, avoid reversing, exiting the vehicle, or forcing through closed barriers. For record verification, check road-operator sites for tolls, discounts, vehicle class, and route rules; use the ETC inquiry service for card transaction details; and review monthly statements from your card issuer.',
      whoFor: [
        'Drivers who entered an ETC-only gate without an ETC card',
        'Travelers using rental cars at ETC-only toll plazas',
        'Corporate accounting staff managing multiple vehicles and cards',
        'Support staff handling ETC-related incident follow-up',
      ],
      jtrSolution: {
        intro: 'JTR is an independent service supporting ETC record receipt, organization, storage, and review. JTR is NOT NEXCO, ETC Usage Inquiry Service (MEISAI), the ETC inquiry service, a card issuer, or a government system. JTR does not generate official data; it presents records clearly.',
        bullets: [
          'Store PDF and CSV logs for easy later search and comparison',
          'Group usage history by ETC card or vehicle according to your settings',
          'Highlight unexpected usage or records needing review to support early action',
          'Provide record comparison features for managers cross-checking internal policy',
          'Guide users to confirm tax, invoicing, and settlement with accountants, employers, and official resources',
        ],
      },
      pdfCsv: 'JTR provides records in PDF format (convenient for viewing and sharing) and CSV format (suitable for sorting, filtering, and importing into accounting systems). Users may open CSV files in spreadsheets after download. JTR does not export directly to Excel or XLSX format.',
      email: 'JTR sends periodic ETC usage record emails to prevent missed reviews. Users configure schedule, target cards, and recipients. Email content is view-only and does not constitute an official invoice, usage certificate, or tax document.',
      steps: [
        {
          t: 'Identify the relevant toll transaction',
          d: 'If you mistakenly enter an ETC-only toll gate, stop in a safe location and follow staff or signage instructions. Never reverse, force passage, or exit your vehicle—these actions pose serious safety risks. Confirm the record after passing through.',
        },
        {
          t: 'Check official toll plaza operator information',
          d: 'Route details, toll amounts, vehicle classifications, discount eligibility, and ETC-only conversion status are managed by each road operator. typically verify the latest rules and conditions on the official website of the relevant operator.',
        },
        {
          t: 'Check ETC Inquiry Service or card statement',
          d: "Obtain post-travel ETC usage details, usage certificates, and PDF or CSV records through the ETC Inquiry Service or your card issuer's statement service to confirm the official transaction record.",
        },
        {
          t: 'Record travel date, IC, card, vehicle, and purpose',
          d: 'Document travel date, entry IC, exit IC, ETC card used, vehicle, vehicle class, driver or department, and travel purpose in your internal format. Keep official records and internal explanations separately for clarity.',
        },
        {
          t: 'Contact the operator if records are incomplete or unclear',
          d: 'If you encounter delayed records, discrepancies in amounts, route mismatches, or card information questions, do not guess. Contact the relevant road operator or card issuer, and store their response with your records.',
        },
        {
          t: 'Store PDF/CSV records and internal notes together',
          d: 'Centralize official PDF/CSV records, card statements, and internal memos to streamline reimbursement requests, audit responses, departmental allocation, and dispute resolution workflows.',
        },
      ],
      commonProblems: [
        {
          q: 'What should I do if I mistakenly enter an ETC-only toll gate?',
          a: 'Stop in a safe location and follow staff or signage instructions. Do not reverse, exit the vehicle, or force passage—these actions risk serious accidents. After passing, verify the official record and contact the operator if needed.',
        },
        {
          q: 'What if I cannot find a record or the amount differs from expected?',
          a: 'Confirm travel date, entry IC, exit IC, card used, and vehicle information. Cross-check official records via the ETC Inquiry Service or card statement. If unresolved, contact the relevant road operator or card issuer.',
        },
        {
          q: 'Should I save PDF or CSV format records?',
          a: 'Save both. PDF is ideal for viewing and sharing; CSV supports sorting, filtering, and import into accounting systems. Using both formats covers record management and reimbursement workflows effectively.',
        },
        {
          q: 'Can I use these records for tax filing or reimbursement requests?',
          a: "Records may serve as supporting material for reimbursement or tax verification, but final handling should follow your employer's policies, accountant's advice, and official guidance. This guide is not tax advice.",
        },
      ],
      examples: [
        {
          who: 'Sales vehicle driver',
          use: 'After mistaken entry, verify the official record and document travel date, IC, card, and vehicle in the internal format. Attach to reimbursement request to streamline accounting review.',
        },
        {
          who: 'Accounting staff',
          use: 'Download monthly PDF/CSV records and cross-check with internal vehicle and department allocation lists to verify consistency and identify questionable entries early.',
        },
        {
          who: 'Rental car user',
          use: 'Store rental agreement, ETC card statement, and official usage details together as reference material for post-return settlement confirmation and expense claims.',
        },
        {
          who: 'Vehicle manager',
          use: 'Use entry IC, exit IC, date/time, vehicle class, and card number to verify unclear travel records via the official inquiry service before requesting driver confirmation.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR an official road operator?',
          a: 'No. JTR is an independent service. For official routes, tolls, discounts, settings, and safety rules, check the relevant road operator or official ETC service. JTR assists with receiving, organizing, storing, and verifying ETC usage records.',
        },
        {
          q: 'Can the ETC Inquiry Service replace road operator websites?',
          a: "No. The ETC Inquiry Service is valuable for viewing ETC card usage details and certificates, but the latest route, toll, discount, lane, and vehicle class rules must be verified on each road operator's official site.",
        },
        {
          q: 'Should I save PDF or CSV format records?',
          a: 'Save both. PDF is ideal for viewing and sharing; CSV supports sorting, filtering, and import into accounting systems. Using both formats covers record management and reimbursement workflows effectively.',
        },
        {
          q: 'Can I use these records for tax filing or reimbursement?',
          a: "Records may serve as supporting material for reimbursement or tax verification, but final handling should follow your employer's policies, accountant's advice, and official guidance. This guide is not tax advice.",
        },
        {
          q: 'What is most important when handling ETC-only toll gate mistaken entry?',
          a: 'Do not rely on memory, screenshots, or incomplete statements. Store official ETC usage records together with internal business explanations. Scattered records make later verification, reimbursement, and audit responses difficult.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How JTR Works',
          desc: 'Learn the flow of receiving, organizing, and storing ETC usage records, and how to manage records in PDF and CSV formats.',
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'Record management features supporting multiple vehicles, multiple cards, departmental allocation, and reimbursement approval workflows.',
          path: '/business',
        },
        {
          title: 'Security & Privacy',
          desc: 'Our policies for protecting travel records, card information, and vehicle data, and transparency in data management.',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC Usage Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service providing ETC card usage inquiry, usage certificate issuance, and statement download in PDF/CSV formats. Standard ETC card records are available for the past 15 months.',
        },
        {
          label: 'ETC General Information Portal: ETC Usage Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explanation of eligible card types, usage certificate issuance conditions, standard ETC card record period, corporate ETC card record period, wireless/non-wireless usage, and PDF/CSV download.',
        },
        {
          label: 'ETC General Information Portal: Getting Started with ETC (Four-Wheel/Two-Wheel)',
          url: 'https://www.go-etc.jp/support',
          note: 'Official support page including card insertion precautions, lane usage rules, error codes, and two-wheel ETC information.',
        },
        {
          label: 'ETC General Information Portal: Error Code List',
          url: 'https://www.go-etc.jp/support/code',
          note: 'Explanation of ETC onboard unit error codes, card insertion errors, read errors, and instructions to stop at the toll booth and follow staff instructions if the barrier does not open.',
        },
        {
          label: 'NEXCO Central Japan 2026 ETC-Only Conversion',
          url: 'https://www.c-nexco.co.jp/corporate/pressroom/news_release/6532.html',
          note: 'Official NEXCO Central Japan release announcing that 35 toll plazas will convert to ETC-only operation starting spring 2026.',
        },
      ],
      longBody: "From spring 2026, the three NEXCO companies will convert more than 100 toll plazas nationwide to ETC-only operation. NEXCO Central Japan will convert 35 plazas, NEXCO West Japan 30, and NEXCO East Japan additional sites, eliminating staffed general lanes at these locations. ETC-only plazas permit passage solely to vehicles with functioning ETC on-board units; vehicles without units or experiencing unit errors cannot proceed. Drivers who enter by mistake must stop at designated safe areas and follow staff instructions. Reversing, exiting the vehicle, or forcing through the barrier create serious safety hazards and must never be attempted. Proper post-entry record confirmation and handling are required.\n\nWhen organizing travel records after mistaken entry at an ETC-only plaza, understanding multiple official information sources correctly is important. Road operators' official websites provide route information, tolls, discounts, vehicle class definitions, ETC-only conversion status, and safety instructions. The ETC Utilization Inquiry Service provides usage statements, proof-of-use documents, and records in PDF and CSV formats for ETC cards—typically 15 months for standard ETC cards and 62 months for ETC Corporate Cards. Card issuers may provide monthly billing statements. JTR is an independent service that assists with receiving, organizing, storing, and reconciling these official records; it does not create official travel records and does not replace operators, card issuers, or government agencies.\n\nIn practice, recording five elements for each trip—which card was billed, which vehicle was used, which section or IC pair, why the trip occurred, and where official evidence is stored—simplifies later review and settlement. For corporate, government, rental, and family use, keeping official usage statements, PDF outputs, CSV exports, card statements, and internal notes together reduces confusion during expense reconciliation, driver confirmation, departmental allocation, and review of potential misuse. When records appear incomplete, delayed, unexpected, or inconsistent, do not speculate; contact the relevant operator or card issuer, and retain their response with your records.\n\nJTR transforms usage records into practical reconciliation workflows. It reduces manual search, print, rename, and forward tasks, focusing on delivery, organization, archiving, and reconciliation support. JTR assists with storing PDF and CSV records, grouping by ETC card or vehicle, highlighting records requiring review, and comparing against internal policies, but it does not determine misuse or render legal conclusions. For tax, invoicing, and settlement processing, JTR assists with record organization, but final handling should be confirmed with your accountant, employer, institution policies, and official guidance.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: 'ETC専用料金所誤進入後の記録整理フロー',
            intro: '誤進入から記録確認・整理・保管までの4ステップ',
            nodes: [
              {
                title: '①安全停止・係員対応',
                desc: 'バックせず、車外に出ず、係員指示に従う',
              },
              {
                title: '②公式照会サービス確認',
                desc: 'ETC利用照会で15ヶ月分履歴・PDF取得',
              },
              {
                title: '③記録整理・分類',
                desc: 'カード・車両・IC区間・日時を紐付け',
              },
              {
                title: '④保管・共有',
                desc: 'PDF/CSV保存、社内精算・経理部門へ提出',
              },
            ],
          },
          en: {
            title: 'Record recovery flow after wrong-lane entry',
            intro: '4 steps from entry incident to organized records',
            nodes: [
              {
                title: '①Safe stop & staff',
                desc: "Don't reverse, stay inside, follow instructions",
              },
              {
                title: '②Official lookup',
                desc: 'ETC inquiry service: 15-month history + PDF',
              },
              {
                title: '③Organize records',
                desc: 'Link card, vehicle, IC section, date/time',
              },
              {
                title: '④Store & share',
                desc: 'Save PDF/CSV, submit to finance/accounting',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '公式情報源マップ',
            intro: '記録確認に必要な4つの公式サービス',
            sources: [
              {
                name: 'ETC利用照会サービス',
                role: 'カード別利用履歴・証明書',
                url: 'www.etc-meisai.jp',
                scope: '過去15ヶ月分・PDF/CSV',
              },
              {
                name: '道路事業者サイト',
                role: '料金表・割引・車種区分',
                url: 'NEXCO/首都高/阪神高速',
                scope: '路線別ルール・IC情報',
              },
              {
                name: 'カード発行会社',
                role: '月次明細・課金確定日',
                url: 'クレカ会社サイト',
                scope: '引落額・ポイント付与',
              },
              {
                name: 'ETC総合情報ポータル',
                role: '対応カード・エラーコード',
                url: '公式案内ページ',
                scope: '法人カード記録期間・非無線利用',
              },
            ],
          },
          en: {
            title: 'Official source map',
            intro: '4 key services for record verification',
            sources: [
              {
                name: 'ETC Inquiry Service',
                role: 'Card usage history & certificates',
                url: 'www.etc-meisai.jp',
                scope: '15 months · PDF/CSV',
              },
              {
                name: 'Road operator sites',
                role: 'Toll tables · discounts · classes',
                url: 'NEXCO/Shutoko/Hanshin',
                scope: 'Route rules · IC data',
              },
              {
                name: 'Card issuer',
                role: 'Monthly statement · billing',
                url: 'Credit card portal',
                scope: 'Debit amount · points',
              },
              {
                name: 'ETC info portal',
                role: 'Supported cards · error codes',
                url: 'Official guidance',
                scope: 'Corporate card periods · manual use',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: '公式データ vs JTR記録整理の役割比較',
            intro: 'どちらも必要—公式は証拠、JTRは整理・可視化',
            columns: [
              '公式サービス',
              'JTR',
            ],
            rows: [
              {
                feature: '利用履歴・証明書発行',
                a: 'ETC利用照会で発行',
                b: '発行せず、受信・保管のみ',
                kindA: 'yes',
                kindB: 'no',
              },
              {
                feature: 'PDF/CSVダウンロード',
                a: '公式サイトで取得',
                b: '取得済みファイルを整理',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '複数カード一括表示',
                a: 'カードごと個別照会',
                b: '設定に応じグループ化',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '想定外利用の強調',
                a: '全明細を平等表示',
                b: 'ポリシー外記録を目立たせ',
                kindA: 'neutral',
                kindB: 'yes',
              },
              {
                feature: '税務・請求書作成',
                a: '利用証明書を提供',
                b: '非対応—会計士へ確認',
                kindA: 'yes',
                kindB: 'no',
              },
            ],
          },
          en: {
            title: 'Official data vs. JTR organization role',
            intro: 'Both needed—official = proof, JTR = clarity',
            columns: [
              'Official services',
              'JTR',
            ],
            rows: [
              {
                feature: 'Usage history certificates',
                a: 'Issued by ETC inquiry',
                b: 'Not issued—store only',
                kindA: 'yes',
                kindB: 'no',
              },
              {
                feature: 'PDF/CSV download',
                a: 'From official site',
                b: 'Organize downloaded files',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Multi-card unified view',
                a: 'Separate per card',
                b: 'Grouped by config',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Flag unexpected use',
                a: 'All entries equal',
                b: 'Highlight policy outliers',
                kindA: 'neutral',
                kindB: 'yes',
              },
              {
                feature: 'Tax/invoice creation',
                a: 'Provide certificates',
                b: 'Not supported—ask CPA',
                kindA: 'yes',
                kindB: 'no',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '誤進入時の記録整理チェックリスト',
            intro: '8項目で記録漏れ・混乱を防止',
            items: [
              {
                text: '課金されたETCカード番号(下4桁)を確認',
                hint: '複数カード使用時は全て記録',
              },
              {
                text: '利用車両(ナンバー・レンタカー管理番号)',
                hint: '社用車・レンタカー区別',
              },
              {
                text: '進入・退出IC名称・日時',
                hint: 'ETC利用照会で正確な時刻確認',
              },
              {
                text: '料金・割引適用の有無',
                hint: '深夜割引・平日朝夕割引など',
              },
              {
                text: '公式PDF/CSV取得・保存',
                hint: '15ヶ月以内にダウンロード',
              },
              {
                text: 'カード明細との照合',
                hint: '引落日・金額一致を確認',
              },
              {
                text: '社内ポリシー適合チェック',
                hint: '私用利用・路線制限違反の有無',
              },
              {
                text: '経理部門・管理者への提出完了',
                hint: 'PDF+説明メモをセット',
              },
            ],
          },
          en: {
            title: 'Wrong-lane entry record checklist',
            intro: '8 items to prevent gaps & confusion',
            items: [
              {
                text: 'Confirm charged ETC card (last 4 digits)',
                hint: 'Record all if multiple cards used',
              },
              {
                text: 'Vehicle used (plate or rental ID)',
                hint: 'Distinguish company vs. rental',
              },
              {
                text: 'Entry/exit IC names & timestamps',
                hint: 'Verify exact time via ETC inquiry',
              },
              {
                text: 'Toll amount & discount applied',
                hint: 'Night, weekday commuter, etc.',
              },
              {
                text: 'Download & save official PDF/CSV',
                hint: 'Within 15-month window',
              },
              {
                text: 'Cross-check card statement',
                hint: 'Match debit date & amount',
              },
              {
                text: 'Internal policy compliance check',
                hint: 'Personal use, route restrictions',
              },
              {
                text: 'Submit to finance/manager',
                hint: 'PDF + explanatory note',
              },
            ],
          },
        },
      },
    ],
  },

  // 6) ETCカード番号と車載器番号の違い
  {
    slug: 'etc-card-vs-obu-number-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: 'ETCカード番号 車載器管理番号',
      en: 'ETC card number OBU management number',
    },
    ja: {
      title: 'ETCカード番号と車載器管理番号の違いと確認方法 | Japan Toll Receipts',
      metaDesc: 'ETCカード番号（カード券面の16桁）とETC車載器管理番号（19桁）の違い、それぞれの確認方法と用途を解説。ETC利用照会サービス登録時に必要な情報の整理、車載器交換・カード切替時の注意点、PDF利用証明書・CSV明細の保管手順、独立サービスJTRの位置づけ、よくある質問まで実務ベースで網羅した解説ガイド。',
      h1: 'ETCカード番号と車載器管理番号の違いと確認方法',
      primaryKeyword: 'ETCカード番号 車載器管理番号',
      answer: 'ETCカード番号は支払いに使うカードを識別し、車載器管理番号(19桁)は設置されたETC車載器を識別します。高速道路料金システムは、支払い・車両登録・無線通信処理を連携させるため両方の番号を必要とします。利用記録を正しく管理するには、どのカードで課金され、どの車両で通行したか、どの公式照会元を確認すべきかを明確にすることが重要です。',
      whyMatters: 'ETC利用記録を適切に管理するには、カード番号と車載器管理番号の役割を理解し、公式の利用明細・セットアップ証明書・車両情報を整理しておく必要があります。記録が不詳細だと、数週間後や数カ月後に「いつ」「どのカード」「どの車両」「どの経路」「なぜ通行したか」を説明することが困難になります。法人・官公庁・レンタカー・家族利用では、公式利用明細・PDF・CSV出力・カード明細・社内メモを一元管理することで、経費精算・ドライバー質問・部署配賦・不正利用確認の際の混乱を減らせます。',
      officialBackground: '日本の高速道路ETC利用記録管理は複数の独立システムで構成されます。道路事業者は路線・料金・割引・車両区分・安全情報を公開。ETC利用照会サービス(https://www.etc-meisai.jp/)は公式のETCカード利用明細と利用証明書を提供(通常15カ月分)。カード会社は月次明細を発行。車載器管理番号(19桁)はセットアップ・ETCマイレージ登録・障害者割引登録・一部割引適用に使われます(https://www.go-etc.jp/support/number_search)。セットアップ証明書は車両・車載器・登録情報を結びつけ、車両や機器の変更時は公式ガイダンス確認と証明書保管が推奨されます(https://www.go-etc.jp/support/setup)。JTRは独立サービスであり、公式料金データを作成せず、ユーザーが受信・整理・保管・確認できるよう支援します。',
      whoFor: [
        '初めてETCを使う個人ドライバー(カード番号と車載器番号の違いを理解したい方)',
        '経費精算や部署配賦のため利用記録を整理する経理・総務担当者',
        '複数車両・複数カードを管理し記録を分離・確認する必要がある法人管理者',
        'レンタカー利用者や家族共用車で記録を明確に保管したいユーザー',
      ],
      jtrSolution: {
        intro: 'JTRは公式ETC利用記録を実務的な確認フローに変換します。手動検索・印刷・ファイル名変更・転送作業を減らし、受信・整理・保管・確認支援に注力。JTRは独立サービスであり、NEXCO・MEISAI・ETC利用照会サービス・カード会社・政府システム・道路事業者ではなく、公式料金データを作成しません。',
        bullets: [
          'PDF・CSV形式で利用記録を保管、カード別・車両別にグループ化(設定時)',
          '確認が必要な記録を強調表示し、早期レビューを支援',
          '社内ポリシーと照合が必要な管理者向けに比較機能を提供',
          '通常と異なる利用パターンがあれば不正利用の可能性として注意喚起(法的判断は行わない)',
          '税務・請求書・経費精算では記録整理を支援、最終判断は会計士・雇用主・公式ガイダンスで確認を推奨',
        ],
      },
      pdfCsv: 'PDF記録は確認・共有に便利で、CSV記録は並べ替え・フィルタ・会計システムへの取込に適しています。両方を保管することで柔軟な運用が可能です。JTRはPDF・CSV形式で出力提供します(Excel・XLSX形式ではありません)。',
      email: '定期メール配信を設定すると、PDF・CSV記録が指定アドレスに届き、手動ダウンロードの手間を省けます。法人・官公庁では経理担当者や管理者へ直接配信することで記録管理フローを効率化できます。',
      steps: [
        {
          t: 'セットアップ証明書と車載器情報の確認',
          d: 'セットアップ証明書、車載器管理番号(19桁)、車検証、現在使用中のETCカード情報を手元に集め、車両と車載器の対応関係を確認します。',
        },
        {
          t: '車載器管理番号と車両情報の照合',
          d: '車載器管理番号と車両のナンバープレート、車種区分、所有者情報が公式セットアップ記録と一致しているか確認します。',
        },
        {
          t: '登録情報の変更有無を点検',
          d: '初回セットアップ以降に車両、ナンバー、所有者、車載器本体、けん引装置などに変更があった場合は再セットアップが必要です。',
        },
        {
          t: '不一致時は認定店または公式案内を確認',
          d: 'セットアップ記録が古い、情報が不一致、または記録が不詳細な場合は、認定セットアップ店または公式案内で対応方法を確認します。',
        },
        {
          t: '修正後にテスト走行で動作確認',
          d: '修正または再セットアップ後、実際にETC走行を行い、想定したETCカードと車両情報で利用履歴が記録されるか確認します。',
        },
        {
          t: '記録と車両対応表をまとめて保管',
          d: 'セットアップ証明書、PDF+CSV形式の利用履歴、社内の車両割当表や備考を一元管理し、将来の確認や問合せに備えます。',
        },
      ],
      commonProblems: [
        {
          q: 'ETCカード番号と車載器管理番号のどちらを登録すればよいか分かりません',
          a: 'ETCカード番号は決済用カードを識別し、車載器管理番号(19桁)は車両に取り付けた機器を識別します。登録手続きでは両方が必要な場合が多いため、公式案内で対象項目を確認してください。',
        },
        {
          q: '利用履歴にカード番号は表示されますが、どの車両で使ったか分かりません',
          a: 'ETC利用照会サービスではカード別の利用履歴が確認できますが、車両情報は通常マスクされます。社内で車両とカードの対応表を作成し、PDF+CSV記録と合わせて管理すると確認しやすくなります。',
        },
        {
          q: '車載器を別の車両に付け替えた場合、再セットアップは必要ですか',
          a: 'はい。車載器管理番号は車両情報と紐づいているため、車両が変わった場合は認定セットアップ店で再セットアップを行い、新しいセットアップ証明書を受け取る必要があります。',
        },
        {
          q: 'レンタカーでETC利用した際、どの記録を保管すればよいですか',
          a: 'レンタル契約書、ETCカード明細、ETC利用照会サービスから取得したPDF+CSV記録を一緒に保管すると、後日の精算や経理確認がスムーズです。',
        },
      ],
      examples: [
        {
          who: '初めてETCを利用するドライバー',
          use: '「ETCカード番号vs車載器番号ガイド」で、請求書と利用履歴を照合する前にどの公式サービスを確認すべきか理解しました。',
        },
        {
          who: '企業の経理担当者',
          use: '月次でPDF+CSV記録を取得し、社内の車両台帳や部署配分と突合することで、経費精算の根拠資料を整理しています。',
        },
        {
          who: '複数台の車両を持つ家族',
          use: 'ETCカード別の利用履歴を参照し、個人用と業務用の走行を分けてから、税理士に情報を渡すようにしています。',
        },
        {
          who: '車両管理者',
          use: '見覚えのない利用履歴を発見した際、公式のETC利用記録で入口IC・出口IC・日時・車種を確認してからドライバーに確認しています。',
        },
      ],
      faqs: [
        {
          q: 'JTRは公式の高速道路情報源ですか?',
          a: 'いいえ。JTRは独立したサービスです。公式の料金、割引、セットアップ、安全ルールは各道路会社またはETC利用照会サービス等で確認してください。JTRは利用記録の受取・整理・保管・確認をサポートします。',
        },
        {
          q: 'ETC利用照会サービスは道路会社サイトの代わりになりますか?',
          a: 'いいえ。ETC利用照会サービスはETCカード利用履歴と利用証明書の発行に便利ですが、最新の料金・割引・車種区分・通行ルールは各道路会社の公式サイトで確認する必要があります。',
        },
        {
          q: 'PDFとCSV、どちらの記録を保存すべきですか?',
          a: '両方保存を推奨します。PDF形式は閲覧・共有に便利で、CSV形式は並べ替え・絞り込み・会計システムへの取込に適しています。',
        },
        {
          q: 'この記録を税務申告や経費精算に使えますか?',
          a: '記録は申告や精算の参考資料として役立ちますが、最終的な取扱いは勤務先の規定、税理士の助言、公式ガイダンスで確認してください。このガイドは税務助言ではありません。',
        },
        {
          q: '金額や経路が間違っているようですが、どうすればよいですか?',
          a: '公式記録を確認し、カード・車両・日時・IC情報を照合してください。それでも不明な場合は該当する道路会社またはカード発行会社に問い合わせてください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'JTRの仕組み',
          desc: 'ETC利用記録をどのように受取・整理・保管するか、JTRの独立した立場と記録管理フローを詳しく説明します。',
          path: '/how-it-works',
        },
        {
          title: 'ビジネス向けプラン',
          desc: '複数車両・複数カード・部署別配分が必要な企業や団体向けに、PDF+CSV記録の一元管理と確認機能を提供します。',
          path: '/business',
        },
        {
          title: 'セキュリティ方針',
          desc: 'ETCカード番号・車載器管理番号・車両情報など機密性の高いデータの取扱い基準と保護方法を公開しています。',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用履歴の確認、利用証明書発行、PDF・CSV形式のダウンロードが可能。通常のETCカードは過去15か月分の記録が参照できます。',
        },
        {
          label: 'ETC総合情報ポータル: ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別、利用証明書の発行条件、通常ETCカードとコーポレートカードの記録保存期間、無線・非無線利用、PDF・CSVダウンロード方法を説明しています。',
        },
        {
          label: 'ETC総合情報ポータル: 車載器管理番号確認方法',
          url: 'https://www.go-etc.jp/support/number_search',
          note: '19桁の車載器管理番号の意味、確認場所、セットアップ・ETCマイレージ・障害者割引登録などでの利用目的を解説しています。',
        },
        {
          label: 'ETC総合情報ポータル: セットアップ',
          url: 'https://www.go-etc.jp/support/setup',
          note: 'ETCおよびETC2.0のセットアップ・再セットアップ手続き、申込内容、セキュリティ規格の注意点を公式に案内しています。',
        },
        {
          label: 'ETCマイレージサービス',
          url: 'https://www.smile-etc.jp/',
          note: '対象のETC通行料金に応じてポイントが貯まり、そのポイントを通行料金に充当できる公式サービスです。',
        },
      ],
      longBody: '日本のETC利用記録を適切に管理するには、ETCカード番号と車載器管理番号の違いを理解し、それぞれがどの公式情報源とどのように結びついているかを把握することが重要です。ETCカード番号は料金を請求される支払カードを識別し、車載器管理番号は車両に取り付けられたETC機器本体を識別します。多くの登録手続きでは両方の情報が必要とされます。これは料金収受システムが、支払手段・車両情報・無線通行処理の三要素を区別しながら連携させる必要があるためです。実際の運用では、カード発行会社の利用明細書にはカード番号と請求額が記載されますが、どの車両で通行したか、どの料金所区間を利用したか、どの割引が適用されたかといった詳細情報は含まれないことがあります。一方、ETC利用照会サービスではカード番号を軸に過去15か月分(通常カードの場合)の通行明細を照会でき、入口IC・出口IC・通行日時・車両番号(下4桁)・車種区分・割引内容などが確認できます。しかし、この照会結果だけでは社内の車両台帳や運転者記録と照合するための十分な文脈が揃わない場合もあります。車載器管理番号は19桁の固有番号で、セットアップ証明書や車載器本体のラベルに記載されており、ETCマイレージサービスへの登録や障害者割引の申請、一部の期間限定割引の適用確認などで必要になります。車両の入替や車載器の故障交換、ナンバープレートの変更があった際には、再セットアップが必要となり、新しいセットアップ証明書が発行されます。この時点で車載器管理番号が変わることもあるため、古い証明書を破棄せず、変更履歴として保管しておくと後の問い合わせ対応がスムーズです。レンタカー利用時には、車両ごとに異なる車載器が搭載されており、自分のETCカードを挿入して利用しますが、利用照会サービスで確認できる車両番号(下4桁)がレンタカーのナンバーと一致しているか照合することで、どの車両での通行かを特定できます。法人や官公庁では複数の車両と複数のカードを組み合わせて運用するため、通行記録を車両台帳・運転日報・経費申請書と突合する際に、カード番号だけでなく車両番号や車載器管理番号も手がかりとして活用されます。JTRは独立したサービスであり、公式の料金所運営者でもカード発行会社でもETC利用照会サービスでもありません。JTRはETC利用記録の受信・整理・保管・確認作業を支援することで、利用者が公式データをより効率的に活用できるよう設計されています。PDF形式とCSV形式の両方で記録をエクスポートできるため、目視確認にはPDFを、社内システムへの取込や部署別集計にはCSVを使い分けることが可能です。記録が不自然に見える場合は、まず公式の利用照会サービスで元データを確認し、カード番号・車両番号・通行日時・IC情報を照合したうえで、必要に応じて道路会社やカード発行会社へ問い合わせることが推奨されます。税務処理や経費精算の最終判断は、勤務先の規程や税理士の助言、公式ガイダンスに従って行う必要があります。',
    },
    en: {
      title: 'ETC Card vs. OBU Number: How to Find & Use Each | Japan Toll Receipts',
      metaDesc: 'Learn the difference between ETC card numbers and on-board unit (OBU) management numbers, where to find them, and how to manage toll records with official services, PDFs, CSVs, and JTR.',
      h1: 'ETC Card Number vs. OBU Management Number: Differences & How to Find',
      primaryKeyword: 'ETC card number OBU management number',
      answer: "The ETC card number identifies the payment card, while the OBU management number (19 digits) identifies the installed on-board unit in your vehicle. Japan's expressway toll system requires both numbers to link payment, vehicle registration, and wireless communication processing. To manage toll records properly, you need to know which card was charged, which vehicle passed through, and which official inquiry source to check.",
      whyMatters: 'Managing ETC toll records requires understanding the role of card numbers and OBU management numbers, and keeping official transaction details, setup certificates, and vehicle information organized. Incomplete records make it difficult weeks or months later to explain when, which card, which vehicle, which route, and why you traveled. For corporate, government, rental-car, and family use, consolidating official statements, PDFs, CSVs, card billing, and internal notes reduces confusion during expense claims, driver questions, departmental allocation, and unauthorized-use checks.',
      officialBackground: "Japan's expressway ETC record management consists of multiple independent systems. Road operators publish route, fare, discount, vehicle class, and safety information. The ETC Inquiry Service (https://www.etc-meisai.jp/) provides official ETC card transaction details and usage certificates (typically 15 months). Card issuers provide monthly statements. The OBU management number (19 digits) is used for setup, ETC mileage registration, disability discounts, and certain toll discounts (https://www.go-etc.jp/support/number_search). Setup certificates link vehicle, unit, and registration data; when changing vehicles or equipment, consult official guidance and retain certificates (https://www.go-etc.jp/support/setup). JTR is an independent service; it does not create official toll data but helps users receive, organize, store, and review records.",
      whoFor: [
        'Individual drivers new to ETC who want to understand the difference between card and OBU numbers',
        'Accounting and admin staff organizing toll records for expense claims and departmental allocation',
        'Corporate vehicle managers handling multiple vehicles and cards who need to separate and verify records',
        'Rental-car users and families sharing vehicles who want clear, organized toll record storage',
      ],
      jtrSolution: {
        intro: 'JTR converts official ETC toll records into a practical review workflow. It reduces manual searches, printing, file renaming, and forwarding, focusing on receive-organize-store-review support. JTR is independent—not NEXCO, ETC Usage Inquiry Service (MEISAI), ETC Inquiry Service, card issuer, government system, or road operator—and does not create official toll data.',
        bullets: [
          'Store toll records in PDF and CSV formats, grouped by card and vehicle (when configured)',
          'Highlight records needing review to support early examination',
          'Offer comparison features for administrators reconciling against internal policies',
          'Flag unusual usage patterns as possible unauthorized use (does not make legal determinations)',
          'Support record organization for tax, invoicing, and expense claims; final decisions rest with accountants, employers, and official guidance',
        ],
      },
      pdfCsv: 'PDF records are convenient for review and sharing; CSV records suit sorting, filtering, and import into accounting systems. Storing both formats enables flexible workflows. JTR provides output in PDF and CSV formats (not Excel or XLSX).',
      email: 'Setting up scheduled email delivery sends PDF and CSV records to specified addresses, eliminating manual download steps. For corporate and government users, direct delivery to accounting staff or administrators streamlines record-management workflows.',
      steps: [
        {
          t: 'Gather setup certificate and OBU information',
          d: 'Collect your setup certificate, OBU management number (19 digits), vehicle registration, and current ETC card information, then confirm the correspondence between vehicle and OBU.',
        },
        {
          t: 'Cross-check OBU number with vehicle data',
          d: 'Verify that the OBU management number matches the vehicle license plate, vehicle class, and owner information in the official setup records.',
        },
        {
          t: 'Review registration changes since initial setup',
          d: 'If the vehicle, plate number, owner, OBU unit, or towing equipment changed after initial setup, a new setup procedure is required.',
        },
        {
          t: 'Consult certified installer or official resources for discrepancies',
          d: 'When setup records are outdated, information mismatches, or records are incomplete, confirm the proper steps with a certified setup shop or official guidance.',
        },
        {
          t: 'Perform test run to verify operation after correction',
          d: 'After correction or re-setup, drive through an ETC lane and confirm that usage history is recorded with the expected ETC card and vehicle information.',
        },
        {
          t: 'Maintain organized records and vehicle mapping table',
          d: 'Keep setup certificates, PDF + CSV usage history, internal vehicle assignment tables, and notes in one place for future reference and inquiries.',
        },
      ],
      commonProblems: [
        {
          q: "I don't know whether to register my ETC card number or OBU management number.",
          a: 'The ETC card number identifies the payment card; the OBU management number (19 digits) identifies the device installed in the vehicle. Many registration procedures require both, so check official guidance for the fields requested.',
        },
        {
          q: "My usage history shows the card number, but I can't tell which vehicle it was used in.",
          a: 'The ETC inquiry service displays card-specific usage history, but vehicle information is usually masked. Create an internal mapping table between vehicles and cards, and manage it alongside your PDF + CSV records for easier tracking.',
        },
        {
          q: 'Do I need to re-setup if I move the OBU to a different vehicle?',
          a: 'Yes. The OBU management number is linked to vehicle information, so when the vehicle changes, visit a certified setup shop for re-setup and obtain a new setup certificate.',
        },
        {
          q: 'What records should I keep when using ETC in a rental car?',
          a: 'Store the rental agreement, ETC card statement, and the PDF + CSV records retrieved from the ETC inquiry service together. This makes later settlement and accounting verification smoother.',
        },
      ],
      examples: [
        {
          who: 'Driver using ETC for the first time',
          use: "By reading the 'ETC Card vs OBU Number Guide', I understood which official services to check before matching invoices with usage history.",
        },
        {
          who: 'Corporate accounting staff',
          use: 'I retrieve PDF + CSV records monthly and reconcile them with our internal vehicle ledger and department allocations, creating clear supporting documents for expense claims.',
        },
        {
          who: 'Family with multiple vehicles',
          use: 'I refer to usage history by ETC card, separate personal and business trips, then hand the organized information to my accountant.',
        },
        {
          who: 'Vehicle manager',
          use: 'When I spot unfamiliar usage history, I check entry IC, exit IC, date-time, and vehicle class in the official ETC records before asking the driver.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR an official highway information source?',
          a: 'No. JTR is an independent service. For official toll rates, discounts, setup procedures, and safety rules, consult each road operator or the ETC inquiry service. JTR supports receipt, organization, storage, and review of usage records.',
        },
        {
          q: 'Can the ETC inquiry service replace the road operator websites?',
          a: "No. The ETC inquiry service is useful for retrieving ETC card usage history and certificates, but current rates, discounts, vehicle classifications, and traffic rules must be confirmed on each road operator's official site.",
        },
        {
          q: 'Should I save PDF or CSV records—or both?',
          a: 'We recommend saving both. PDF format is convenient for viewing and sharing; CSV format is ideal for sorting, filtering, and importing into accounting systems.',
        },
        {
          q: 'Can I use these records for tax filings or expense claims?',
          a: "The records serve as helpful reference material for filings and claims, but final handling should be confirmed with your employer's policies, accountant's advice, and official guidance. This guide is not tax advice.",
        },
        {
          q: 'The amount or route seems incorrect. What should I do?',
          a: 'Review the official records and cross-check card, vehicle, date-time, and IC information. If still unclear, contact the relevant road operator or card issuer.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How JTR Works',
          desc: "Explains in detail how you receive, organize, and store ETC usage records, JTR's independent position, and the record management workflow.",
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'For companies and organizations with multiple vehicles, cards, and department allocation needs, we offer centralized management and review of PDF + CSV records.',
          path: '/business',
        },
        {
          title: 'Security Policy',
          desc: 'Publicly discloses the handling standards and protection methods for sensitive data such as ETC card numbers, OBU management numbers, and vehicle information.',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC Usage Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Check ETC card usage history, issue usage certificates, and download PDF and CSV formats. Standard ETC cards can access up to 15 months of records.',
        },
        {
          label: 'ETC General Information Portal: ETC Usage Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Describes eligible card types, certificate issuance conditions, record retention periods for standard and corporate ETC cards, wireless and non-wireless usage, and PDF/CSV download methods.',
        },
        {
          label: 'ETC General Information Portal: OBU Management Number Lookup',
          url: 'https://www.go-etc.jp/support/number_search',
          note: 'Explains the meaning of the 19-digit OBU management number, where to find it, and its use in setup, ETC mileage, and disability discount registration.',
        },
        {
          label: 'ETC General Information Portal: Setup',
          url: 'https://www.go-etc.jp/support/setup',
          note: 'Official guide to ETC and ETC2.0 setup and re-setup procedures, application details, and security standard precautions.',
        },
        {
          label: 'ETC Mileage Service',
          url: 'https://www.smile-etc.jp/',
          note: 'Official service where you earn points on eligible ETC toll charges and can apply those points toward future tolls.',
        },
      ],
      longBody: "Managing ETC usage records in Japan effectively requires understanding the difference between your ETC card number and your on-board unit management number, and how each connects to official data sources. The ETC card number identifies the payment card that is billed, while the on-board unit management number identifies the physical device installed in your vehicle. Many registration procedures require both pieces of information because the toll collection system must distinguish and coordinate three elements: payment method, vehicle information, and wireless toll processing. In practice, your card issuer's statement shows the card number and billed amounts, but may not include details such as which vehicle was used, which toll sections were traversed, or which discounts were applied. Meanwhile, the ETC usage inquiry service allows you to look up 15 months of trip records by card number (for standard cards), displaying entry and exit interchanges, date and time, the last four digits of the vehicle number, vehicle class, and discount details. However, this inquiry output alone may not provide enough context to reconcile records with internal vehicle logs or driver records. The on-board unit management number is a 19-digit unique identifier printed on your setup certificate and on a label affixed to the device itself. It is required for ETC Mileage Service registration, disability discount applications, and confirmation of certain time-limited discount eligibility. Whenever you replace your vehicle, swap a faulty unit, or change your license plate, a new setup is required and a new certificate is issued. At that point the unit number may change, so retaining old certificates as a change history simplifies later inquiries. When using a rental car, each vehicle carries its own on-board unit; you insert your ETC card, and you can verify which vehicle was used by matching the last four digits shown in the inquiry service with the rental vehicle's plate. Corporate and government fleets operate multiple vehicles and multiple cards, so when reconciling trip records with vehicle registers, driver logs, and expense claims, not only the card number but also the vehicle number and unit management number serve as important keys. JTR is an independent service—it is not an official toll-road operator, card issuer, or the ETC usage inquiry service itself. JTR is designed to help users receive, organize, store, and verify ETC usage records so that official data can be used more efficiently. Because records can be exported in both PDF and CSV formats, you can use PDF for visual review and CSV for import into internal systems or department-level aggregation. If a record appears unusual, first verify the source data in the official inquiry service by cross-checking the card number, vehicle number, date and time, and interchange information, then contact the road company or card issuer as needed. Final decisions on tax treatment and expense reimbursement must follow your employer's policies, your accountant's advice, and official guidance.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: 'ETC利用記録の生成フロー',
            intro: 'カード番号と車載器番号がどのように連携して記録が作られるかを図解',
            nodes: [
              {
                title: '料金所通過',
                desc: '車載器が無線通信で車両情報を送信',
              },
              {
                title: '車載器番号確認',
                desc: '19桁の管理番号で車両・区分を識別',
              },
              {
                title: 'ETCカード読取',
                desc: 'カード番号で支払元を特定',
              },
              {
                title: '料金計算',
                desc: '経路・車種・割引を適用',
              },
              {
                title: '記録生成',
                desc: '利用明細とカード明細に反映',
              },
            ],
          },
          en: {
            title: 'ETC Record Generation Flow',
            intro: 'How card numbers and OBU IDs work together to create toll records',
            nodes: [
              {
                title: 'Toll gate passage',
                desc: 'OBU transmits vehicle data wirelessly',
              },
              {
                title: 'OBU ID verification',
                desc: '19-digit ID identifies vehicle & class',
              },
              {
                title: 'ETC card read',
                desc: 'Card number determines payment source',
              },
              {
                title: 'Fare calculation',
                desc: 'Route, class, discounts applied',
              },
              {
                title: 'Record created',
                desc: 'Reflected in usage & billing statements',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: 'ETCカード番号 vs 車載器管理番号',
            intro: '2つの番号の役割と用途を一覧比較',
            columns: [
              'ETCカード番号',
              '車載器管理番号',
            ],
            rows: [
              {
                feature: '桁数',
                a: '16桁（カード会社により異なる）',
                b: '19桁（統一形式）',
                kindA: 'neutral',
                kindB: 'neutral',
              },
              {
                feature: '主な役割',
                a: '支払元の識別',
                b: '車両・機器の識別',
                kindA: 'yes',
                kindB: 'yes',
              },
              {
                feature: '記載場所',
                a: 'カード表面',
                b: 'セットアップ証明書・車載器本体',
                kindA: 'neutral',
                kindB: 'neutral',
              },
              {
                feature: '変更頻度',
                a: 'カード更新時',
                b: '車両変更・再セットアップ時',
                kindA: 'neutral',
                kindB: 'neutral',
              },
              {
                feature: '利用照会に必要',
                a: '必須',
                b: '車両特定時に有用',
                kindA: 'yes',
                kindB: 'partial',
              },
            ],
          },
          en: {
            title: 'ETC Card Number vs OBU ID',
            intro: 'Side-by-side comparison of the two identifier types',
            columns: [
              'ETC Card Number',
              'OBU Management ID',
            ],
            rows: [
              {
                feature: 'Digit count',
                a: '16 digits (varies by issuer)',
                b: '19 digits (unified format)',
                kindA: 'neutral',
                kindB: 'neutral',
              },
              {
                feature: 'Primary role',
                a: 'Payment source ID',
                b: 'Vehicle & device ID',
                kindA: 'yes',
                kindB: 'yes',
              },
              {
                feature: 'Where found',
                a: 'Card surface',
                b: 'Setup certificate, OBU unit',
                kindA: 'neutral',
                kindB: 'neutral',
              },
              {
                feature: 'Change frequency',
                a: 'Card renewal',
                b: 'Vehicle change, re-setup',
                kindA: 'neutral',
                kindB: 'neutral',
              },
              {
                feature: 'Needed for query',
                a: 'Required',
                b: 'Useful for vehicle ID',
                kindA: 'yes',
                kindB: 'partial',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '公式情報源マップ',
            intro: 'カード番号・車載器番号に関連する公式サービスと役割',
            sources: [
              {
                name: 'ETC利用照会サービス',
                role: 'カード番号で利用明細・証明書を照会（15カ月分）',
                url: 'https://www.etc-meisai.jp/',
                scope: '全国の高速道路利用記録',
              },
              {
                name: 'ETC総合情報ポータル',
                role: '車載器番号確認・セットアップ・割引制度案内',
                url: 'https://www.go-etc.jp/',
                scope: '車載器管理・登録情報',
              },
              {
                name: 'カード会社明細',
                role: '月次請求明細でカード番号別の支払額確認',
                url: '各カード会社サイト',
                scope: '請求・支払履歴',
              },
              {
                name: 'JTR（独立サービス）',
                role: '記録の受信・整理・保管支援（公式データ作成なし）',
                url: 'https://japantollreceipts.jp/',
                scope: 'PDF・CSV整理・配信',
              },
            ],
          },
          en: {
            title: 'Official Information Sources',
            intro: 'Key services for card number & OBU ID management',
            sources: [
              {
                name: 'ETC Usage Inquiry',
                role: 'Query statements & certificates by card number (15 months)',
                url: 'https://www.etc-meisai.jp/',
                scope: 'Nationwide toll records',
              },
              {
                name: 'ETC Portal (go-etc.jp)',
                role: 'OBU ID lookup, setup, discount programs',
                url: 'https://www.go-etc.jp/',
                scope: 'Device management & registration',
              },
              {
                name: 'Card company statements',
                role: 'Monthly billing by card number',
                url: 'Issuer websites',
                scope: 'Billing & payment history',
              },
              {
                name: 'JTR (independent)',
                role: 'Record aggregation & organization (no official data creation)',
                url: 'https://japantollreceipts.jp/',
                scope: 'PDF/CSV delivery',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '記録管理のチェックリスト',
            intro: 'カード番号・車載器番号を活用した確実な記録保管',
            items: [
              {
                text: 'ETCカード番号を控え、利用照会サービスにログイン可能にする',
                hint: 'etc-meisai.jpで15カ月分照会可',
              },
              {
                text: '車載器管理番号をセットアップ証明書で確認・保管',
                hint: '車両変更時の再セットアップに必要',
              },
              {
                text: 'カード別・車両別にPDF・CSV記録を整理',
                hint: 'JTRで自動グループ化可能',
              },
              {
                text: '月次カード明細と利用明細を照合',
                hint: '不正利用・請求相違の早期発見',
              },
              {
                text: 'レンタカー・共用車は車載器番号でログを分離',
                hint: 'ドライバー・部署別精算に有効',
              },
              {
                text: '定期配信を設定し記録の受信漏れを防ぐ',
                hint: '経理担当者へ直接メール配信',
              },
            ],
          },
          en: {
            title: 'Record Management Checklist',
            intro: 'Using card & OBU numbers for reliable record-keeping',
            items: [
              {
                text: 'Note ETC card number; ensure access to usage inquiry service',
                hint: 'etc-meisai.jp offers 15-month history',
              },
              {
                text: 'Verify & store OBU ID from setup certificate',
                hint: 'Needed for vehicle changes, re-setup',
              },
              {
                text: 'Organize PDF/CSV by card & vehicle',
                hint: 'JTR can auto-group records',
              },
              {
                text: 'Cross-check card billing vs. usage statements',
                hint: 'Early detection of fraud, discrepancies',
              },
              {
                text: 'Separate logs by OBU ID for rentals, shared cars',
                hint: 'Useful for driver/dept allocation',
              },
              {
                text: 'Set up periodic delivery to avoid missing records',
                hint: 'Direct email to accounting staff',
              },
            ],
          },
        },
      },
    ],
  },

  // 7) ETC利用明細に入口ICが不明と表示される理由
  {
    slug: 'etc-meisai-unknown-entry-ic',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: 'ETC明細 入口IC不明',
      en: 'ETC entry IC unknown',
    },
    ja: {
      title: 'ETC明細 入口IC不明ガイド | Japan Toll Receipts',
      metaDesc: 'ETC明細で入口IC（入口インターチェンジ）が「不明」「未表示」「---」と表示されるケースの原因（ETC通信エラー、カード装着不良、車載器不調、料金所側システム）と、ETC利用照会サービスでの公式記録確認、PDF・CSV保存、NEXCOへの問い合わせ・払戻対応、企業・レンタカー・ドライバー向け実務手順を解説。',
      h1: 'ETC明細 入口IC不明ガイド',
      primaryKeyword: 'ETC明細 入口IC不明',
      answer: 'ETC利用記録で入口ICが空欄・不明・想定外の場合、料金計算・問合せ・払戻処理に影響します。利用日・出口IC・カード番号・車両・走行目的を記録し、該当する道路会社またはETC利用照会サービスで公式記録を確認してください。不詳細な記録はスクリーンショットや記憶だけに頼らず、公式明細とメモを一緒に保管することが重要です。',
      whyMatters: '入口IC不明の記録は、料金根拠の確認・経費精算・車両管理・不正利用チェックの各場面で混乱を招きます。ドライバーが数週間後に「なぜこの料金か」を説明できない、経理担当が正しい勘定科目を判断できない、レンタカー利用者が返却後のカード明細と照合できない、といった問題が起こります。公式記録とメモを組み合わせて保管すれば、後日の確認作業が安全かつ効率的になります。',
      officialBackground: '日本の有料道路記録は複数の独立したシステムで管理されています。NEXCO各社や首都高速など道路会社は料金・ルート・割引・車種区分・レーン情報を公開し、ETC利用照会サービスはETCカード別の利用明細・利用証明書・PDF/CSVダウンロードを提供、カード発行会社は月次カード明細を発行します。JTRはこれらの公式記録を受取・整理・保管・確認する独立サービスであり、道路会社・MEISAI・ETC利用照会サービス・カード会社・政府機関ではありません。JTRは公式通行料金データを作成せず、既存の公式記録を実務で使いやすく提示します。入口IC不明の記録が出た場合、利用日・出口IC・カード・車両・車種区分を手元に用意し、該当する道路会社またはETC利用照会サービスへ問合せてください。',
      whoFor: [
        '入口IC不明のETC明細を経費精算に使うドライバー',
        '複数車両・複数カードの通行記録を管理する経理担当者',
        'レンタカー返却後にカード明細と走行記録を照合する利用者',
        '不詳細な記録を早期に発見し確認作業を進める車両管理者',
      ],
      jtrSolution: {
        intro: 'JTRは入口IC不明を含むETC利用記録をPDF・CSVで受取り、カード別・車両別に整理し、レビューが必要な記録を目立たせて管理者の早期確認を支援します。公式記録と社内メモを一元管理することで、後日の問合せ・精算・確認対応が容易になります。',
        bullets: [
          'PDF・CSV形式でETC利用明細を保管し、入口IC・出口IC・日時・カード・車両情報を一覧表示',
          'カード別・車両別に記録を分類し、複数車両・複数部署の通行を区別',
          '不明・遅延・想定外の記録を強調表示し、管理者の早期レビューを促進',
          '公式明細と社内メモ（運転者・目的・承認状況）を同じ画面で確認可能',
          '税務・精算・確認用の記録整理を支援（最終判断は会計士・雇用主・公式指針で確認）',
        ],
      },
      pdfCsv: 'JTRはPDF（閲覧・共有に便利）とCSV（並替え・絞込み・会計ソフト取込みに対応）の両形式で記録を提供します。CSVを表計算ソフトで開けば、日付・カード・車両ごとの集計や部署別配分が容易になります。公式記録は改変せず原本として保管してください。',
      email: 'JTRはカード別・車両別の月次記録をメール配信できます。入口IC不明など確認が必要な記録が含まれる場合、管理者へ早期通知することで、ドライバーへの問合せや道路会社への照会を迅速に開始できます。配信設定は任意です。',
      steps: [
        {
          t: '対象となる記録の特定',
          d: '入口ICが不明・未表示・予期しない形式で記録されたETC利用明細を確認します。日付・出口IC・ETCカード・車両情報を整理し、記録の不詳細性を把握します。',
        },
        {
          t: '公式な料金事業者サイトの確認',
          d: '経路・料金額・車両区分・割引適用・ETC専用ゲート・道路固有のルールに関する質問がある場合は、まず該当する道路事業者の公式サイトを確認します。',
        },
        {
          t: 'ETC利用照会サービスまたはカード明細の確認',
          d: '走行後のETC利用明細・利用証明書・PDF記録・CSV記録を取得するため、ETC利用照会サービスまたはカード会社明細を確認します。',
        },
        {
          t: '社内記録形式への転記',
          d: '利用日・入口IC・出口IC・ETCカード・車両・車種区分・運転者または部署・利用目的を一貫した社内フォーマットに記録します。',
        },
        {
          t: '公式記録と社内説明の分離保管',
          d: '公式な通行記録と社内の精算目的・承認経緯は別ファイルとして管理します。後日の確認・監査・質問対応時に混乱を防ぎます。',
        },
        {
          t: '不明点がある場合は公式窓口へ問い合わせ',
          d: '記録が不詳細・遅延・予期しない内容の場合は推測せず、該当する道路事業者またはカード発行会社へ連絡し、回答を記録と共に保管します。',
        },
      ],
      commonProblems: [
        {
          q: '入口IC不明の記録が複数件あり、精算書類として不十分',
          a: 'ETC利用照会サービスでPDF+CSV記録をダウンロードし、日付・出口IC・カード番号・車両情報を整理した上で、不明点を道路事業者へ問い合わせ、回答を添付します。',
        },
        {
          q: '記録が遅れて表示され、月次締め後に発覚した',
          a: '遅延記録の利用日・カード・車両を確認し、該当月の補足資料として保管します。経理担当者または上長へ報告し、次回処理方法を社内で決定します。',
        },
        {
          q: 'レンタカー利用時に入口IC不明の記録があり説明が困難',
          a: 'レンタル契約書・ETC利用照会サービスの記録・カード明細を揃え、レンタル期間と照合します。不明点がある場合はレンタル会社または道路事業者へ確認します。',
        },
        {
          q: '社用車の利用記録に見覚えのない日付と出口ICがある',
          a: '公式記録で日付・カード・車両・出口ICを確認し、運転者へ確認します。説明がつかない場合は上長へ報告し、記録を保全した上で社内規程に従い対応します。',
        },
      ],
      examples: [
        {
          who: '経理担当者',
          use: '月次精算前に「入口IC不明」記録を確認し、ETC利用照会サービスでPDF+CSV記録を取得して、不足情報を道路事業者へ問い合わせ、回答を添付して承認申請します。',
        },
        {
          who: '車両管理責任者',
          use: '複数車両のETC記録を比較中に入口IC不明の行を発見し、JTRで該当記録をフラグ表示させ、運転者へ確認依頼を送り、回答を記録へ追記します。',
        },
        {
          who: 'レンタカー利用者',
          use: 'レンタル期間中の通行記録に入口IC不明の行があり、契約書・ETC明細・公式記録を揃えて精算書類パケットとして提出し、質問に備えます。',
        },
        {
          who: 'サポート担当者',
          use: '顧客から入口IC不明記録の問い合わせを受け、日付・出口IC・カード番号・車種区分を聞き取り、該当する道路事業者窓口へ案内します。',
        },
      ],
      faqs: [
        {
          q: 'JTRは入口IC不明記録の公式情報源ですか?',
          a: 'いいえ。JTRは独立したサービスで、公式な経路・料金・割引・設定・安全ルールは該当する道路事業者または公式ETCサービスで確認する必要があります。JTRはETC利用記録の受信・整理・保管・確認支援を行います。',
        },
        {
          q: 'ETC利用照会サービスで入口IC不明の理由は分かりますか?',
          a: 'ETC利用照会サービスは記録を提供しますが、入口IC不明の原因説明は道路事業者窓口へ問い合わせる必要があります。記録ダウンロード後、該当する事業者へ連絡してください。',
        },
        {
          q: 'PDF記録とCSV記録のどちらを保存すべきですか?',
          a: '両方の保存を推奨します。PDF記録は確認・共有が容易で、CSV記録は並べ替え・絞り込み・会計システムへの取り込みに適しています。',
        },
        {
          q: '入口IC不明の記録は税務処理や精算に使えますか?',
          a: '記録は精算審査の補助資料となりますが、最終的な取扱いは勤務先の規程・会計士の助言・公式ガイダンスに従ってください。本ガイドは税務助言ではありません。',
        },
        {
          q: '記録の金額や経路が間違っているようです。どうすればよいですか?',
          a: '公式記録を確認し、カードと車両を照合し、利用日とIC情報を比較した上で、該当する道路事業者またはカード発行会社へ問い合わせてください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'ETCメイサイ記録欠落の原因',
          desc: '記録が見つからない場合の主因と確認手順を解説します。',
          path: '/guides/etc-meisai-missing-trips-reasons',
        },
        {
          title: 'ETC明細の反映時間と月末対応',
          desc: '走行から明細反映までの時間と、月末締めの段取りを解説します。',
          path: '/guides/etc-meisai-update-timing',
        },
        {
          title: 'JTRの仕組み',
          desc: 'ETC利用記録をPDF+CSV形式で受信・整理・保管し、カード別・車両別に確認できる仕組みを説明します。',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数車両・複数カードのETC記録を一元管理し、部署別配賦・承認ワークフロー・確認対応を支援します。',
          path: '/business',
        },
        {
          title: 'セキュリティ',
          desc: 'JTRが記録を保管・処理する際のアクセス制御・暗号化・取扱履歴の仕組みを説明します。',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用記録の確認・利用証明書発行・PDF/CSV形式での明細ダウンロードを提供する公式サービス。通常のETCカード記録は過去15か月分が対象です。',
        },
        {
          label: 'ETC総合情報ポータル: ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別・利用証明書発行・通常ETCカード記録期間・ETCコーポレートカード記録期間・無線/非無線ETC利用・PDF/CSVダウンロードの説明。',
        },
        {
          label: 'ETC総合情報ポータル: お問い合わせ',
          url: 'https://www.go-etc.jp/contact',
          note: '料金・割引・ETC利用に関する質問のための公式道路事業者カスタマーセンター一覧。',
        },
        {
          label: 'DrivePlaza 料金・経路検索',
          url: 'https://www.driveplaza.com/dp/SearchTop',
          note: 'NEXCO高速道路および関連路線の経路・料金検索サービス。',
        },
        {
          label: 'NEXCO西日本 料金・経路検索',
          url: 'https://search.w-nexco.co.jp/',
          note: 'NEXCO西日本の経路・料金検索サービス。',
        },
      ],
      longBody: 'ETC利用明細に入口インターチェンジ情報が欠落または不明表示される事例は、実際の通行が正常に完了していても記録上の表示が曖昧になる可能性があり、後日の確認や経費精算の際に混乱を招くことがあります。この現象は特定の料金所設備の動作特性、ETC車載器とカード間の通信タイミング、または照会システム側の記録形式の違いによって生じることがあり、利用者側の操作ミスではない場合も含まれます。そのため単に金額だけを確認するのではなく、通行日時、出口IC、利用カード番号の下四桁、車両情報、通行理由という五つの基本要素を一貫した形式で記録し、公式な利用証明書またはPDF・CSV形式の明細データと社内説明を分離して保管することが実務上の安全策となります。企業や官公庁、レンタカー事業者、複数車両を管理する家庭では、後から「誰がどの車両でどの経路を使ったのか」を再現できるよう、ETC利用照会サービスや各カード会社の明細と内部記録を突き合わせる習慣が重要です。JTRは独立したサービスとして、こうした公式記録の受信・整理・保管・見直し支援を担いますが、料金計算やルール判定の公式情報源ではありません。入口IC情報が不明な場合、まず該当する道路運営会社の公式サイトで料金体系や割引条件、車種区分、ETC専用化状況を確認し、次にETC利用照会サービスまたはカード明細で実際の利用履歴と証明書を取得します。記録が予想と異なる、遅延している、または意図しない通行として表示されている場合は、推測せず該当する運営会社またはカード発行会社へ直接問い合わせ、回答内容を記録と一緒に保存してください。よくある誤りとして、スクリーンショットや手書きメモだけで済ませる、公式証明書を取得せずに口頭説明だけで精算を進める、車両と利用者の紐付けを曖昧なままにする、といった対応が挙げられます。こうした状態では数か月後の監査や部門間確認で説明が困難になるため、初回記録時に必要な情報を揃えておくことが後の負担軽減につながります。JTRはPDF・CSV形式で記録を提供し、カードや車両ごとのグループ化、見直しが必要な項目の強調表示、内部ポリシーとの比較支援を行いますが、不正を断定したり法的判断を下す機能ではなく、早期の見直しと管理者確認を支援する位置付けです。税務処理や請求書対応については、最終的な扱いを会計士、勤務先、所属機関の規程、または公式ガイダンスで確認することを推奨します。入口IC不明表示が発生した際も、公式記録と社内文脈を切り離さず一体管理することで、後日の説明責任と業務効率の両立が可能になります。',
    },
    en: {
      title: 'ETC Entry IC Unknown · Missing Toll Record Guide | JTR',
      metaDesc: 'ETC records with missing or unknown entry IC: how to organize records, query official sources, save PDF·CSV, and handle refunds. Practical steps for companies, rental users, and drivers.',
      h1: 'ETC Entry IC Unknown Guide',
      primaryKeyword: 'ETC entry IC unknown',
      answer: 'When an ETC usage record shows a blank, unknown, or unexpected entry IC, it affects toll calculation, inquiries, and refund processes. Record the usage date, exit IC, card number, vehicle, and trip purpose, then confirm the official record via the relevant highway operator or ETC Usage Inquiry Service. Do not rely solely on screenshots or memory for incomplete records—keep official statements and notes together.',
      whyMatters: 'Missing entry IC data causes confusion in toll verification, expense claims, multi-vehicle records, and misuse checks. Drivers cannot explain charges weeks later, accounting staff cannot assign correct expense categories, rental customers cannot reconcile post-return card statements, and vehicle managers face delays. Combining official records with notes makes later review safe and efficient.',
      officialBackground: "Japan's toll road records are managed by independent systems. Highway operators (NEXCO, Metropolitan Expressway) publish toll, route, discount, vehicle class, and lane data; ETC Usage Inquiry Service provides card-based usage statements, certificates, and PDF/CSV downloads; card issuers provide monthly statements. JTR is an independent service for receiving, organizing, storing, and reviewing these official records—NOT a highway operator, MEISAI, ETC inquiry service, card issuer, or government agency. JTR does not create official toll data; it presents existing official records for practical use. When an entry IC is missing, prepare usage date, exit IC, card, vehicle, and vehicle class, then contact the relevant highway operator or ETC Usage Inquiry Service.",
      whoFor: [
        'Drivers submitting ETC records with missing entry IC for expense reimbursement',
        'Accounting staff managing toll records across multiple vehicles and cards',
        'Rental car customers reconciling card statements with trip records after vehicle return',
        'Vehicle managers identifying incomplete records early and initiating verification',
      ],
      jtrSolution: {
        intro: 'JTR receives ETC usage records (including missing entry IC) in PDF and CSV, organizes them by card and vehicle, and highlights records needing review to help managers confirm details early. Centralized management of official records and internal notes simplifies later inquiries, claims, and audits.',
        bullets: [
          'Store ETC usage statements in PDF and CSV, listing entry IC, exit IC, date-time, card, and vehicle in one view',
          'Classify records by card and vehicle to distinguish trips across multiple vehicles and departments',
          'Highlight missing, delayed, or unexpected records to prompt early manager review',
          'Display official statements and internal notes (driver, purpose, approval status) on the same screen',
          'Support tax, claims, and audit record organization (final decisions rest with accountants, employers, and official guidance)',
        ],
      },
      pdfCsv: 'JTR provides records in PDF (convenient for viewing and sharing) and CSV (supports sorting, filtering, and accounting software import). Opening CSV in spreadsheet software enables summaries by date, card, or vehicle and department allocation. Preserve official records unaltered as originals.',
      email: 'JTR can deliver monthly card- and vehicle-based records by email. When records requiring review (such as missing entry IC) are present, early notification to managers enables prompt driver inquiry or highway operator contact. Delivery settings are optional.',
      steps: [
        {
          t: 'Identify the affected record',
          d: 'Review ETC usage detail where entry IC is unknown, missing, or recorded in unexpected format. Organize date, exit IC, ETC card, and vehicle information to understand the incomplete nature of the record.',
        },
        {
          t: 'Check official toll operator websites',
          d: 'If you have questions about routes, toll amounts, vehicle class, discount eligibility, ETC-only gates, or road-specific rules, first consult the official website of the relevant road operator.',
        },
        {
          t: 'Check ETC inquiry service or card statement',
          d: 'To obtain post-journey ETC usage detail, usage certificate, PDF record, or CSV record, check the ETC inquiry service or your card company statement.',
        },
        {
          t: 'Transcribe into internal record format',
          d: 'Record usage date, entry IC, exit IC, ETC card, vehicle, vehicle class, driver or department, and usage purpose into a consistent internal format.',
        },
        {
          t: 'Store official records and internal notes separately',
          d: 'Manage official transit records and internal settlement purpose or approval history as separate files. This prevents confusion during later review, audit, or inquiries.',
        },
        {
          t: 'Contact official helpdesk for unclear points',
          d: 'If a record is incomplete, delayed, or has unexpected content, do not guess—contact the relevant road operator or card issuer, and store the response with the record.',
        },
      ],
      commonProblems: [
        {
          q: 'Multiple records with unknown entry IC make settlement documents insufficient',
          a: 'Download PDF + CSV records from the ETC inquiry service, organize by date, exit IC, card number, and vehicle information, then contact the road operator with specific questions and attach their response.',
        },
        {
          q: 'Record appeared late and was discovered after monthly closing',
          a: 'Verify the usage date, card, and vehicle of the delayed record, and store it as supplementary material for the relevant month. Report to accounting staff or supervisor and decide internally on next-time handling.',
        },
        {
          q: 'Rental car usage has unknown entry IC record and is difficult to explain',
          a: 'Gather rental contract, ETC inquiry service records, and card statement, and cross-reference with rental period. If unclear points remain, contact the rental company or road operator.',
        },
        {
          q: 'Company vehicle usage record shows unfamiliar date and exit IC',
          a: 'Check official record for date, card, vehicle, and exit IC, then confirm with the driver. If unexplained, report to supervisor, preserve the record, and follow internal procedures.',
        },
      ],
      examples: [
        {
          who: 'Accounting staff',
          use: "Before monthly settlement, check records marked 'entry IC unknown,' retrieve PDF + CSV records from ETC inquiry service, contact road operator for missing information, attach response, and submit for approval.",
        },
        {
          who: 'Vehicle manager',
          use: 'While comparing ETC records for multiple vehicles, discover row with unknown entry IC, flag the record in JTR, send confirmation request to driver, and append response to the record.',
        },
        {
          who: 'Rental car user',
          use: 'When rental period transit record shows unknown entry IC row, gather contract, ETC statement, and official record into settlement document package and prepare for questions.',
        },
        {
          who: 'Support staff',
          use: 'When customer inquires about unknown entry IC record, collect date, exit IC, card number, and vehicle class, then direct them to the relevant road operator helpdesk.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR an official source for unknown entry IC records?',
          a: 'No. JTR is an independent service; official route, toll, discount, setting, and safety rules must be verified with the relevant road operator or official ETC service. JTR receives, organizes, stores, and assists with review of ETC usage records.',
        },
        {
          q: 'Does the ETC inquiry service explain why entry IC is unknown?',
          a: 'The ETC inquiry service provides records, but explanation of unknown entry IC cause requires contacting the road operator helpdesk. After downloading records, contact the relevant operator.',
        },
        {
          q: 'Should I save PDF records or CSV records?',
          a: 'Saving both is recommended. PDF records are easy to review and share, while CSV records are suitable for sorting, filtering, and importing into accounting systems.',
        },
        {
          q: 'Can unknown entry IC records be used for tax processing or settlement?',
          a: "Records serve as supplementary material for settlement review, but final treatment must follow your employer's rules, accountant's advice, or official guidance. This guide is not tax advice.",
        },
        {
          q: 'The recorded amount or route appears incorrect. What should I do?',
          a: 'Check the official record, cross-reference card and vehicle, compare usage date and IC information, then contact the relevant road operator or card issuer.',
        },
      ],
      relatedFeatures: [
        {
          title: 'Missing ETC Meisai records — causes & checks',
          desc: 'Why trips are absent and how to verify before processing expenses.',
          path: '/guides/etc-meisai-missing-trips-reasons',
        },
        {
          title: 'ETC statement update timing',
          desc: 'How long trips take to appear and how to plan month-end closing.',
          path: '/guides/etc-meisai-update-timing',
        },
        {
          title: 'How JTR Works',
          desc: 'Explains the mechanism for receiving, organizing, and storing ETC usage records in PDF + CSV format, with card-by-card and vehicle-by-vehicle review.',
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'Centralized management of ETC records for multiple vehicles and cards, supporting departmental allocation, approval workflows, and audit response.',
          path: '/business',
        },
        {
          title: 'Security',
          desc: 'Explains access control, encryption, and audit log mechanisms when JTR stores and processes records.',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC Usage Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service providing ETC card usage record review, usage certificate issuance, and statement download in PDF/CSV format. Standard ETC card records cover the past 15 months.',
        },
        {
          label: 'ETC General Information Portal: ETC Usage Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explanation of eligible card types, usage certificate issuance, standard ETC card record period, ETC corporate card record period, wireless/non-wireless ETC usage, and PDF/CSV download.',
        },
        {
          label: 'ETC General Information Portal: Contact',
          url: 'https://www.go-etc.jp/contact',
          note: 'Official road operator customer center directory for questions about tolls, discounts, and ETC usage.',
        },
        {
          label: 'DrivePlaza Route & Toll Search',
          url: 'https://www.driveplaza.com/dp/SearchTop',
          note: 'Route and toll search service for NEXCO expressways and related routes.',
        },
        {
          label: 'NEXCO West Japan Route & Toll Search',
          url: 'https://search.w-nexco.co.jp/',
          note: 'Route and toll search service for NEXCO West Japan.',
        },
      ],
      longBody: "When entry interchange information appears missing or marked as unknown on an ETC transaction statement, the on-road passage itself may have completed without incident, yet the recorded display becomes ambiguous and can complicate later verification or expense reconciliation. This phenomenon may arise from specific toll-gate equipment behavior, communication timing between the onboard ETC unit and card, or differences in how the inquiry system formats records—often unrelated to any user error. Rather than relying solely on the charged amount, prudent practice involves documenting five core elements in consistent format: travel date and time, exit interchange, last four digits of the card number, vehicle identity, and the purpose of the trip. Separating official usage certificates or PDF and CSV statement data from internal explanatory notes forms a sound operational safeguard. For corporations, government agencies, rental-car operators, and households managing multiple vehicles, the ability to reconstruct who drove which vehicle along which route demands a routine habit of cross-checking ETC inquiry service records, card-issuer statements, and internal logs. JTR serves as an independent platform for receiving, organizing, storing, and supporting the review of such official records, yet it does not function as the authoritative source for toll calculations or regulatory determinations. When entry interchange data is unknown, first consult the relevant road operator's official site to confirm toll structures, discount conditions, vehicle classifications, and ETC-exclusive lanes. Next, retrieve actual usage history and certificates through the ETC inquiry service or card statements. If records differ from expectations, appear delayed, or show unintended trips, avoid speculation and contact the operator or card issuer directly, preserving their replies alongside your records. Common pitfalls include relying solely on screenshots or handwritten notes, advancing expense claims with verbal explanations instead of official certificates, and leaving vehicle-to-user associations unclear. Such gaps make explanation difficult during audits or interdepartmental reviews months later, so gathering necessary information at the point of initial recording reduces future burden. JTR delivers records in PDF and CSV formats, supports grouping by card or vehicle, highlights items requiring attention, and aids comparison against internal policies—but it does not adjudicate misconduct or render legal judgments; its role is to facilitate early review and managerial confirmation. For tax handling or invoicing matters, final treatment should be confirmed with an accountant, employer, affiliated institution's rules, or official guidance. Even when entry interchange appears unknown, unified management that keeps official records and internal context together enables both accountability and operational efficiency down the line.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '入口IC不明記録の確認フロー',
            intro: '不明記録が出た場合の公式確認と保管手順',
            nodes: [
              {
                title: '記録発見',
                desc: '明細で入口IC空欄・不明・想定外を発見',
              },
              {
                title: '情報収集',
                desc: '利用日・出口IC・カード番号・車両・目的を記録',
              },
              {
                title: '公式照会',
                desc: '道路会社またはETC利用照会サービスで公式記録を確認',
              },
              {
                title: '記録保管',
                desc: '公式明細とメモを一緒にPDF・CSVで保管',
              },
              {
                title: '精算対応',
                desc: '完全な記録を使い経費精算・監査に対応',
              },
            ],
          },
          en: {
            title: 'Unknown Entry IC Record Flow',
            intro: 'Official verification and storage steps for incomplete records',
            nodes: [
              {
                title: 'Detect Record',
                desc: 'Find blank, unknown, or unexpected entry IC in statement',
              },
              {
                title: 'Gather Info',
                desc: 'Note date, exit IC, card number, vehicle, and purpose',
              },
              {
                title: 'Official Inquiry',
                desc: 'Check official record via road operator or ETC service',
              },
              {
                title: 'Store Record',
                desc: 'Keep official statement and notes together in PDF/CSV',
              },
              {
                title: 'Settlement',
                desc: 'Use complete records for reimbursement and audit',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '公式記録の照会先マップ',
            intro: '入口IC不明記録の公式確認先',
            sources: [
              {
                name: 'NEXCO各社',
                role: '高速道路料金・ルート・割引情報',
                url: 'https://www.driveplaza.com/',
                scope: '東日本・中日本・西日本の各エリア',
              },
              {
                name: '首都高速・阪神高速',
                role: '都市高速の料金・利用記録',
                url: '各道路会社公式サイト',
                scope: '首都圏・関西圏の都市高速',
              },
              {
                name: 'ETC利用照会サービス',
                role: 'カード別利用明細・証明書・CSV',
                url: 'https://www.etc-meisai.jp/',
                scope: '全国の有料道路ETC利用記録',
              },
              {
                name: 'カード発行会社',
                role: '月次カード明細・請求額',
                url: '各カード会社サイト',
                scope: 'ETCカード決済記録',
              },
              {
                name: 'JTR',
                role: '公式記録の整理・保管・一元管理',
                url: 'https://japantollreceipts.jp/',
                scope: '独立記録管理サービス',
              },
            ],
          },
          en: {
            title: 'Official Source Reference Map',
            intro: 'Where to verify unknown entry IC records',
            sources: [
              {
                name: 'NEXCO Companies',
                role: 'Expressway tolls, routes, discounts',
                url: 'https://www.driveplaza.com/',
                scope: 'East, Central, West Japan areas',
              },
              {
                name: 'Metro Expressways',
                role: 'Urban expressway tolls and records',
                url: 'Official operator sites',
                scope: 'Tokyo and Osaka metro areas',
              },
              {
                name: 'ETC Inquiry Service',
                role: 'Card-based statements, certificates, CSV',
                url: 'https://www.etc-meisai.jp/',
                scope: 'Nationwide ETC toll records',
              },
              {
                name: 'Card Issuers',
                role: 'Monthly card statements, billing',
                url: 'Card company sites',
                scope: 'ETC card payment records',
              },
              {
                name: 'JTR',
                role: 'Official record organization and storage',
                url: 'https://japantollreceipts.jp/',
                scope: 'Independent record management',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: '記録保管方法の比較',
            intro: 'スクリーンショットのみ vs. 公式記録+メモ',
            columns: [
              'スクリーンショットのみ',
              '公式記録+メモ',
            ],
            rows: [
              {
                feature: '料金根拠の確認',
                a: '入口IC不明で計算困難',
                b: '公式記録で完全な根拠',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '経費精算対応',
                a: '経理が勘定科目を判断できない',
                b: 'ルート・目的で正確に分類',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '監査・税務対応',
                a: '不完全な記録で説明困難',
                b: '公式明細で証跡を提示',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '後日の照合作業',
                a: '記憶頼みで時間がかかる',
                b: '日時・車両で素早く照合',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '複数車両管理',
                a: 'カード別の分類が手動',
                b: '車両別・部署別に自動分類',
                kindA: 'partial',
                kindB: 'yes',
              },
            ],
          },
          en: {
            title: 'Record Storage Method Comparison',
            intro: 'Screenshot-only vs. Official record + notes',
            columns: [
              'Screenshot Only',
              'Official + Notes',
            ],
            rows: [
              {
                feature: 'Toll verification',
                a: 'Unknown entry, calculation difficult',
                b: 'Complete basis via official record',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Expense reimbursement',
                a: 'Accounting cannot classify correctly',
                b: 'Accurate by route and purpose',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Audit & tax compliance',
                a: 'Incomplete record, hard to explain',
                b: 'Present evidence with official statement',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Later reconciliation',
                a: 'Rely on memory, time-consuming',
                b: 'Quick match by date and vehicle',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Multi-vehicle records',
                a: 'Manual card-based sorting',
                b: 'Auto-sort by vehicle and department',
                kindA: 'partial',
                kindB: 'yes',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '入口IC不明記録の確認チェックリスト',
            intro: '公式照会前に手元で準備すべき情報',
            items: [
              {
                text: '利用日時を記録から確認',
                hint: '日付・時刻が分かれば道路会社の照会が容易',
              },
              {
                text: '出口ICと走行ルートをメモ',
                hint: '出口が分かれば推定ルートで入口を特定可能',
              },
              {
                text: 'ETCカード番号と車両情報を用意',
                hint: '照会サービスでカード別・車両別に検索',
              },
              {
                text: '走行目的と運転者を記録',
                hint: '経費精算・部署配分に必要な社内情報',
              },
              {
                text: '公式明細をPDF・CSVで保管',
                hint: 'スクリーンショットだけでなく元データを保存',
              },
              {
                text: '道路会社またはETC利用照会サービスへ問合せ',
                hint: '公式記録で入口IC・料金根拠を確認',
              },
              {
                text: '公式記録と社内メモを一緒に保管',
                hint: '監査・税務対応時に完全な証跡を提示',
              },
            ],
          },
          en: {
            title: 'Unknown Entry IC Verification Checklist',
            intro: 'Information to prepare before official inquiry',
            items: [
              {
                text: 'Confirm usage date and time',
                hint: 'Date/time simplifies road operator inquiry',
              },
              {
                text: 'Note exit IC and route taken',
                hint: 'Exit location helps identify likely entry IC',
              },
              {
                text: 'Prepare ETC card number and vehicle info',
                hint: 'Search by card and vehicle in inquiry service',
              },
              {
                text: 'Record trip purpose and driver',
                hint: 'Internal info needed for reimbursement and allocation',
              },
              {
                text: 'Store official statement as PDF/CSV',
                hint: 'Save source data, not just screenshots',
              },
              {
                text: 'Contact road operator or ETC inquiry service',
                hint: 'Verify entry IC and toll basis via official record',
              },
              {
                text: 'Keep official record and notes together',
                hint: 'Present complete evidence for audit and tax',
              },
            ],
          },
        },
      },
    ],
  },

  // 8) ETC利用明細が反映されるまでの時間
  {
    slug: 'etc-meisai-update-timing',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: 'ETC明細 反映時間',
      en: 'ETC statement posting time',
    },
    ja: {
      title: 'ETC明細はいつ反映される？通常4〜5時間・遅延理由 | JTR',
      metaDesc: 'ETC利用照会サービスへの反映は通常4〜5時間後。遅延の理由（料金所システム・週末・連休・メンテナンス）、月末締めの対応、走行直後・新規登録後の表示タイミングを実務ベースで解説。',
      h1: 'ETC明細の反映時間と月末対応',
      primaryKeyword: 'ETC明細 反映時間',
      answer: 'ETC利用照会サービスへの反映は即時ではありません。新規登録後は初回明細作成に約4時間、個別走行の反映は道路や交通状況により変動します。月末締めや請求書対応では確定証明書の発行タイミングも考慮が重要です。同日や繁忙期の利用は翌日以降の確認が安全です。',
      whyMatters: '経費精算や月末締めで「まだ表示されない」と焦る場面は少なくありません。反映遅延は異常ではなく、道路運営者の処理や登録状態、カード種別により異なります。即日完結を前提にすると記録漏れや二度手間が発生します。反映の仕組みを理解し、バッファを設けた運用が記録管理の安定につながります。JTRは記録の到着状況を整理し、遅延や未着に気づきやすくする補助ツールです。',
      officialBackground: '公式ETC利用照会サービスのQ&Aでは、新規登録完了後に利用明細データが作成されるまで約4時間かかる場合があると案内されています。また、走行反映時間に関する公式ページでは、道路や状況により表示タイミングが変動し、交通量などの影響で時間を要する場合があると記載されています。通常のETCクレジットカードとETCパーソナルカードは過去15か月分、ETCコーポレートカードは過去62日分の利用証明書発行・明細確認が可能です。NEXCO東日本の案内では、インボイス制度対応にはETC利用照会サービスから発行される確定後の利用証明書を使用するよう注意喚起があり、出口料金所での金額は未確定の場合があるとされています。',
      whoFor: [
        '走行後すぐにオンラインで明細を確認したい個人ドライバー',
        '月末締めで高速料金記録を集計する経理・総務担当者',
        '確定証明書のPDF発行を待っている企業の経理部門',
        '新規登録直後で履歴が表示されず不安を感じているユーザー',
      ],
      jtrSolution: {
        intro: 'Japan Toll Receiptsは公式処理を早めるサービスではなく、到着した記録を整理し、遅延や未着に気づきやすくするための記録管理ツールです。定期配信と一覧表示により、月末確認の手間と見落としリスクを軽減します。',
        bullets: [
          '定期配信で記録の到着状況を把握し、未反映の日付を早期に発見',
          'PDF・CSV形式で保管、月末締め後の追加確認作業を効率化',
          '複数カード・複数車両の記録を統合管理し、手作業の照合負担を軽減',
          '記録一覧により「いつ反映されたか」の履歴が可視化され、遅延傾向の把握が可能',
          '到着した記録をそのまま整理・保管し、公式タイミングに依存しない運用体制を支援',
        ],
      },
      pdfCsv: 'JTRは公式ETC利用照会サービスから取得可能な記録をPDF・CSV形式で配信します。ExcelやXLSX形式ではありませんが、CSVファイルは表計算ソフトで開いて集計・分析が可能です。月次レポートや年次保管にも対応し、記録の長期整理を支援します。',
      email: 'JTRは登録されたメールアドレスへ定期的に記録を配信します。月末締め前や週次確認など、タイミングに応じた受信設定が可能です。到着した記録は自動保管され、後日の確認や追加集計にも対応します。配信履歴により未着の早期発見をサポートします。',
      steps: [
        {
          t: '新規登録と初回反映タイミングの理解',
          d: 'ETC利用照会サービスの新規登録後、初回の利用明細データ作成には約4時間かかる場合があります。登録直後に履歴が表示されなくても正常です。',
        },
        {
          t: '走行後の反映待機時間を確保',
          d: '通行後すぐには明細が表示されないことがあります。道路や交通状況により反映時間が変動するため、数時間から半日程度待ってから再確認しましょう。',
        },
        {
          t: '登録カードと実際の利用カードの一致確認',
          d: 'ETC利用照会サービスに登録したカード番号と、実際に車載器で使用したカードが一致しているか確認します。別のカードで通行した記録は表示されません。',
        },
        {
          t: 'インボイス制度対応には確定証明書を使用',
          d: 'NEXCO公式案内により、インボイス制度に対応する場合はETC利用照会サービスから発行される確定後の利用証明書を使用する必要があります。',
        },
        {
          t: '月末締め処理にバッファ期間を設ける',
          d: '月末最終日の通行記録は翌営業日以降に反映される場合があります。経理処理では1~2営業日の猶予を設けることで記録漏れを防げます。',
        },
        {
          t: 'JTRで記録の到着状況を可視化',
          d: 'Japan Toll Receiptsは公式処理を早めるものではありませんが、定期配信により記録の到着状況を整理し、未反映の通行を見つけやすくします。',
        },
      ],
      commonProblems: [
        {
          q: '当日の通行記録が表示されない',
          a: '当日や直近の走行は反映に時間がかかることがあります。交通量や道路運営会社の処理状況により数時間から半日程度待ってから再確認してください。',
        },
        {
          q: '登録後すぐに履歴が見えない',
          a: '新規登録完了後、初回の利用明細データ作成には約4時間かかる場合があります。公式Q&Aで案内されている正常な処理時間です。',
        },
        {
          q: '月末締め日に記録が足りない',
          a: '最終日や直前の通行は翌営業日以降に反映されることがあります。経理処理では1~2営業日のバッファを設けて最終確認することを推奨します。',
        },
        {
          q: 'インボイス用の証明書がすぐ必要',
          a: 'NEXCO案内により、インボイス制度対応にはETC利用照会サービスから発行される確定後の利用証明書が必要です。出口時点では金額が未確定の場合があります。',
        },
      ],
      examples: [
        {
          who: '当日利用のドライバー',
          use: '午前中にETC通行し昼休みに確認したが表示されず、夕方に再確認すると反映されていた。焦らず待つことで解決。',
        },
        {
          who: '新規登録ユーザー',
          use: '登録完了直後は履歴が空白だったが、公式案内通り約4時間後に確認すると初回データが作成されていた。',
        },
        {
          who: '月次経理担当者',
          use: '月末最終日ではなく翌月2営業日後を最終確認日とすることで、遅れて反映される記録の取りこぼしを防止。',
        },
        {
          who: 'JTR利用企業',
          use: '定期配信レポートで特定日の記録が未着であることに気づき、反映待ちか登録ミスかを早期に判別できた。',
        },
      ],
      faqs: [
        {
          q: 'ETC利用明細の反映にはどれくらい時間がかかりますか?',
          a: '公式案内では道路や交通状況により変動すると説明されています。新規登録後の初回表示には約4時間、通常の走行反映には数時間から半日程度かかる場合があります。',
        },
        {
          q: '当日の記録が表示されないのは問題ですか?',
          a: 'いいえ。直近や当日の通行は反映に時間がかかることがあります。正常な反映待ち時間内である可能性が高いため、数時間後に再確認してください。',
        },
        {
          q: 'JTRを使えば記録が早く表示されますか?',
          a: 'いいえ。Japan Toll Receiptsは公式処理を早めるものではありません。記録が利用可能になった後に整理・配信し、未反映の通行を見つけやすくする独立サービスです。',
        },
        {
          q: 'インボイス制度対応で反映タイミングが重要なのはなぜですか?',
          a: 'NEXCO案内により、インボイス制度の書類としてはETC利用照会サービスから発行される確定後の利用証明書が必要です。出口時点では金額未確定の場合があります。',
        },
        {
          q: '記録がいつまでも表示されない場合はどうすればよいですか?',
          a: 'カード登録、車載器番号、車両情報、通行日、利用照会サービスのステータスを確認してください。それでも表示されない場合は公式サポートまたはカード会社へ問い合わせます。',
        },
      ],
      relatedFeatures: [
        {
          title: 'ETCメイサイ記録欠落の原因',
          desc: '記録が見つからない場合の主因と確認手順を解説します。',
          path: '/guides/etc-meisai-missing-trips-reasons',
        },
        {
          title: 'ETC明細 入口IC不明の対応',
          desc: '「入口IC不明」と表示された走行の確認方法を解説します。',
          path: '/guides/etc-meisai-unknown-entry-ic',
        },
        {
          title: '仕組み',
          desc: 'Japan Toll ReceiptsがETC明細を整理・配信する流れを確認',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '月次経理処理と記録バッファ管理を効率化',
          path: '/business',
        },
        {
          title: '無料トライアル',
          desc: '反映タイミングの可視化を実際に体験',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC総合情報ポータル：ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '利用照会サービスの公式説明。対応カード種別、利用証明書、PDF・CSVダウンロード、通常ETCクレジットカード・ETCパーソナルカードの15か月履歴、ETCコーポレートカードの62日履歴、登録要件、登録後約4時間の初回表示タイミングを案内。',
        },
        {
          label: 'ETC利用照会サービス公式ポータル',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用明細の確認、利用証明書発行、PDF・CSVダウンロードを提供する公式サイト。',
        },
        {
          label: 'ETC利用照会サービスQ&A：登録後の表示タイミング',
          url: 'https://www.etc-meisai.jp/faq/11.html',
          note: '本登録完了後、初回の利用明細データ作成に約4時間かかる場合があることを説明する公式Q&A。',
        },
        {
          label: 'ETC利用照会サービス：走行反映時間',
          url: 'https://www.etc-meisai.jp/soko.html',
          note: 'ETC通行記録の表示タイミングに関する公式案内。交通量や状況により反映に時間がかかる場合があることを説明。',
        },
        {
          label: 'NEXCO東日本：高速道路通行料金のインボイス制度対応',
          url: 'https://www.driveplaza.com/cms/box_side_news/2023/0915/etc_Invoice-system_start.html',
          note: 'ETCクレジットカード利用時のETC利用照会サービス経由での確定利用証明書発行とインボイス制度対応の注意点を案内する公式通知。',
        },
      ],
      longBody: '日本のETC利用記録は、料金所を通過した瞬間にオンラインで表示されるわけではありません。ETC利用照会サービスに反映されるまでには処理時間が必要であり、ドライバーが領収書や利用証明書、CSVエクスポートを必要とする際に初めてこのタイムラグに気付くケースが多く見られます。公式のETC利用照会サービスには、理解しておくべき2つのタイミング要素があります。1つ目は新規登録時の初期表示タイミングです。公式Q&Aによれば、本登録完了後に利用明細データの初期作成が行われ、利用明細が表示されるまで約4時間程度かかる場合があるとされています。2つ目は個別の走行反映タイミングです。公式案内では、表示タイミングは道路や状況によって異なり、交通量やその他の事情により時間を要する場合があると説明されています。一般ドライバーにとっての実用的なルールは、最近の走行記録が見当たらない場合でも、すぐに欠落と判断せず待つことです。通過直後に表示されなくても、数時間後に反映される場合があります。当日走行や繁忙期に近い利用であれば、想定される反映時間を経過してから再確認する方が安全です。経理担当者にとっては、月末締めを全ての走行が即座に利用可能という前提で組み立てるべきではないことを意味します。公式ポータルによれば、通常のETCクレジットカードおよびETCパーソナルカードは過去15か月分の利用証明書発行と明細確認が可能ですが、ETCコーポレートカードは62日間と短い期間に制限されています。この保存期間は反映タイミングとは別の概念です。反映タイミングは最近の走行がいつ表示されるかに関わり、保存期間は記録が公式サービスでいつまで利用可能かを示します。インボイス制度対応のための確定タイミングも存在します。NEXCO公式案内では、インボイス制度に対応した書類が必要な場合は、ETC利用照会サービスから発行される確定後の利用証明書を使用するよう促しています。料金所出口時点では金額が未確定の場合があり、早期の証明書や印刷物はインボイス要件を満たさない可能性があると説明されています。つまり、走行が表示されていても、全ての業務用途やインボイス用途が詳細に満たされるとは限りません。走行がすぐに表示されない一般的な理由としては、通常の処理遅延、ごく最近の走行、新規登録直後のタイミング、カード登録の不備、登録時のカードと車載器の不一致、無線決済以外の処理、特殊な料金処理、道路運営者側のタイミング、休日の交通量などが挙げられます。遅延が全てエラーであるかのような表現は避けるべきで、最初の説明は通常のタイミングであることが多いです。月末経理業務では、バッファを組み込んだワークフローが最善です。会社が月の最終日に通行記録を締める場合、当日や直近の走行は後追い確認が必要になる可能性があります。より良いプロセスは、日次収集に加えて想定反映時間後の最終確認日を設けることです。そうすれば、ドライバーがルートや目的を忘れた後に経理が欠落記録を発見する事態を避けられます。Japan Toll Receipts(JTR)は、定期的なスケジュールで記録を配信し、欠落や遅延記録に気付きやすくすることで支援できます。JTRは公式記録の反映を早めるシステムではありません。料金所運営者の処理や公式サービスのタイミングを制御することはできません。JTRの価値は、記録収集を整理されたワークフローに変え、何が届いていて何が後日確認を要するかをユーザーが把握しやすくする点にあります。ユーザーから「なぜETC走行が表示されないのか」と問われた場合、最も安全な回答は段階的なものです。使用したカードを確認し、アカウント登録を確認し、十分な時間が経過したかを確認し、該当する記録期間を確認し、走行がETC無線通過か別の決済方法かを確認します。通常の反映時間後に再確認し、それでも見当たらない場合は、公式サービスまたはカード・料金所運営者のサポートに問い合わせます。要するに、ETC更新タイミングは単一の固定時刻ではありません。登録状況、走行反映、金額確定、カード種別、道路状況、公式処理に依存します。適切な記録管理は、遅延が通常であることを前提とし、確認バッファを組み込み、自動化によって後日発覚を防ぐことを意味します。',
    },
    en: {
      title: 'ETC Statement Timing & Delays – When Records Appear | JTR',
      metaDesc: 'ETC Inquiry Service posting times, delay causes, month-end handling explained. New registration timing, ride reflection, certificate issuance, and JTR record support.',
      h1: 'ETC Statement Timing & Month-End Handling',
      primaryKeyword: 'ETC statement posting time',
      answer: 'ETC Inquiry Service posting is not instant. After new registration, initial statement creation takes approximately 4 hours; individual trip reflection varies by road and traffic conditions. For month-end close and invoicing, certificate issuance timing is critical. Same-day or peak-period trips are safer to verify next day or later.',
      whyMatters: 'Waiting for statements during expense reporting or month-end close causes frequent frustration. Posting delays are normal—not errors—and vary by road operator, registration status, and card type. Assuming same-day finalization leads to missing records and duplicate effort. Understanding posting mechanics and building buffer time into workflows stabilizes record management. JTR organizes arriving records and makes delays or missing data easier to spot.',
      officialBackground: 'The official ETC Inquiry Service FAQ states that statement data creation after new registration may take approximately 4 hours. The posting-time page notes that display timing fluctuates by road and conditions, with traffic volume and other factors causing delays. Standard ETC credit cards and ETC Personal Cards offer 15 months of certificate issuance and statement history; ETC Corporate Cards provide 62 days. NEXCO East Japan advises that invoice-system compliance requires confirmed certificates from ETC Inquiry Service, warning that exit toll-gate amounts may be preliminary and unconfirmed.',
      whoFor: [
        'Individual drivers who want online statements immediately after trips',
        'Accounting and admin staff consolidating toll records for month-end close',
        'Corporate finance teams waiting for confirmed certificate PDFs',
        'New registrants concerned when history does not appear right away',
      ],
      jtrSolution: {
        intro: 'Japan Toll Receipts does not accelerate official processing; it organizes arriving records and makes delays or missing data visible. Scheduled delivery and list views reduce month-end review effort and oversight risk.',
        bullets: [
          'Scheduled delivery shows record arrival status, spotting unreflected dates early',
          'PDF and CSV archiving streamline post-month-end review and reduce manual work',
          'Multi-card, multi-vehicle consolidation cuts cross-reference labor',
          "Record lists visualize 'when posted' history, revealing delay patterns",
          'Archive arriving records as-is, supporting operations independent of official timing',
        ],
      },
      pdfCsv: 'JTR delivers records retrievable from the official ETC Inquiry Service in PDF and CSV formats—not Excel or XLSX. CSV files open in spreadsheet software for aggregation and analysis. Monthly reports and annual archiving support long-term record organization.',
      email: 'JTR sends records to registered email addresses on a schedule. Reception settings adapt to pre-month-end, weekly, or other timing needs. Arrived records auto-archive for later review or additional aggregation. Delivery history supports early detection of missing data.',
      steps: [
        {
          t: 'Understand new registration and first data timing',
          d: 'After registering with the ETC Toll Inquiry Service, initial usage detail data creation may take approximately 4 hours. It is normal if no history appears immediately after registration.',
        },
        {
          t: 'Allow waiting time for post-trip reflection',
          d: 'Details may not appear immediately after passage. Because reflection time varies by road and traffic conditions, wait several hours to half a day before checking again.',
        },
        {
          t: 'Verify registered card matches actual usage card',
          d: 'Confirm that the card number registered with the ETC Toll Inquiry Service matches the card actually used in the onboard unit. Records from other cards will not be displayed.',
        },
        {
          t: 'Use finalized certificates for invoice system compliance',
          d: 'According to official NEXCO guidance, invoice system compliance requires using finalized usage certificates issued from the ETC Toll Inquiry Service.',
        },
        {
          t: 'Set buffer period for month-end closing',
          d: 'Travel records from the last day of the month may be reflected on or after the next business day. Allowing 1–2 business days in accounting processes prevents missed records.',
        },
        {
          t: 'Visualize record arrival status with JTR',
          d: 'Japan Toll Receipts does not accelerate official processing, but scheduled delivery organizes record arrival status and makes unreflected passages easier to identify.',
        },
      ],
      commonProblems: [
        {
          q: 'Same-day travel records not displayed',
          a: 'Same-day or recent trips may take time to reflect. Depending on traffic volume and road operator processing status, wait several hours to half a day before checking again.',
        },
        {
          q: 'No history visible right after registration',
          a: 'After completing new registration, initial usage detail data creation may take approximately 4 hours. This is normal processing time as indicated in the official Q&A.',
        },
        {
          q: 'Records missing at month-end closing',
          a: 'Final-day or immediately prior passages may reflect on or after the next business day. For accounting processes, we recommend setting a buffer of 1–2 business days for final confirmation.',
        },
        {
          q: 'Invoice certificate needed immediately',
          a: 'According to NEXCO guidance, invoice system compliance requires finalized usage certificates issued from the ETC Toll Inquiry Service. Amounts may be unconfirmed at the exit point.',
        },
      ],
      examples: [
        {
          who: 'Same-day driver',
          use: 'Traveled via ETC in the morning and checked at lunch with no display, then rechecked in the evening to find it reflected. Problem resolved by waiting patiently.',
        },
        {
          who: 'New registration user',
          use: 'History was blank immediately after registration completion, but checking approximately 4 hours later as indicated in official guidance showed initial data created.',
        },
        {
          who: 'Monthly accounting staff',
          use: 'Set final confirmation date to 2 business days after month-end rather than the last day, preventing missed records that reflect late.',
        },
        {
          who: 'JTR corporate user',
          use: 'Noticed missing records for a specific date in scheduled delivery reports, enabling early distinction between awaiting reflection or registration errors.',
        },
      ],
      faqs: [
        {
          q: 'How long does ETC usage detail reflection take?',
          a: 'Official guidance explains it varies by road and traffic conditions. Initial display after new registration may take approximately 4 hours; normal trip reflection may take several hours to half a day.',
        },
        {
          q: 'Is it a problem if same-day records are not displayed?',
          a: 'No. Recent or same-day passages may take time to reflect. There is a high possibility you are within normal reflection waiting time, so recheck after several hours.',
        },
        {
          q: 'Will records display faster if I use JTR?',
          a: 'No. Japan Toll Receipts does not accelerate official processing. It is an independent service that organizes and delivers records after they become available, making unreflected passages easier to identify.',
        },
        {
          q: 'Why does reflection timing matter for invoice system compliance?',
          a: 'According to NEXCO guidance, invoice system documentation requires finalized usage certificates issued from the ETC Toll Inquiry Service. Amounts may be unconfirmed at the exit point.',
        },
        {
          q: 'What should I do if records never appear?',
          a: 'Check card registration, onboard unit number, vehicle information, travel date, and inquiry service status. If still not displayed, contact official support or your card company.',
        },
      ],
      relatedFeatures: [
        {
          title: 'Missing ETC Meisai records — causes & checks',
          desc: 'Why trips are absent and how to verify before processing expenses.',
          path: '/guides/etc-meisai-missing-trips-reasons',
        },
        {
          title: 'ETC entry IC unknown — recovery steps',
          desc: 'What to do when ETC Usage Inquiry Service (MEISAI) shows \'entry IC unknown\' on a trip.',
          path: '/guides/etc-meisai-unknown-entry-ic',
        },
        {
          title: 'How it works',
          desc: 'Review the flow of how Japan Toll Receipts organizes and delivers ETC details',
          path: '/how-it-works',
        },
        {
          title: 'Business plans',
          desc: 'Streamline monthly accounting processes and record buffer management',
          path: '/business',
        },
        {
          title: 'Free trial',
          desc: 'Experience reflection timing visualization firsthand',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC General Information Portal: ETC Toll Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Official explanation of the inquiry service. Covers supported card types, usage certificates, PDF/CSV download, 15-month history for regular ETC credit cards and ETC personal cards, 62-day history for ETC corporate cards, registration requirements, and approximately 4-hour initial display timing after registration.',
        },
        {
          label: 'ETC Toll Inquiry Service Official Portal',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official site providing ETC card usage detail confirmation, usage certificate issuance, and PDF/CSV download.',
        },
        {
          label: 'ETC Toll Inquiry Service Q&A: Display timing after registration',
          url: 'https://www.etc-meisai.jp/faq/11.html',
          note: 'Official Q&A explaining that initial usage detail data creation may take approximately 4 hours after completing full registration.',
        },
        {
          label: 'ETC Toll Inquiry Service: Trip reflection time',
          url: 'https://www.etc-meisai.jp/soko.html',
          note: 'Official guidance on ETC travel record display timing. Explains that reflection may take time depending on traffic volume and conditions.',
        },
        {
          label: 'NEXCO East Japan: Invoice system compliance for expressway tolls',
          url: 'https://www.driveplaza.com/cms/box_side_news/2023/0915/etc_Invoice-system_start.html',
          note: 'Official notice providing guidance on finalized usage certificate issuance via ETC Toll Inquiry Service when using ETC credit cards, and invoice system compliance precautions.',
        },
      ],
      longBody: "ETC transaction records in Japan do not appear online the instant you pass through a toll gate. Processing time is required before trips are reflected in the ETC Meisai service, and many drivers only notice this lag when they need receipts, usage certificates, or CSV exports. The official ETC inquiry service has two timing factors worth understanding. First is the initial display timing for new registrations. According to the official FAQ, after main registration is completed, initial usage detail data is created, and it may take approximately four hours before details appear. Second is the reflection timing for individual trips. Official guidance explains that display timing varies by road and situation, and may require more time depending on traffic volume and other circumstances. The practical rule for everyday drivers is to wait before assuming a recent trip is missing. Even if a passage does not appear immediately after exiting, it may be reflected several hours later. For same-day trips or trips near busy periods, it is safer to recheck after the expected reflection window has passed. For accounting staff, this means month-end closing should not assume all trips are instantly available. According to the official portal, standard ETC credit cards and ETC personal cards allow usage certificate issuance and detail confirmation for the past fifteen months, while ETC corporate cards are limited to a shorter sixty-two-day window. This retention period is a separate concept from reflection timing. Reflection timing governs when a recent trip appears; retention period governs how long a record remains available in the official service. There is also a finalization timing relevant to the invoice system. NEXCO official guidance encourages using finalized usage certificates issued from the ETC inquiry service when invoice-compliant documents are required. It explains that amounts may be unconfirmed at the exit gate, and early certificates or printouts may not satisfy invoice requirements. In other words, even if a trip appears, it may not yet meet all business or invoice purposes. Common reasons trips do not appear immediately include normal processing delay, very recent passage, timing immediately after new registration, incomplete card registration, mismatch between registered card and onboard unit, non-wireless settlement processing, special toll processing, road operator timing, and holiday traffic volume. Avoid suggesting that every delay is an error; the first explanation is often normal timing. For month-end accounting work, workflows with buffer are best. If a company closes travel records on the last day of the month, same-day or very recent trips may require follow-up confirmation. A better process includes daily collection plus a final confirmation day after the expected reflection window, so accounting does not discover missing records after drivers have forgotten routes or purposes. Japan Toll Receipts can help by delivering records on a regular schedule, making gaps or delayed records easier to notice. JTR is not a system that speeds official record reflection. It cannot control toll operator processing or official service timing. JTR's value lies in turning record collection into an organized workflow, making it easier for users to see what has arrived and what requires later confirmation. When users ask why an ETC trip is not showing, the safest answer is step-by-step: confirm the card used, confirm account registration, confirm enough time has passed, confirm the relevant record period, and confirm whether the trip was ETC wireless passage or another settlement method. Recheck after normal reflection time, and if still missing, contact official service or card and toll operator support. In short, ETC update timing is not a single fixed hour. It depends on registration status, trip reflection, amount finalization, card type, road conditions, and official processing. Proper record management means assuming delay is normal, building in confirmation buffers, and using automation to prevent late discovery.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: 'ETC明細の反映フロー',
            intro: '走行から照会サービス表示までの処理経路',
            nodes: [
              {
                title: '料金所通過',
                desc: 'ETC無線通信で車載器と通信',
              },
              {
                title: '道路事業者処理',
                desc: '利用データを集約・確定処理',
              },
              {
                title: '照会サービス反映',
                desc: '約4時間～翌日以降に表示',
              },
              {
                title: '確定証明書発行',
                desc: '走行翌日以降にPDF作成可能',
              },
            ],
          },
          en: {
            title: 'ETC Record Update Flow',
            intro: 'Processing path from toll pass to service display',
            nodes: [
              {
                title: 'Toll gate pass',
                desc: 'ETC wireless communication',
              },
              {
                title: 'Road operator processing',
                desc: 'Data aggregation and confirmation',
              },
              {
                title: 'Service reflection',
                desc: 'Appears ~4 hrs to next day',
              },
              {
                title: 'Certificate issuance',
                desc: 'PDF available day after travel',
              },
            ],
          },
        },
      },
      {
        component: 'MonthlyReviewBoard',
        labels: {
          ja: {
            title: '月末締め対応スケジュール',
            intro: '記録確認と証明書発行の推奨タイムライン',
            milestones: [
              {
                day: 25,
                label: '走行記録の事前確認',
                kind: 'default',
              },
              {
                day: 28,
                label: '未反映分の最終走行',
                kind: 'action',
              },
              {
                day: 30,
                label: '反映待ちバッファ',
                kind: 'default',
              },
              {
                day: 31,
                label: '月末締め',
                kind: 'deadline',
              },
              {
                day: 1,
                label: '翌月初：遅延分確認',
                kind: 'action',
              },
              {
                day: 3,
                label: '確定証明書一括発行',
                kind: 'default',
              },
            ],
          },
          en: {
            title: 'Month-End Review Schedule',
            intro: 'Recommended timeline for record checks and certificate issuance',
            milestones: [
              {
                day: 25,
                label: 'Pre-check travel records',
                kind: 'default',
              },
              {
                day: 28,
                label: 'Last travel for month',
                kind: 'action',
              },
              {
                day: 30,
                label: 'Reflection buffer period',
                kind: 'default',
              },
              {
                day: 31,
                label: 'Month-end close',
                kind: 'deadline',
              },
              {
                day: 1,
                label: 'Next month: delayed check',
                kind: 'action',
              },
              {
                day: 3,
                label: 'Batch certificate issuance',
                kind: 'default',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: '反映時間：カード種別比較',
            intro: 'ETC利用照会サービスにおける表示タイミングと証明書保管期間',
            columns: [
              'ETCクレジット／パーソナル',
              'ETCコーポレート',
            ],
            rows: [
              {
                feature: '新規登録後の初回反映',
                a: '約4時間',
                b: '約4時間',
                kindA: 'neutral',
                kindB: 'neutral',
              },
              {
                feature: '走行後の通常反映',
                a: '数時間～翌日',
                b: '数時間～翌日',
                kindA: 'neutral',
                kindB: 'neutral',
              },
              {
                feature: '証明書保管期間',
                a: '過去15か月分',
                b: '過去62日分のみ',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '即日確定証明書',
                a: '原則翌日以降',
                b: '原則翌日以降',
                kindA: 'no',
                kindB: 'no',
              },
            ],
          },
          en: {
            title: 'Update Timing: Card Type Comparison',
            intro: 'Display timing and certificate retention period in ETC service',
            columns: [
              'ETC Credit/Personal',
              'ETC Corporate',
            ],
            rows: [
              {
                feature: 'Initial update after registration',
                a: '~4 hours',
                b: '~4 hours',
                kindA: 'neutral',
                kindB: 'neutral',
              },
              {
                feature: 'Normal post-travel update',
                a: 'Hours to next day',
                b: 'Hours to next day',
                kindA: 'neutral',
                kindB: 'neutral',
              },
              {
                feature: 'Certificate retention',
                a: 'Past 15 months',
                b: 'Past 62 days only',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Same-day certificate',
                a: 'Next day onwards',
                b: 'Next day onwards',
                kindA: 'no',
                kindB: 'no',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '反映時間に関する公式情報源',
            intro: 'ETC明細の表示タイミングと証明書発行を案内する公式ページ',
            sources: [
              {
                name: 'ETC利用照会サービスQ&A',
                role: '新規登録後の初回反映時間（約4時間）を案内',
                url: 'https://www.etc-meisai.jp/',
                scope: '全カード種別共通',
              },
              {
                name: '走行反映時間の公式ページ',
                role: '道路・交通状況による変動を説明',
                url: 'https://www.etc-meisai.jp/',
                scope: '通常走行時',
              },
              {
                name: 'NEXCO東日本インボイス案内',
                role: '確定証明書の使用と未確定金額の注意喚起',
                url: 'https://www.e-nexco.co.jp/',
                scope: 'インボイス対応',
              },
              {
                name: 'カード別保管期間一覧',
                role: 'クレジット15か月／コーポレート62日の違い',
                url: 'https://www.etc-meisai.jp/',
                scope: '証明書発行可能期間',
              },
            ],
          },
          en: {
            title: 'Official Sources on Update Timing',
            intro: 'Official pages explaining ETC record display timing and certificate issuance',
            sources: [
              {
                name: 'ETC Service FAQ',
                role: 'Initial update time after registration (~4 hrs)',
                url: 'https://www.etc-meisai.jp/',
                scope: 'All card types',
              },
              {
                name: 'Travel Update Timing Page',
                role: 'Explains variation by road and traffic conditions',
                url: 'https://www.etc-meisai.jp/',
                scope: 'Normal travel',
              },
              {
                name: 'NEXCO East Invoice Guide',
                role: 'Confirmed certificate use and unconfirmed amount warnings',
                url: 'https://www.e-nexco.co.jp/',
                scope: 'Invoice compliance',
              },
              {
                name: 'Retention Period by Card',
                role: 'Credit 15 months / Corporate 62 days difference',
                url: 'https://www.etc-meisai.jp/',
                scope: 'Certificate issuance period',
              },
            ],
          },
        },
      },
    ],
  },

  // 9) ETC利用照会サービスとJTRの違い
  {
    slug: 'etc-meisai-vs-jtr-comparison',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: 'ETC利用照会サービス vs JTR',
      en: 'ETC ETC Usage Inquiry Service (MEISAI) inquiry vs JTR',
    },
    ja: {
      title: 'ETC利用照会サービスと Japan Toll Receipts の比較 | JTR',
      metaDesc: '法人運用視点でETC利用照会サービスとJapan Toll Receiptsの使い分けを整理。複数ETCカード・社用車・コーポレートカード過去62日制約の対応、車両別・部署別配賦、PDF・CSV受信の自動化など経理ワークフロー寄りの観点を解説。広い中立比較は別ページにて掲載。',
      h1: 'ETC利用照会サービスとJTRの比較',
      primaryKeyword: 'ETC利用照会サービス vs JTR',
      answer: 'ETC利用照会サービスは、ETCカードの利用履歴をオンラインで確認し、利用証明書や明細をPDF・CSV形式でダウンロードできる公式システムです。Japan Toll Receipts(JTR)は、これらの記録をより効率的に受信・整理・保管するための独立した業務支援サービスで、日次メール配信などの自動化機能を提供します。公式サービスが情報源であり、JTRは業務フローを補完する層として機能します。',
      whyMatters: '多くの利用者が「JTRは公式サービスなのか」「MEISAI と何が違うのか」と疑問を持ちます。実際には、ETC利用照会サービスがNEXCOなどの公式データソースであり、JTRはその記録を定期的に受け取り、整理し、英語対応や複数カード管理を支援する独立サービスです。手動ログイン・検索・ダウンロード・保管の繰り返しが負担になる企業や外国人居住者にとって、この違いを理解することで、適切なツール選択と業務効率化が可能になります。',
      officialBackground: 'ETC総合情報ポータルによれば、ETC利用照会サービスは対象ETCカード利用者がオンラインで走行履歴を確認できる公式システムです。ETCクレジットカード、ETCパーソナルカード、ETC法人カードに対応し、利用証明書の発行、利用明細の確認、PDF・CSV形式での明細ダウンロードが可能です。一般的なETCクレジットカードとパーソナルカードは約15か月分、法人カードは62日分の履歴が閲覧できます。登録後の初回データ表示には約4時間、走行後の反映にも時間を要する場合があります。インボイス制度に関してNEXCO東日本や国税庁資料では、クレジット明細のみでは不十分なケースがあり、ETC利用照会サービスから取得する確定利用証明書が重要と説明されています。JTRはこの公式システムとは別の独立サービスで、記録の受信・整理・保管を支援しますが、データの作成・認証・税務保証は行いません。',
      whoFor: [
        'JTRが公式サービスかどうか混乱しているドライバー',
        '手動ポータルダウンロードと自動配信を比較検討中の企業',
        '公式サービスの日本語操作が難しい外国人居住者',
        '日次メール配信の価値を判断中の経理チーム',
      ],
      jtrSolution: {
        intro: 'JTRは公式ETC利用照会サービスが提供する記録を、より使いやすい形で受信・整理するための独立業務支援サービスです。手動ログイン・検索・保存の繰り返しを減らし、定期配信と英語対応で記録管理を効率化します。',
        bullets: [
          '日次または定期スケジュールでPDF・CSV形式の明細をメール受信',
          '複数カード・複数ドライバーの記録を一元的に整理・保管',
          '英語対応画面で外国人居住者の操作負担を軽減',
          '手動ダウンロード忘れや月末の記録収集遅延を削減',
          '公式システムを情報源として尊重し、業務フロー層として補完',
        ],
      },
      pdfCsv: '公式ETC利用照会サービスはPDF・CSV形式での明細ダウンロードに対応しています。PDFは閲覧・保管用の文書形式、CSVは並べ替え・フィルタ・インポート・配賦用の構造化データです。JTRはこれらファイルの定期配信を自動化し、経理業務への組み込みを容易にします。',
      email: 'JTRの日次メール配信機能により、利用者は毎日または指定スケジュールで受信トレイにPDF・CSV形式の明細を受け取れます。ドライバーや部門ごとの記録収集待ち時間が削減され、経理フローの一部として明細が自然に統合されます。公式ポータルへの手動ログインは不要になります。',
      steps: [
        {
          t: 'ETC利用照会サービスを公式の利用証明書・利用明細の取得元として利用する',
          d: '公式のETC利用照会サービス(MEISAI)は、利用証明書や利用明細のPDF・CSVダウンロードが可能な正式な情報源です。JTRはこれを補完する独立サービスです。',
        },
        {
          t: 'JTRを独立した配信・整理・確認のワークフロー層として位置付ける',
          d: 'Japan Toll Receipts(JTR)は公式サービスではなく、利用者が自身のETC利用記録をより効率的に受信・整理・確認するための独立した補助ツールです。',
        },
        {
          t: 'JTRを公式機関・NEXCO・ETC利用照会サービス（MEISAI）の代替として説明しない',
          d: 'JTRはNEXCO、MEISAI、ETC利用照会サービス、道路会社、政府機関のいずれでもありません。公式データの作成や認証は行いません。',
        },
        {
          t: 'PDF形式は閲覧用、CSV形式は会計データ処理用として保管する',
          d: '公式サービスとJTRはともにPDF・CSVの両形式に対応しています。PDFは書類確認用、CSVは集計・配賦・インポート用に活用できます。',
        },
        {
          t: 'インボイス制度対応は公式ガイダンスと税理士の確認を優先する',
          d: 'JTRは記録の整理を支援しますが、税務上の取扱いや保管要件の判断は、国税庁の案内や税理士による確認に基づいて行ってください。',
        },
        {
          t: '手作業ダウンロードの負担が課題になったときにJTRを検討する',
          d: '公式サービスへの手動ログイン・検索・ダウンロード・整理が繰り返し発生し、業務の負担になる場合にJTRの導入を検討するのが適切なタイミングです。',
        },
      ],
      commonProblems: [
        {
          q: 'JTRは公式サービスですか?',
          a: 'いいえ。JTRはNEXCO・ETC利用照会サービス・道路会社・政府機関とは独立したサービスです。公式のETC利用照会サービスが情報源で、JTRはその記録を受信・整理する補助ツールです。',
        },
        {
          q: '公式サービスがあるのにJTRを使う理由は何ですか?',
          a: '公式ポータルへの手動ログイン・検索・ダウンロード・整理が毎月繰り返されると時間がかかります。JTRは配信を自動化し、複数カードや英語対応が必要な場合に効率化できます。',
        },
        {
          q: 'JTRを使えば税務対応が完了しますか?',
          a: 'いいえ。JTRは記録の整理を支援しますが、税務やインボイス制度の取扱いについては国税庁の案内や税理士による確認が必要です。JTRは税務保証を提供しません。',
        },
        {
          q: '公式サービスとJTRはどちらを使うべきですか?',
          a: '公式のETC利用照会サービスは記録の正式な取得元として利用し、JTRは定期配信・英語対応・複数カード整理など業務効率化が必要な場合に併用すると効果的です。',
        },
      ],
      examples: [
        {
          who: '年に数回しか記録を確認しない個人ドライバー',
          use: '公式のETC利用照会サービスで年2回PDFをダウンロードするだけなら、JTRは不要かもしれません。手作業が負担にならない場合は公式サービスのみで十分です。',
        },
        {
          who: '英語対応が必要な外国籍居住者',
          use: '日本語のみの公式ポータルを毎回操作するのが負担な場合、JTRは英語対応の整理・配信フローを提供し、繰り返し作業の負担を軽減できます。',
        },
        {
          who: '複数のETCカードを管理する中小企業',
          use: '8枚のETCカードを持つ企業で月末の記録収集が遅れがちな場合、JTRは日次で記録を配信し、経理担当者がドライバーを待つ時間を減らせます。',
        },
        {
          who: '税務処理に慎重な企業の経理担当者',
          use: 'JTRで記録を収集しつつ、インボイス制度や保管要件の判断は公式ガイダンスと税理士の確認に基づいて行い、記録整理と税務判断を分けて運用します。',
        },
      ],
      faqs: [
        {
          q: 'JTRとETC利用照会サービスは同じですか?',
          a: 'いいえ。ETC利用照会サービスは公式の利用証明書・明細取得システムで、JTRは独立した記録配信・整理の補助サービスです。公式サービスが情報源で、JTRはワークフロー層です。',
        },
        {
          q: 'JTRはETC利用照会サービス（MEISAI）の代わりになりますか?',
          a: 'いいえ。JTRは公式のMEISAI(ETC利用照会サービス)を置き換えるものではなく、利用者が記録をより便利に管理するための独立した補助ツールです。',
        },
        {
          q: '公式サービスでもPDF・CSVはダウンロードできますか?',
          a: 'はい。公式のETC利用照会サービスでは利用明細をPDFまたはCSV形式でダウンロードできます。JTRはその配信と整理を効率化する役割を担います。',
        },
        {
          q: 'JTRを使えばインボイス制度対応が保証されますか?',
          a: 'いいえ。JTRは記録の整理を支援しますが、税務やインボイス制度の取扱いは国税庁の案内や税理士による確認が必要です。JTRは税務保証を提供しません。',
        },
        {
          q: 'どちらを使うべきですか?',
          a: '公式サービスは記録の正式な取得元として利用し、JTRは定期配信・英語対応・複数カード管理など業務効率化が必要な場合に併用すると効果的です。',
        },
      ],
      relatedFeatures: [
        {
          title: '中立比較版（広い読者向け）',
          desc: 'ETC利用照会サービスとJTRを広い読者向けに中立に並べた比較ピラー。仕組み・役割・使い分けの全体像を掴みたい方はこちらから。',
          path: '/comparison/etc-riyou-shoukai-vs-jtr',
        },
        {
          title: 'JTRの仕組み',
          desc: '公式記録の独立した配信・整理フローとして機能するJTRの技術的な仕組みを確認できます。',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数カード管理や定期配信が必要な企業向けに、JTRがどのように業務効率化を支援するかを説明します。',
          path: '/business',
        },
        {
          title: '無料トライアル',
          desc: '公式サービスと併用した際の効果を確認するため、JTRの配信フローを実際に試すことができます。',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC総合情報ポータル:ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '公式のETC利用照会サービスの説明、対応カード種別、利用証明書・明細のPDF/CSVダウンロード、15か月履歴(通常ETCクレジットカード・ETCパーソナルカード)、62日履歴(ETCコーポレートカード)、登録要件、初回表示タイミング約4時間の案内。',
        },
        {
          label: 'ETC利用照会サービス(公式ポータル)',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用明細の確認、利用証明書の発行、PDF・CSV形式のダウンロードが可能な公式ポータルサイト。',
        },
        {
          label: 'ETC利用照会サービスQ&A:登録後の表示タイミング',
          url: 'https://www.etc-meisai.jp/faq/11.html',
          note: '本登録完了後、初回の利用明細データ表示まで約4時間かかる旨を説明する公式Q&A。',
        },
        {
          label: 'NEXCO東日本:高速道路通行料金のインボイス制度対応',
          url: 'https://www.driveplaza.com/cms/box_side_news/2023/0915/etc_Invoice-system_start.html',
          note: 'ETCクレジットカード利用時のETC利用照会サービスを通じた利用証明書(確定金額)の取扱いとインボイス制度上の注意点を説明する公式案内。',
        },
        {
          label: '国税庁:高速道路利用税インボイスQ&A問103',
          url: 'https://www.nta.go.jp/taxes/shiraberu/zeimokubetsu/shohi/keigenzeiritsu/pdf/qa/103.pdf',
          note: 'ETCクレジットカードによる高速道路料金のインボイス制度上の取扱いを説明する国税庁公式Q&A。税務助言を行わず慎重に引用してください。',
        },
      ],
      longBody: 'ETC利用照会サービスとJapan Toll Receipts(JTR)は関連する課題に対応していますが、同一のものではありません。ETC利用照会サービスは、ETCカードの利用履歴をオンラインで確認し、利用証明書を発行し、利用明細をダウンロードするための公式システムです。一方、JTRは独立したサービスであり、利用者がETC記録をより簡単に受け取り、整理し、確認できるよう支援するもので、通常はPDFとCSVファイルをメールで自動配信する形式をとります。公式ポータルによれば、ETC利用照会サービスは対象となるETCカード利用者が走行記録をオンラインで確認できるサービスであり、ETCクレジットカード、ETCパーソナルカード、ETCコーポレートカードに対応しています。利用証明書の発行、利用明細の確認、PDFまたはCSV形式でのダウンロードが可能です。通常のETCクレジットカードとETCパーソナルカードは原則15か月分の記録を保持し、ETCコーポレートカードは62日間の記録期間となっています。この公式サービスが基盤となっており、これがなければ大半のETCカード利用者にとって公式なオンライン利用証明書の発行経路は存在しません。ポータルでは利用者がカードを証明し、走行を確認し、ファイルをダウンロードし、公式記録へのアクセスを処理します。また、登録要件、反映タイミングの遅延、カード対象範囲、公式証明書の取り扱いなど、JTRが尊重すべきルールも定められています。JTRが存在する理由は、公式アクセスと便利な業務ワークフローが同じではないためです。ドライバーは公式サービスにログインできても、毎月ログインすることを忘れる場合があります。企業は記録を入手できても、ドライバーから収集する時間を失う可能性があります。英語を話す居住者はPDFとCSVの必要性を理解していても、日本語だけの画面を繰り返し操作したくない場合があります。JTRは記録取得をより一貫した配信とアーカイブのパターンに変えます。この違いは一文で説明できます。公式ETC利用照会サービスはソースであり、JTRは独立したワークフロー層です。公式サービスは利用記録へのアクセスを提供し、JTRは利用者がそれらの記録をより使いやすい方法で受け取り、管理できるよう支援します。この比較が重要なのは、JTRをNEXCO、MEISAI、ETC利用照会サービス、道路事業者、政府機関として説明してはならないためです。JTRは公式の通行料金データを作成せず、料金を認証せず、公式ルールを上書きせず、税務コンプライアンスを保証しません。利用者が設定を承認した後、自身の記録を管理する手助けをします。個人にとって公式ポータルの利点は直接アクセスと別の報告ワークフローが不要な点です。欠点は、利用者がログイン、検索、ダウンロード、保存、整理を覚えておく必要がある点です。企業にとって、この手作業は繰り返しコストになります。カード、ドライバー、部門、月ごとに記録処理が増えます。JTRの利点は再現性です。日次または定期的な記録が受信箱に届き、通常の経理フローの一部になります。経理処理において、PDFとCSVは異なる役割を果たします。PDFは読みやすい文書形式の記録です。CSVは並べ替え、フィルタリング、インポート、配分のための構造化データです。公式ポータルはPDFとCSVのダウンロードに対応しています。JTRの価値は、これらのファイル形式の配信をドライバーやチーム全体で運用しやすくすることです。インボイス制度の取り扱いは公式ソース優先を維持すべきです。NEXCOと国税庁の資料は、クレジットカード明細だけでは特定のインボイス制度目的に不十分な場合があり、ETC利用照会サービスからの確定利用証明書が重要であると説明しています。JTRは確認を支援する記録のアーカイブに役立ちますが、税務保証として販売すべきではありません。保持すべき書類は利用者または会計士が判断する必要があります。適切な利用者決定の枠組みはシンプルです。時折記録が必要で、日本語を十分に読むことができ、手動ログインを気にしない場合は公式ETC利用照会サービスを使用してください。定期配信、英語対応の整理、PDFとCSVのアーカイブ、手作業の記録作業削減を希望する場合はJTRを使用してください。公式システムをソースとして、JTRを管理を容易にするワークフローとして使用したい場合は両方を使用してください。最良の比較は、一方が他方を置き換えるかのような「公式対非公式」ではありません。より良い比較は「公式ソース対独立配信ワークフロー」です。真剣なJTR利用者の大半は、公式システムの存在を依然として必要としています。JTRの価値は、その公式情報を使用する際の日常的な摩擦を軽減することです。',
    },
    en: {
      title: 'ETC ETC Usage Inquiry Service (MEISAI) Inquiry vs Japan Toll Receipts Comparison | JTR',
      metaDesc: 'Compare the official ETC ETC Usage Inquiry Service (MEISAI) inquiry service with Japan Toll Receipts. Understand their roles, PDF/CSV downloads, automated delivery benefits, and choose what fits your workflow.',
      h1: 'ETC ETC Usage Inquiry Service (MEISAI) Inquiry vs JTR Comparison',
      primaryKeyword: 'ETC ETC Usage Inquiry Service (MEISAI) inquiry vs JTR',
      answer: 'The ETC inquiry service is the official system for viewing ETC card usage history online and downloading usage certificates and statements in PDF and CSV formats. Japan Toll Receipts (JTR) is an independent business support service that receives, organizes, and stores these records more efficiently, offering automation features like daily email delivery. The official service is the data source; JTR functions as a complementary workflow layer.',
      whyMatters: 'Many users wonder whether JTR is an official service or how it differs from ETC Usage Inquiry Service (MEISAI). In reality, the ETC inquiry service is the official NEXCO data source, while JTR is an independent service that periodically receives those records, organizes them, and supports English-language access and multi-card management. For companies and foreign residents burdened by repeated manual logins, searches, downloads, and filing, understanding this distinction enables better tool selection and workflow efficiency.',
      officialBackground: 'According to the ETC General Information Portal, the ETC inquiry service is the official system allowing eligible ETC cardholders to view driving records online. It supports ETC credit cards, personal cards, and corporate cards, enabling usage certificate issuance, statement viewing, and PDF/CSV downloads. Standard credit and personal cards retain approximately 15 months of history; corporate cards retain 62 days. Initial data display after registration takes about four hours, and post-trip reflection may also be delayed. Regarding invoice requirements, NEXCO East and National Tax Agency materials note that credit card statements alone may be insufficient; confirmed usage certificates from the ETC inquiry service are essential. JTR is a separate independent service supporting record receipt, organization, and storage, but does not create, authenticate, or guarantee data for tax purposes.',
      whoFor: [
        'Drivers confused whether JTR is an official service',
        'Companies comparing manual portal downloads with automated delivery',
        'Foreign residents struggling with Japanese-only official portal navigation',
        'Accounting teams evaluating the value of daily email delivery',
      ],
      jtrSolution: {
        intro: 'JTR is an independent business support service that receives and organizes records provided by the official ETC inquiry service in a more accessible format. It reduces repetitive manual logins, searches, and filing, streamlining record management through scheduled delivery and English support.',
        bullets: [
          'Receive PDF and CSV statements by email on daily or custom schedules',
          'Centrally organize and store records for multiple cards and drivers',
          'English-language interface reduces operational burden for foreign residents',
          'Eliminate download oversights and month-end collection delays',
          'Respect the official system as the data source, complementing workflow layers',
        ],
      },
      pdfCsv: 'The official ETC inquiry service supports statement downloads in PDF and CSV formats. PDF is a document format for viewing and archiving; CSV is structured data for sorting, filtering, import, and allocation. JTR automates the periodic delivery of these files, simplifying integration into accounting workflows.',
      email: "JTR's daily email delivery feature allows users to receive PDF and CSV statements in their inbox every day or on a custom schedule. Wait times for driver or departmental record collection are reduced, and statements integrate naturally into accounting workflows. Manual logins to the official portal are no longer necessary.",
      steps: [
        {
          t: 'Use the official ETC inquiry service as your primary source for usage certificates and details',
          d: 'The official ETC inquiry service (ETC Usage Inquiry Service (MEISAI)) is the authoritative source for downloading usage certificates and details in PDF or CSV format. JTR is an independent complementary service.',
        },
        {
          t: 'Position JTR as an independent delivery, organization, and review workflow layer',
          d: 'Japan Toll Receipts (JTR) is not an official service; it is an independent tool that helps you receive, organize, and review your ETC usage records more efficiently.',
        },
        {
          t: 'Do not describe JTR as a replacement for official agencies, NEXCO, or ETC Usage Inquiry Service (MEISAI)',
          d: 'JTR is not NEXCO, ETC Usage Inquiry Service (MEISAI), the ETC inquiry service, road operators, or a government agency. It does not create or authenticate official data.',
        },
        {
          t: 'Store PDF format for document review and CSV format for accounting data processing',
          d: 'Both the official service and JTR support PDF and CSV formats. Use PDF for document verification and CSV for aggregation, allocation, and import.',
        },
        {
          t: 'Prioritize official guidance and tax advisor confirmation for invoice system compliance',
          d: 'While JTR helps organize records, tax treatment and retention requirements should be determined based on National Tax Agency guidance and consultation with your tax advisor.',
        },
        {
          t: 'Consider JTR when manual download workload becomes a recurring challenge',
          d: 'When repeated manual login, search, download, and organization on the official service becomes a burden on your workflow, that is the appropriate time to consider adopting JTR.',
        },
      ],
      commonProblems: [
        {
          q: 'Is JTR an official service?',
          a: 'No. JTR is independent of NEXCO, ETC Usage Inquiry Service (MEISAI), road operators, and government agencies. The official ETC inquiry service is the data source, and JTR is a tool that receives and organizes those records.',
        },
        {
          q: 'Why use JTR when the official service already exists?',
          a: 'Repeated manual login, search, download, and organization on the official portal can be time-consuming. JTR automates delivery and improves efficiency when managing multiple cards or requiring English support.',
        },
        {
          q: 'Does using JTR satisfy tax compliance requirements?',
          a: 'No. JTR helps organize records, but tax and invoice system treatment requires confirmation based on National Tax Agency guidance and consultation with your tax advisor. JTR does not provide tax assurance.',
        },
        {
          q: 'Should I use the official service or JTR?',
          a: 'Use the official ETC inquiry service as the authoritative source for records, and combine it with JTR when you need recurring delivery, English support, or multi-card organization for operational efficiency.',
        },
      ],
      examples: [
        {
          who: 'Individual driver who checks records only a few times per year',
          use: 'If downloading PDFs from the official ETC inquiry service twice a year is sufficient, JTR may be unnecessary. Manual work is not a burden when the official service alone is enough.',
        },
        {
          who: 'Foreign resident requiring English-language support',
          use: 'If operating the Japanese-only official portal repeatedly is burdensome, JTR provides English-language organization and delivery workflows, reducing the repetitive workload.',
        },
        {
          who: 'Small business managing multiple ETC cards',
          use: 'For a company with eight ETC cards where month-end record collection often delays, JTR delivers records daily, reducing the time accounting staff spend waiting on drivers.',
        },
        {
          who: 'Accounting staff at a company cautious about tax processing',
          use: 'Collect records via JTR while making invoice system and retention requirement decisions based on official guidance and tax advisor confirmation, separating record organization from tax judgment.',
        },
      ],
      faqs: [
        {
          q: 'Are JTR and the ETC inquiry service the same?',
          a: 'No. The ETC inquiry service is the official system for obtaining usage certificates and details, while JTR is an independent record delivery and organization tool. The official service is the data source; JTR is the workflow layer.',
        },
        {
          q: 'Can JTR replace ETC Usage Inquiry Service (MEISAI)?',
          a: 'No. JTR does not replace the official ETC Usage Inquiry Service (MEISAI) (ETC inquiry service); it is an independent tool that helps you manage records more conveniently.',
        },
        {
          q: 'Can I also download PDF and CSV from the official service?',
          a: 'Yes. The official ETC inquiry service allows you to download usage details in PDF or CSV format. JTR streamlines the delivery and organization of those records.',
        },
        {
          q: 'Does using JTR guarantee invoice system compliance?',
          a: 'No. JTR helps organize records, but tax and invoice system treatment requires confirmation based on National Tax Agency guidance and consultation with your tax advisor. JTR does not provide tax assurance.',
        },
        {
          q: 'Which should I use?',
          a: 'Use the official service as the authoritative source for records, and combine it with JTR when you need recurring delivery, English support, or multi-card management for operational efficiency.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How JTR Works',
          desc: 'Review the technical mechanics of how JTR functions as an independent delivery and organization layer for official records.',
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'Learn how JTR supports operational efficiency for companies requiring multi-card management and recurring delivery.',
          path: '/business',
        },
        {
          title: 'Free Trial',
          desc: 'Test the JTR delivery workflow to see the effect of combining it with the official service.',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC General Information Portal: ETC Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Official description of the ETC inquiry service, supported card types, usage certificate and detail PDF/CSV downloads, 15-month history (standard ETC credit/personal cards), 62-day history (ETC corporate cards), registration requirements, and initial display timing of approximately 4 hours.',
        },
        {
          label: 'ETC Inquiry Service (Official Portal)',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official portal site for checking ETC card usage details, issuing usage certificates, and downloading in PDF or CSV format.',
        },
        {
          label: 'ETC Inquiry Service Q&A: Display Timing After Registration',
          url: 'https://www.etc-meisai.jp/faq/11.html',
          note: 'Official Q&A explaining that it takes approximately 4 hours for initial usage detail data to appear after registration.',
        },
        {
          label: 'NEXCO East: Invoice System Support for Expressway Tolls',
          url: 'https://www.driveplaza.com/cms/box_side_news/2023/0915/etc_Invoice-system_start.html',
          note: 'Official guidance explaining the treatment of usage certificates (confirmed amounts) via the ETC inquiry service for ETC credit card use and invoice system considerations.',
        },
        {
          label: 'National Tax Agency: Expressway Usage Tax Invoice Q&A No. 103',
          url: 'https://www.nta.go.jp/taxes/shiraberu/zeimokubetsu/shohi/keigenzeiritsu/pdf/qa/103.pdf',
          note: 'Official National Tax Agency Q&A explaining invoice system treatment for expressway tolls via ETC credit card. Reference carefully without providing tax advice.',
        },
      ],
      longBody: "The ETC Inquiry Service and Japan Toll Receipts (JTR) address related challenges, but they are not the same thing. The ETC Inquiry Service is the official system for checking ETC card usage history online, issuing usage certificates, and downloading transaction details. JTR, on the other hand, is an independent service that helps users receive, organize, and review their ETC records more easily, typically through automated email delivery of PDF and CSV files. According to the official portal, the ETC Inquiry Service allows eligible ETC cardholders to view their toll records online and supports ETC credit cards, ETC personal cards, and ETC corporate cards. Users can issue usage certificates, review transaction details, and download records in PDF or CSV format. Standard ETC credit and personal cards retain records for fifteen months in principle, while ETC corporate cards maintain a sixty-two-day record period. This official service forms the foundation; without it, most ETC cardholders would have no official online path to usage certificates. The portal handles user authentication, trip verification, file downloads, and access to official records. It also establishes rules that JTR must respect, including registration requirements, reflection delays, card eligibility, and the handling of official certificates. JTR exists because official access and convenient business workflows are not the same. Drivers may be able to log into the official service but forget to do so monthly. Companies may obtain records but lose time collecting them from drivers. English-speaking residents may understand the need for PDFs and CSVs but prefer not to navigate Japanese-only screens repeatedly. JTR transforms record retrieval into a more consistent delivery and archiving pattern. The difference can be explained in one sentence: the official ETC Inquiry Service is the source; JTR is an independent workflow layer. The official service provides access to usage records; JTR helps users receive and manage those records in a more usable way. This comparison matters because JTR must not be described as NEXCO, ETC Usage Inquiry Service (MEISAI), the ETC Inquiry Service, road operators, or government agencies. JTR does not create official toll data, authenticate charges, override official rules, or guarantee tax compliance. It helps users manage their own records after they approve the setup. For individuals, the official portal's advantage is direct access with no separate reporting workflow needed. The disadvantage is that users must remember to log in, search, download, save, and organize. For companies, this manual work becomes a recurring cost. Processing multiplies per card, driver, department, and month. JTR's advantage is repeatability: daily or periodic records arrive in the inbox and become part of normal accounting flow. In accounting processes, PDF and CSV serve different roles. PDF is a readable document-format record. CSV is structured data for sorting, filtering, importing, and allocation. The official portal supports both PDF and CSV downloads. JTR's value is making delivery of these file formats more operational across drivers and teams. Handling of the invoice system should maintain official-source priority. NEXCO and National Tax Agency materials explain that credit card statements alone may be insufficient for certain invoice system purposes, and confirmed usage certificates from the ETC Inquiry Service are important. JTR is useful for archiving records to support verification, but should not be sold as a tax guarantee. Which documents to retain must be decided by the user or accountant. The framework for proper user decision is simple. If you need records occasionally, read Japanese comfortably, and don't mind manual login, use the official ETC Inquiry Service. If you want regular delivery, English-friendly organization, PDF and CSV archiving, and reduced manual record work, use JTR. If you want to use the official system as the source and JTR as a workflow to make management easier, use both. The best comparison is not 'official versus unofficial' as though one replaces the other. A better comparison is 'official source versus independent delivery workflow.' Most serious JTR users still need the official system to exist. JTR's value is reducing the daily friction of working with that official information.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '公式サービスからJTRへのデータフロー',
            intro: 'ETC利用照会サービス（公式）が記録を作成し、JTRが受信・整理・配信する流れ',
            nodes: [
              {
                title: '高速道路走行',
                desc: 'ETCゲートを通過し利用データが生成',
              },
              {
                title: '公式システム反映',
                desc: 'ETC利用照会サービスに履歴が登録（数時間後）',
              },
              {
                title: 'JTR受信',
                desc: '公式データを定期的に取得・整理',
              },
              {
                title: '自動配信',
                desc: 'PDF・CSV形式でメール送信',
              },
            ],
          },
          en: {
            title: 'Data Flow from Official Service to JTR',
            intro: 'ETC inquiry service (official) creates records; JTR receives, organizes, and delivers',
            nodes: [
              {
                title: 'Highway Travel',
                desc: 'Pass ETC gate; usage data generated',
              },
              {
                title: 'Official System Update',
                desc: 'Record appears in ETC inquiry service (hours later)',
              },
              {
                title: 'JTR Retrieval',
                desc: 'Fetch and organize official data periodically',
              },
              {
                title: 'Auto Delivery',
                desc: 'Email PDF/CSV to your inbox',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: 'ETC利用照会サービス vs JTR機能比較',
            intro: '公式システムと業務支援サービスの役割の違い',
            columns: [
              'ETC利用照会サービス',
              'Japan Toll Receipts',
            ],
            rows: [
              {
                feature: 'データ作成元',
                a: '公式（NEXCO等）',
                b: '公式データを受信',
                kindA: 'yes',
                kindB: 'neutral',
              },
              {
                feature: '日次メール配信',
                a: '手動ログイン必要',
                b: '自動配信',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '英語画面対応',
                a: '日本語のみ',
                b: '完全英語対応',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '複数カード一元管理',
                a: 'カード別ログイン',
                b: '統合ダッシュボード',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '税務保証',
                a: '公式証明書発行',
                b: '公式データ参照',
                kindA: 'yes',
                kindB: 'neutral',
              },
            ],
          },
          en: {
            title: 'ETC Inquiry Service vs JTR Feature Comparison',
            intro: 'Official system vs business support service roles',
            columns: [
              'ETC Inquiry Service',
              'Japan Toll Receipts',
            ],
            rows: [
              {
                feature: 'Data Source',
                a: 'Official (NEXCO etc.)',
                b: 'Receives official data',
                kindA: 'yes',
                kindB: 'neutral',
              },
              {
                feature: 'Daily Email Delivery',
                a: 'Manual login required',
                b: 'Automated delivery',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'English Interface',
                a: 'Japanese only',
                b: 'Full English support',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Multi-Card Management',
                a: 'Per-card login',
                b: 'Unified dashboard',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Tax Guarantee',
                a: 'Official certificates',
                b: 'References official data',
                kindA: 'yes',
                kindB: 'neutral',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: 'ETC記録の公式情報源とJTRの位置付け',
            intro: 'データ作成・認証は公式システム、業務効率化はJTRが担当',
            sources: [
              {
                name: 'ETC利用照会サービス',
                role: '公式データ作成・保管（NEXCO等）',
                url: 'etc-meisai.jp',
                scope: '15か月履歴・証明書発行',
              },
              {
                name: 'Japan Toll Receipts',
                role: '定期受信・整理・配信支援',
                url: 'japantollreceipts.jp',
                scope: '自動化・英語対応',
              },
              {
                name: '国税庁インボイス資料',
                role: '税務要件ガイダンス',
                url: 'nta.go.jp',
                scope: '適格簡易請求書基準',
              },
              {
                name: 'NEXCO東日本FAQ',
                role: '利用証明書取得方法案内',
                url: 'e-nexco.co.jp',
                scope: 'PDF・CSV取得手順',
              },
            ],
          },
          en: {
            title: "Official ETC Data Sources and JTR's Role",
            intro: 'Official systems create/certify data; JTR streamlines workflow',
            sources: [
              {
                name: 'ETC Inquiry Service',
                role: 'Official data creation (NEXCO etc.)',
                url: 'etc-meisai.jp',
                scope: '15-month history, certificates',
              },
              {
                name: 'Japan Toll Receipts',
                role: 'Periodic retrieval & delivery support',
                url: 'japantollreceipts.jp',
                scope: 'Automation, English UI',
              },
              {
                name: 'NTA Invoice Guidelines',
                role: 'Tax compliance guidance',
                url: 'nta.go.jp',
                scope: 'Qualified invoice standards',
              },
              {
                name: 'NEXCO East FAQ',
                role: 'Certificate retrieval instructions',
                url: 'e-nexco.co.jp',
                scope: 'PDF/CSV download steps',
              },
            ],
          },
        },
      },
      {
        component: 'DecisionTree',
        labels: {
          ja: {
            title: 'あなたに適したサービス選択フロー',
            intro: '利用状況に応じた公式サービスとJTRの使い分け',
            root: '月に何回高速道路を利用しますか？',
            branches: [
              {
                condition: '月1〜2回のみ',
                kind: 'neutral',
                outcome: '公式サービスで十分',
                action: 'ETC利用照会サービスで手動ダウンロード',
              },
              {
                condition: '週1回以上・複数カード',
                kind: 'yes',
                outcome: 'JTRで効率化を推奨',
                action: '日次配信で記録収集を自動化',
              },
              {
                condition: '日本語操作が難しい',
                kind: 'yes',
                outcome: 'JTR英語画面を活用',
                action: '公式データを英語環境で受信',
              },
              {
                condition: '税務証明が必要',
                kind: 'neutral',
                outcome: '公式証明書を取得',
                action: 'JTRは補助、公式が最終証拠',
              },
            ],
          },
          en: {
            title: 'Which Service Fits Your Needs?',
            intro: 'Choose between official service and JTR based on usage',
            root: 'How often do you use toll roads?',
            branches: [
              {
                condition: '1–2 times/month',
                kind: 'neutral',
                outcome: 'Official service sufficient',
                action: 'Manual download from ETC inquiry portal',
              },
              {
                condition: 'Weekly + multiple cards',
                kind: 'yes',
                outcome: 'JTR recommended',
                action: 'Automate collection via daily delivery',
              },
              {
                condition: 'Japanese UI difficult',
                kind: 'yes',
                outcome: 'Use JTR English interface',
                action: 'Receive official data in English environment',
              },
              {
                condition: 'Tax certificate needed',
                kind: 'neutral',
                outcome: 'Obtain official certificate',
                action: 'JTR assists; official source is final proof',
              },
            ],
          },
        },
      },
    ],
  },

  // 10) ETC利用明細を毎日メールで受け取る方法
  {
    slug: 'etc-meisai-daily-email-howto',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: 'ETC利用明細 メール 毎日',
      en: 'ETC usage records email daily',
    },
    ja: {
      title: 'ETC利用明細を毎日メールで受信する方法 | PDF・CSV・JTR自動化',
      metaDesc: 'ETC利用明細を毎日メールで受信する設定方法を解説。Japan Toll ReceiptsによるPDF・CSV自動配信、メール受信箱を検索可能な記録アーカイブにする運用、月次の取得漏れを減らす仕組み、ETC利用照会サービスとの関係まで、経理・ドライバー両方の視点で詳しく紹介します。',
      h1: 'ETC利用明細を毎日メールで受信する方法',
      primaryKeyword: 'ETC利用明細 メール 毎日',
      answer: '毎日のメール配信は、ETC記録管理を月次作業から通常業務フローへと変える最も簡単な方法です。公式ETC利用照会サービスが元データを提供し、JTRは利用者が繰り返しログイン・検索・ダウンロード・リネーム・転送を行わなくても、記録をメールで受信・整理できるよう支援します。手動操作に依存しないため、記録の取得漏れを防ぎ、経理チームの負担を軽減します。',
      whyMatters: '手動での記録取得は多くのワークフローで破綻します。ドライバーがダウンロードを忘れる、企業が月末まで待つ、ポータルログインを思い出せない、最近の走行がまだ表示されず後で確認しない、経理チームが不足PDFを追いかける時間を費やす―こうした問題を毎日配信が解決します。「誰かが記録を取りに行ったか?」ではなく、組織が定期的な記録ストリームを受信する仕組みに変わります。後から検索しやすいアーカイブを作ることが目標です。',
      officialBackground: '公式ETC利用照会サービスは利用証明書の発行、利用明細の確認、PDFまたはCSVでの利用明細ダウンロードに対応しています。通常のETCクレジットカードおよびETCパーソナルカードは過去15か月分、ETCコーポレートカードは62日分の記録に対応します。初回登録後、利用明細データの表示には約4時間かかる場合があります。走行の反映タイミングは交通量などにより異なり、請求関係の記録では確定した利用証明書が必要になることがあります。公式記録は貴重ですが、公式サービスは利用者の能動的な操作に依存しています。',
      whoFor: [
        '手動ダウンロードを避けたい毎日のETCドライバー',
        '定期的な通行料記録が必要な経理チーム',
        '複数のETCカードを管理する事業者',
        '英語対応レポートを好む外国人居住者',
      ],
      jtrSolution: {
        intro: 'JTRは独立した配信・自動化サービスです。NEXCO、MEISAI、ETC利用照会サービス、料金収受機関のいずれでもありません。公式通行料データを作成せず、利用者の承認設定に基づいて記録の受信・管理を支援します。',
        bullets: [
          'PDF・CSV両方を配信―それぞれ異なるレビュー目的に対応',
          '適切な担当者・管理者・経理メールボックス・共有アーカイブへルーティング',
          '公式の記帳・確定タイミングを考慮し、即時詳細性を前提としない',
          'メールアーカイブを検索可能な記録ストリームとして活用',
          '手動ログイン作業を削減し、一貫したワークフローを実現',
        ],
      },
      pdfCsv: 'PDF・CSV両方の受信を推奨します。PDFは文書レビューに適し、CSVは会計処理や分類に適しています。事業者は月次を通じて記録を受信でき、最終的な月末処理はレビューと照合になり、緊急収集ではなくなります。',
      email: '毎日のメール設定には明確なルーティングが必要です。個人利用者は自分宛のみ、事業者はドライバー・管理者・経理CCを設定、複数車両はカード別・部門別ルーティングが考えられます。カードとドライバーが増えるほど、命名とルーティングの重要性が高まります。',
      steps: [
        {
          t: 'ETCカードの利用照会サービス登録を完了する',
          d: '自動化を始める前に、対象となるETCカードをETC利用照会サービスに登録し、利用明細の取得権限を確保してください。',
        },
        {
          t: '毎日配信を選択する',
          d: '記録の抜け漏れが経費精算や経理処理の支障になる場合は、月次や手動ダウンロードではなく毎日配信の設定を検討してください。',
        },
        {
          t: 'PDFとCSVの両方を受信する',
          d: 'PDFは書類確認に、CSVは仕訳や分類作業に適しています。用途に応じて両形式を活用してください。',
        },
        {
          t: '適切な宛先にレポートを振り分ける',
          d: '個人利用者は本人のみ、法人は運転者・上長・経理担当・共有メールボックスなど、組織内の役割に応じた配信先を設定してください。',
        },
        {
          t: '公式の反映タイミングを考慮する',
          d: '利用当日中に全記録が確定するわけではありません。公式の処理時間やインボイス制度対応の確定タイミングを理解して運用してください。',
        },
        {
          t: 'メールアーカイブを検索可能な記録として活用する',
          d: 'メール保存は検索に便利ですが、正式な保管や税務対応は社内規程や会計方針に従って別途管理してください。',
        },
      ],
      commonProblems: [
        {
          q: '毎日メールが届くが当日の利用が反映されない',
          a: 'ETC利用照会サービスは登録後約4時間、通行後も交通量や処理状況により反映に時間がかかる場合があります。即時詳細性を前提とせず翌営業日確認を推奨します。',
        },
        {
          q: 'メール配信だけで税務対応できるか',
          a: 'JTRは記録の整理・受信を支援しますが、税務適合性や保管義務の保証はしません。正式な帳簿保存は会計方針に従ってください。',
        },
        {
          q: '複数カードの振分けができない',
          a: 'カード枚数や運転者が多い場合、カード別・部署別のメール振分け設定が重要です。JTRは複数カードの配信ルーティングに対応する設計です。',
        },
        {
          q: '公式ポータルとJTRの違いが分からない',
          a: 'ETC利用照会サービスが記録の提供元、JTRは受信・整理・配信の自動化層です。公式システムに代わるものではなく、業務フローを補完します。',
        },
      ],
      examples: [
        {
          who: '毎日通勤するドライバー',
          use: '毎日の利用有無レポートを受信し、通行記録の発生を即座に確認できます。ポータルへの繰り返しログインが不要になります。',
        },
        {
          who: 'ETCカード5枚の中小企業',
          use: 'カード別の日次レポートを経理担当に自動送信し、月末の集計作業を月次レビューと照合作業に集約できます。',
        },
        {
          who: '個人事業主・フリーランス',
          use: '日次の通行記録メールを年度別フォルダに保存し、後日CSVファイルを顧客別・案件別に分類して確定申告準備に活用します。',
        },
        {
          who: '車両管理責任者',
          use: '通常と異なる経路や時間帯の通行が発生した際、運転者の記憶が鮮明なうちに確認・記録できる運用上のきっかけとして活用します。',
        },
      ],
      faqs: [
        {
          q: 'ETC利用明細を毎日メールで受け取れますか?',
          a: '公式サービスはダウンロード提供です。JTRは利用者が承認した設定に基づき、整理されたETC利用記録の日次メール配信を支援します。',
        },
        {
          q: '日次配信=当日中の確定を意味しますか?',
          a: 'いいえ。公式の処理時間やインボイス制度の確定タイミングがあります。即時詳細性ではなく、継続的な記録受信フローとして活用してください。',
        },
        {
          q: 'PDFとCSVどちらを受け取るべきですか?',
          a: '両方が有用です。PDFは書類確認・保管向き、CSVは会計ソフト取込や分類作業向きです。用途に応じて併用を推奨します。',
        },
        {
          q: '法人で経理部門宛に配信できますか?',
          a: 'はい。JTRは組織内の適切な受信者—運転者・上長・経理担当・共有メールボックス—への振分け設定を想定した設計です。',
        },
        {
          q: 'メール保存だけで長期保管は十分ですか?',
          a: 'メールは検索に便利ですが、正式な帳簿保存・税務対応は社内規程や会計方針に従い、別途ファイル管理や会計システム保管を併用してください。',
        },
      ],
      relatedFeatures: [
        {
          title: '無料トライアル',
          desc: '日次配信の動作と受信フローを実際に確認できます',
          path: '/free-trial',
        },
        {
          title: '法人向けプラン',
          desc: '複数カード・部署別配信・経理連携に対応',
          path: '/business',
        },
        {
          title: 'セキュリティ',
          desc: 'メール配信とデータ保護の仕組みを説明',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス(公式)',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対応カード種別・利用証明書・PDF/CSV提供・保存期間(通常ETCクレジットカード/ETCパーソナルカード15か月、ETCコーポレートカード62日)・登録後約4時間の初回反映目安を説明',
        },
        {
          label: 'ETC利用照会サービスポータル',
          url: 'https://www.etc-meisai.jp/',
          note: '利用明細確認・利用証明書発行・PDF/CSVダウンロードを提供する公式ポータル',
        },
        {
          label: 'ETC利用照会サービスQ&A:登録後の表示タイミング',
          url: 'https://www.etc-meisai.jp/faq/11.html',
          note: '本登録完了後、利用明細データの初回表示まで約4時間かかる旨を説明',
        },
        {
          label: 'ETC利用照会サービス:走行反映時間',
          url: 'https://www.etc-meisai.jp/soko.html',
          note: '通行後の反映目安と交通量等の状況により時間を要する場合がある旨を説明',
        },
        {
          label: 'NEXCO東日本:高速道路通行料金のインボイス制度対応',
          url: 'https://www.driveplaza.com/cms/box_side_news/2023/0915/etc_Invoice-system_start.html',
          note: 'ETCクレジットカード利用時の利用証明書(確定版)取得方法とインボイス制度対応の注意点を説明',
        },
      ],
      longBody: 'ETC利用明細を毎日メールで受け取る仕組みは、月末にまとめてダウンロードする作業を日常的な記録フローに変える手段として注目されています。公式のETC利用照会サービスは利用証明書の発行、利用明細の確認、PDFやCSVでのダウンロードに対応しており、通常のETCクレジットカードやETCパーソナルカードでは過去15か月分、ETCコーポレートカードでは62日分の照会が可能とされています。しかし公式ポータルは利用者自身がログイン・検索・ダウンロード・保存・転送を繰り返す前提であり、忙しい経理担当者や日常的に高速道路を利用するドライバーにとっては手間が積み重なります。毎日配信の利点は、記録取得のタイミングを自動化し、誰かが取りに行くのを待つのではなく定期的な記録の流れを作る点にあります。JTRは独立した配信・自動化サービスとして位置づけられ、NEXCO・MEISAI・ETC利用照会サービス・料金所運営者のいずれでもありません。JTRは公式の通行料金データを作成せず、利用者の承認された設定に基づいて記録の受信と管理を支援します。毎日配信といっても即座に全記録が確定するわけではなく、公式案内では利用照会サービスへの初回登録後、データ表示に約4時間を要する場合があり、交通量などの状況により反映に時間を要することもあると説明されています。インボイス制度に関連する確定利用証明書も、クレジットカード会社の請求確定後に発行可能となるため、同日報告が即座に税務対応完了を意味するわけではありません。個人利用者にとっては、日本語のみのポータルを毎回操作する負担を軽減し、メール検索で過去の記録を探せる利便性があります。個人事業主であれば、日次メールを年度別フォルダに保存し、後からCSV行を顧客別に分類することも可能です。企業では、経理部門が月中に記録を受け取り、ドライバーが経路を覚えているうちに不自然な利用を確認し、月末処理を緊急収集ではなく照合作業に変えられます。配信設定では宛先の明確化が重要で、個人は自分宛のみ、企業はドライバー・上長・経理のCCが必要な場合もあり、カード枚数や運転者が増えるほど命名と振り分けの価値が高まります。また公式サービスには照会可能期間がありますが、配信後の記録は利用者がメールストレージ・ファイルサーバー・会計システムで長期保管できます。JTRは公式ポータルの保存期間を延長するのではなく、配信後の記録を利用者が外部で管理できるよう支援します。よくある失敗例として、カード登録前に自動化を期待する、同日確定を前提に税務処理を組む、PDFのみ受信してCSVを活用しない、宛先を設定せず記録が埋もれる、公式の反映タイミングを無視して即座の詳細性を求めるといったケースがあります。毎日配信の本質は、公式システムが記録の発生源であり、JTRが記録を利用者の日常業務に組み込む経路である点にあります。その経路が整うことで、記録管理の負担が減り、検索性と定期性が向上し、経理・監査・償還の準備がスムーズになります。',
    },
    en: {
      title: 'How to Receive ETC Usage Records by Email Daily | JTR Guide',
      metaDesc: 'Learn how to set up daily email delivery of ETC toll records. JTR automates PDF & CSV distribution, archive management, and proper relationships with official sources.',
      h1: 'How to Receive ETC Usage Records by Email Daily',
      primaryKeyword: 'ETC usage records email daily',
      answer: 'Daily email delivery is the simplest way to transform ETC record management from a monthly scramble into a normal business workflow. The official ETC inquiry service provides the source data; JTR helps users receive and organize records via email without repeated logins, searches, downloads, renames, and forwards. By removing dependence on manual actions, you prevent missed records and reduce accounting team burden.',
      whyMatters: "Manual record retrieval breaks down in most workflows. Drivers forget to download, companies wait until month-end, portal logins are forgotten, recent trips haven't appeared yet and are never checked later, accounting teams spend hours chasing missing PDFs—daily delivery solves this. Instead of asking 'Did someone fetch the records?', your organization receives a regular stream. The goal is a searchable archive built over time, not crisis retrieval.",
      officialBackground: 'The official ETC inquiry service supports issuing usage certificates, viewing usage details, and downloading records as PDF or CSV. Standard ETC credit cards and ETC Personal Cards cover the past 15 months; ETC Corporate Cards cover 62 days. After initial registration, usage detail display may take approximately four hours. Trip reflection timing varies with traffic volume and other factors; billing-related records may require finalized usage certificates. Official records are valuable, but the official service depends on users taking action.',
      whoFor: [
        'Daily ETC drivers who want to avoid manual downloads',
        'Accounting teams that need regular toll records',
        'Businesses managing multiple ETC cards',
        'Foreign residents who prefer English-language reports',
      ],
      jtrSolution: {
        intro: 'JTR is an independent delivery and automation service. We are not NEXCO, ETC Usage Inquiry Service (MEISAI), the ETC inquiry service, or any toll collection agency. We do not create official toll data; we help users receive and manage records based on their approved settings.',
        bullets: [
          'Deliver both PDF & CSV—each serves different review purposes',
          'Route to the right person, manager, accounting mailbox, or shared archive',
          'Respect official posting and settlement timing; do not assume instant completeness',
          'Turn email archives into searchable record streams',
          'Reduce manual login work and achieve consistent workflows',
        ],
      },
      pdfCsv: 'We recommend receiving both PDF and CSV. PDF suits document review; CSV suits accounting and categorization. Businesses receive records throughout the month, so final month-end processing becomes review and reconciliation, not emergency collection.',
      email: 'Daily email setup requires clear routing. Individual users send to themselves only; businesses configure driver, manager, and accounting CC; fleets may route by card or department. As cards and drivers grow, naming and routing become essential.',
      steps: [
        {
          t: 'in-depth ETC Card Usage Inquiry Service registration',
          d: 'Before automating, register your target ETC card(s) with the ETC Usage Inquiry Service to ensure access to usage detail records.',
        },
        {
          t: 'Choose daily delivery',
          d: 'If missing records would disrupt expense claims or accounting, consider daily delivery settings rather than monthly reports or manual downloads.',
        },
        {
          t: 'Receive both PDF and CSV formats',
          d: 'PDF suits document review; CSV suits journaling and categorization work. Use both formats according to your workflow needs.',
        },
        {
          t: 'Route reports to appropriate recipients',
          d: 'For personal use, send to the user only. For corporate use, set delivery to drivers, supervisors, accounting staff, shared mailboxes—aligned with organizational roles.',
        },
        {
          t: 'Account for official processing timing',
          d: 'Not all records finalize on the day of use. Understand official processing times and Invoice System confirmation timing to operate accordingly.',
        },
        {
          t: 'Use email archives as searchable records',
          d: 'Email storage is convenient for search, but formal retention and tax compliance should follow internal policy and accounting standards separately.',
        },
      ],
      commonProblems: [
        {
          q: 'Daily emails arrive but same-day usage is not reflected',
          a: 'The ETC Usage Inquiry Service requires approximately 4 hours after registration, and post-travel reflection may be delayed by traffic volume or processing load. Do not assume immediate completeness; confirm the following business day.',
        },
        {
          q: 'Can email delivery alone satisfy tax requirements?',
          a: 'JTR assists with record organization and delivery but does not guarantee tax compliance or archival obligations. Formal ledger storage should follow accounting policy.',
        },
        {
          q: 'Unable to route multiple cards separately',
          a: 'When managing many cards or drivers, per-card and per-department routing is essential. JTR is designed to support multi-card delivery routing.',
        },
        {
          q: 'Unclear on the difference between the official portal and JTR',
          a: 'The ETC Usage Inquiry Service is the record source; JTR is the automation layer for receiving, organizing, and delivering. It does not replace the official system but complements business workflows.',
        },
      ],
      examples: [
        {
          who: 'Daily commuting driver',
          use: 'Receive daily usage presence reports and confirm toll activity immediately. Eliminates repeated portal logins.',
        },
        {
          who: 'SME with 5 ETC cards',
          use: 'Auto-send per-card daily reports to accounting staff, consolidating month-end aggregation into monthly review and reconciliation tasks.',
        },
        {
          who: 'Individual business owner / freelancer',
          use: 'Store daily toll record emails in annual folders, then later classify CSV files by client or project for tax filing preparation.',
        },
        {
          who: 'Vehicle manager',
          use: 'When unusual routes or times occur, use delivery as an operational prompt to confirm details while driver memory is fresh.',
        },
      ],
      faqs: [
        {
          q: 'Can I receive ETC usage details by email every day?',
          a: 'The official service provides download access. JTR assists with daily email delivery of organized ETC usage records based on user-approved settings.',
        },
        {
          q: 'Does daily delivery mean same-day finalization?',
          a: 'No. Official processing times and Invoice System confirmation timing apply. Use this as a continuous record-receiving workflow, not for immediate completeness.',
        },
        {
          q: 'Should I receive PDF or CSV?',
          a: 'Both are useful. PDF is suited to document review and storage; CSV to accounting software import and categorization. We recommend using both according to purpose.',
        },
        {
          q: 'Can reports be delivered to corporate accounting departments?',
          a: 'Yes. JTR is designed to support routing to appropriate organizational recipients—drivers, supervisors, accounting staff, shared mailboxes.',
        },
        {
          q: 'Is email storage alone sufficient for long-term retention?',
          a: 'Email is convenient for search, but formal ledger storage and tax compliance should follow internal policy and accounting standards, using separate file management or accounting system archives.',
        },
      ],
      relatedFeatures: [
        {
          title: 'Free Trial',
          desc: 'Confirm daily delivery behavior and receiving workflow in practice',
          path: '/free-trial',
        },
        {
          title: 'Business Plans',
          desc: 'Support for multiple cards, departmental routing, accounting integration',
          path: '/business',
        },
        {
          title: 'Security',
          desc: 'Explanation of email delivery and data protection mechanisms',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC Usage Inquiry Service (official)',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explains supported card types, usage certificates, PDF/CSV provision, retention periods (standard ETC credit/personal cards 15 months, ETC corporate cards 62 days), and initial reflection guideline of approximately 4 hours after registration',
        },
        {
          label: 'ETC Usage Inquiry Service Portal',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official portal providing usage detail confirmation, usage certificate issuance, and PDF/CSV download',
        },
        {
          label: 'ETC Usage Inquiry Service Q&A: Display timing after registration',
          url: 'https://www.etc-meisai.jp/faq/11.html',
          note: 'Explains that after full registration, initial display of usage detail data requires approximately 4 hours',
        },
        {
          label: 'ETC Usage Inquiry Service: Travel reflection time',
          url: 'https://www.etc-meisai.jp/soko.html',
          note: 'Explains estimated post-travel reflection timing and that traffic volume or other conditions may require additional time',
        },
        {
          label: 'NEXCO East: Expressway toll Invoice System compliance',
          url: 'https://www.driveplaza.com/cms/box_side_news/2023/0915/etc_Invoice-system_start.html',
          note: 'Explains how to obtain usage certificates (finalized version) for ETC credit card use and Invoice System compliance considerations',
        },
      ],
      longBody: "The practice of receiving ETC itemized records by email every day is gaining attention as a way to transform the end-of-month bulk download routine into an ongoing documentation flow. The official ETC usage inquiry service supports issuance of usage certificates, confirmation of itemized records, and download in PDF and CSV formats; standard ETC credit cards and ETC personal cards allow inquiries for the past fifteen months, while ETC corporate cards permit inquiries covering sixty-two days. Yet the official portal assumes that users themselves will repeatedly log in, search, download, save, and forward records—a cycle that accumulates friction for busy accounting staff and drivers who use expressways regularly. The advantage of daily delivery lies in automating the timing of record retrieval and creating a regular documentation rhythm rather than waiting for someone to go fetch it. JTR positions itself as an independent delivery and automation service and is not NEXCO, ETC Usage Inquiry Service (MEISAI), the ETC usage inquiry service, or any tollbooth operator. JTR does not generate official toll data; it assists users in receiving and managing records based on their authorized settings. Even with daily delivery, not all records settle instantly; official guidance notes that after initial registration with the inquiry service, data display may require approximately four hours, and reflection can take longer depending on traffic volume and other conditions. Confirmed usage certificates related to invoice system requirements also become available only after the credit card company finalizes billing, so same-day reporting does not mean immediate tax compliance completion. For individual users, the benefit is reducing the burden of operating a Japanese-only portal each time and gaining the convenience of searching past records through email. Sole proprietors can save daily emails into year-specific folders and later classify CSV rows by client. In corporate settings, accounting departments can receive records mid-month, verify unusual usage while drivers still remember their routes, and turn month-end processing from an urgent collection scramble into a reconciliation task. In delivery configuration, clarity of destination is vital: individuals typically address only themselves, while companies may require CC to drivers, supervisors, and accounting; as card count and driver count grow, the value of naming conventions and sorting rules rises. Although the official service has inquiry retention limits, records after delivery can be stored long-term by users in email storage, file servers, or accounting systems. JTR does not extend the official portal's retention window; it enables users to manage delivered records externally. Common missteps include expecting automation before registering cards, structuring tax workflows on the assumption of same-day finalization, receiving only PDF without leveraging CSV, failing to set recipients so records become buried, and demanding immediate completeness while ignoring official reflection timing. The essence of daily delivery is that the official system remains the source of record events and JTR serves as the channel embedding those records into users' daily operations. When that channel is in place, record-management overhead decreases, searchability and regularity improve, and preparation for accounting, auditing, and reimbursement becomes smoother.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: 'ETC利用明細の自動メール配信フロー',
            intro: '公式記録から受信箱まで、毎日の配信がどのように機能するか',
            nodes: [
              {
                title: '1. 走行',
                desc: 'ドライバーがETC料金所を通過',
              },
              {
                title: '2. 公式記録',
                desc: '利用照会サービスに数時間以内に反映',
              },
              {
                title: '3. JTR自動取得',
                desc: '毎日スケジュールで記録を取得',
              },
              {
                title: '4. PDF・CSV生成',
                desc: '両形式で配信準備',
              },
              {
                title: '5. メール配信',
                desc: '設定済みの受信者へ自動送信',
              },
              {
                title: '6. アーカイブ',
                desc: '検索可能な記録ストリームとして蓄積',
              },
            ],
          },
          en: {
            title: 'Automated ETC Email Delivery Flow',
            intro: 'How daily delivery works from official records to your inbox',
            nodes: [
              {
                title: '1. Drive',
                desc: 'Driver passes through ETC toll gate',
              },
              {
                title: '2. Official record',
                desc: 'Appears in ETC Usage Inquiry Service (MEISAI) service within hours',
              },
              {
                title: '3. JTR fetch',
                desc: 'Daily schedule retrieves records',
              },
              {
                title: '4. PDF & CSV',
                desc: 'Both formats prepared for delivery',
              },
              {
                title: '5. Email send',
                desc: 'Auto-delivered to configured recipients',
              },
              {
                title: '6. Archive',
                desc: 'Accumulates as searchable record stream',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '公式ETC記録ソースとJTRの役割',
            intro: '各サービスの責任範囲と関係性',
            sources: [
              {
                name: 'ETC利用照会サービス',
                role: '公式利用明細の提供元',
                url: 'www.etc-meisai.jp',
                scope: '過去15か月（通常カード）または62日（法人カード）',
              },
              {
                name: 'NEXCO・道路会社',
                role: '通行料金の収受・記録',
                url: '各道路会社',
                scope: '料金所通過記録の元データ作成',
              },
              {
                name: 'クレジット会社',
                role: '請求・支払処理',
                url: '各カード会社',
                scope: '最終的な料金請求・明細',
              },
              {
                name: 'JTR',
                role: '配信・自動化支援（公式記録は作成せず）',
                url: 'japantollreceipts.jp',
                scope: '利用者承認に基づく記録受信・整理',
              },
            ],
          },
          en: {
            title: 'Official ETC Sources & JTR Role',
            intro: 'Responsibility scope and relationship of each service',
            sources: [
              {
                name: 'ETC ETC Usage Inquiry Service (MEISAI) Service',
                role: 'Official statement provider',
                url: 'www.etc-meisai.jp',
                scope: 'Past 15 months (regular) or 62 days (corporate)',
              },
              {
                name: 'NEXCO & Road Operators',
                role: 'Toll collection & recording',
                url: 'Each road company',
                scope: 'Source toll gate transaction records',
              },
              {
                name: 'Credit Issuers',
                role: 'Billing & payment processing',
                url: 'Each card company',
                scope: 'Final charge statements',
              },
              {
                name: 'JTR',
                role: 'Delivery automation (does not create official records)',
                url: 'japantollreceipts.jp',
                scope: 'Record fetch & routing per user consent',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: '手動ダウンロード vs 毎日メール配信',
            intro: '2つのアプローチを比較',
            columns: [
              '手動ダウンロード',
              'JTR毎日配信',
            ],
            rows: [
              {
                feature: 'ログイン作業',
                a: '毎回必要',
                b: '初回設定のみ',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '取得忘れリスク',
                a: '頻繁に発生',
                b: '自動実行で防止',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'PDF・CSV両対応',
                a: '個別選択',
                b: '両方同時配信',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '複数カード管理',
                a: '個別ログイン',
                b: '一括ルーティング',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '経理チーム連携',
                a: '転送作業必要',
                b: '直接CC配信',
                kindA: 'partial',
                kindB: 'yes',
              },
            ],
          },
          en: {
            title: 'Manual Download vs Daily Email',
            intro: 'Comparing two approaches',
            columns: [
              'Manual Download',
              'JTR Daily Email',
            ],
            rows: [
              {
                feature: 'Login effort',
                a: 'Every time',
                b: 'Once at setup',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Forgotten fetches',
                a: 'Common',
                b: 'Auto-prevented',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'PDF & CSV',
                a: 'Choose one',
                b: 'Both delivered',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Multi-card mgmt',
                a: 'Separate logins',
                b: 'Unified routing',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Accounting team',
                a: 'Manual forward',
                b: 'Direct CC',
                kindA: 'partial',
                kindB: 'yes',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '毎日メール配信の設定チェックリスト',
            intro: 'スムーズな自動化のための確認項目',
            items: [
              {
                text: 'ETC利用照会サービスのアカウント登録完了',
                hint: 'JTR連携の前提条件',
              },
              {
                text: '配信先メールアドレスを決定（個人・管理者・経理）',
                hint: '複数宛先の場合はCC設定も検討',
              },
              {
                text: 'PDF・CSV両形式の受信を選択',
                hint: 'レビュー用と処理用で使い分け',
              },
              {
                text: 'カード別の命名ルールを設定',
                hint: '複数カードの場合は識別しやすい名前に',
              },
              {
                text: '初回配信後、受信確認とアーカイブ方法を確立',
                hint: 'メールフォルダやラベルで整理',
              },
              {
                text: '公式記録の反映タイミングを理解',
                hint: '走行後4時間程度、即時完全性を期待しない',
              },
            ],
          },
          en: {
            title: 'Daily Email Setup Checklist',
            intro: 'Steps to ensure smooth automation',
            items: [
              {
                text: 'ETC ETC Usage Inquiry Service (MEISAI) service account registered',
                hint: 'Prerequisite for JTR integration',
              },
              {
                text: 'Delivery email addresses decided (personal, admin, accounting)',
                hint: 'Consider CC for multiple recipients',
              },
              {
                text: 'Both PDF & CSV formats selected',
                hint: 'Use PDF for review, CSV for processing',
              },
              {
                text: 'Card naming convention established',
                hint: 'Clear labels for multi-card setups',
              },
              {
                text: 'First delivery confirmed & archive method set',
                hint: 'Use folders or labels for organization',
              },
              {
                text: 'Official record timing understood',
                hint: '~4 hours after trip; not instant completeness',
              },
            ],
          },
        },
      },
    ],
  },
];

export const TIER_F_SLUGS = TIER_F_PAGES.map((p) => p.slug);
